// src/pages/BusinessDashboard.tsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import {
  FaSignOutAlt, FaStore, FaUserEdit, FaHome,
  FaCamera, FaBullhorn, FaPlus, FaTrash, FaCheckCircle,
  FaClock, FaGem, FaLock, FaChartBar, FaChartLine, FaEye, FaMousePointer,
  FaCalendarCheck, FaImages, FaExternalLinkAlt, FaRobot, FaMagic, FaMicrophone,
  FaLightbulb, FaSmile, FaBriefcase, FaHandshake as FaHandshakeIcon, FaGlassCheers, FaVideo, FaFacebook, FaInstagram, FaTiktok,
  FaEnvelope, FaGift, FaQrcode, FaInfoCircle, FaArrowRight, FaCoins, FaDollarSign,
  FaFire, FaStopwatch, FaBoxOpen, FaTruck, FaShoppingBag, FaSearch, FaPrint, FaUtensils, FaBed, FaTag,
  FaWifi, FaShower, FaKey, FaGlobe, FaMapMarkerAlt, FaPhone, FaShippingFast, FaCheck, FaCrown, FaCheckDouble,
  FaCalendarAlt, FaLink, FaHotel, FaListAlt, FaTimes, FaBan, FaPaperPlane, FaSync, FaRulerCombined, FaWeightHanging,
  FaUsers,
  FaQuestionCircle,
  FaEyeSlash,
  FaSwimmingPool, FaSpa, FaConciergeBell, FaTv, FaParking, FaSnowflake, FaDog, FaSmokingBan, FaWineGlassAlt,
  FaSave, FaStar, FaGavel, FaFileSignature, FaPenFancy, FaFileContract, FaSignature, FaDownload, FaCopy, FaHistory,
  FaTimesCircle, FaExclamationTriangle, FaExpand, FaCompress, FaBell, FaShieldAlt, FaUserShield, FaMobileAlt,
  FaChevronDown, FaChevronUp,
  FaTicketAlt, FaUserSecret, FaLocationArrow
} from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatSystem from '../components/ChatSystem';
import ReserveMerchantCalendar from '../components/reserve/ReserveMerchantCalendar';
import ReserveBookingEngine from '../components/reserve/ReserveBookingEngine';
import ReserveCommercialPolicies from '../components/reserve/ReserveCommercialPolicies';
import ReserveMerchantPortal from '../components/reserve/ReserveMerchantPortal';
import ReserveVirtualTourManager from '../components/reserve/ReserveVirtualTourManager';
import ReservePartnerIntegrationCenter from '../components/reserve/ReservePartnerIntegrationCenter';
import { syncForfeoReserveAccommodationInventory } from '../lib/forfeoReserveInventoryClient';
import { isForfeoReserveBookingEnabled } from '../lib/forfeoReserveBookingClient';
import { isForfeoReservePolicyEnabled } from '../lib/forfeoReservePolicyClient';
import { isForfeoReserveMerchantPortalEnabled } from '../lib/forfeoReserveMerchantPortalClient';
import { isForfeoReserveVirtualTourEnabled } from '../lib/forfeoReserveVirtualTourClient';
import { isForfeoReserveIntegrationsEnabled } from '../lib/forfeoReserveProductionClient';
import { redirectToSubscription } from '../services/stripePayment';
import BusinessDiningSection from '../components/business/BusinessDiningSection';
import MerchantPerformancePanel from '../components/business/MerchantPerformancePanel';
import MerchantCombosPanel from '../components/business/MerchantCombosPanel';
import MerchantRevenuePanel from '../components/business/MerchantRevenuePanel';
import {
  formatVoucherAmount,
  redeemMerchantVoucher,
  validateMerchantVoucher,
  type ShopVoucher,
  type VoucherScanSource,
} from '../lib/vouchers';
import { useTransactionI18n } from '../lib/transactionI18n';
import '../styles/commerce-v214-global-light.css';

// ======================
// CONSTANTES & HELPERS
// ======================
const CATEGORIES = [
  'Restauration', 'Hébergement', 'Activité & Loisir', 'Service Local',
  'Transport', 'Boutique & Mode', 'Santé & Beauté', 'Événementiel',
  'Art & Culture', 'Vie Nocturne', 'Autre'
];

const HOTEL_AMENITIES_OPTIONS: Record<string, { label: string; icon: React.ReactNode }[]> = {
  'Confort': [
    { label: 'Wi-Fi Gratuit', icon: <FaWifi /> },
    { label: 'Climatisation', icon: <FaSnowflake /> },
    { label: 'Chauffage', icon: <FaFire /> },
  ],
  'Bien-être & Loisir': [
    { label: 'Piscine', icon: <FaSwimmingPool /> },
    { label: 'Spa & Bien-être', icon: <FaSpa /> },
    { label: 'Salle de sport', icon: <FaWeightHanging /> },
  ],
  'Services': [
    { label: 'Réception 24/24', icon: <FaConciergeBell /> },
    { label: 'Petit-déjeuner inclus', icon: <FaUtensils /> },
    { label: 'Service de chambre', icon: <FaConciergeBell /> },
    { label: 'Animaux acceptés', icon: <FaDog /> },
  ],
  'Chambre': [
    { label: 'Télévision', icon: <FaTv /> },
    { label: 'Sèche-cheveux', icon: <FaMagic /> },
    { label: 'Fer à repasser', icon: <FaTiktok /> },
    { label: 'Bureau', icon: <FaBriefcase /> }
  ]
};

const CONTRACT_TEMPLATES = [
  { id: 'service', label: 'Contrat de Services', icon: <FaBriefcase /> },
  { id: 'partnership', label: 'Partenariat Commercial', icon: <FaHandshakeIcon /> },
  { id: 'booking', label: 'Contrat de Réservation', icon: <FaCalendarCheck /> },
  { id: 'employment', label: 'Contrat de Travail', icon: <FaUsers /> },
  { id: 'rental', label: 'Contrat de Location', icon: <FaKey /> },
  { id: 'custom', label: 'Contrat Personnalisé', icon: <FaFileContract /> },
];

const toStr = (v: any) => (v === null || v === undefined ? '' : String(v));
const shortId = (v: any) => toStr(v).slice(0, 8).toUpperCase();
const toNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const COLOR_MAP: Record<string, { text: string; soft: string }> = {
  blue: { text: 'text-blue-500', soft: 'bg-blue-50' },
  green: { text: 'text-green-500', soft: 'bg-green-50' },
  purple: { text: 'text-purple-500', soft: 'bg-purple-50' },
  orange: { text: 'text-orange-500', soft: 'bg-orange-50' },
  red: { text: 'text-red-500', soft: 'bg-red-50' },
  yellow: { text: 'text-yellow-500', soft: 'bg-yellow-50' },
  slate: { text: 'text-slate-500', soft: 'bg-slate-50' },
  indigo: { text: 'text-indigo-500', soft: 'bg-indigo-50' },
};

// Palette "Forfeo Explore" (identique à la page publique de réservation)
const TILE_TONES: Record<string, string> = {
  reserve: 'fx-tone-blue',
  'virtual-tour': 'fx-tone-violet',
  integrations: 'fx-tone-cyan',
  performance: 'fx-tone-blue',
  combos: 'fx-tone-cyan',
  revenues: 'fx-tone-green',
  scanner: 'fx-tone-indigo',
  booking: 'fx-tone-blue',
  orders: 'fx-tone-green',
  calendar: 'fx-tone-amber',
  hotel: 'fx-tone-blue',
  drops: 'fx-tone-cyan',
  presale: 'fx-tone-amber',
  boutique: 'fx-tone-pink',
  lastcall: 'fx-tone-red',
  shop: 'fx-tone-green',
  wallet: 'fx-tone-amber',
  messages: 'fx-tone-indigo',
  posts: 'fx-tone-pink',
  loyalty: 'fx-tone-violet',
  sign: 'fx-tone-slate',
  stats: 'fx-tone-cyan',
  profile: 'fx-tone-slate',
  accountLifecycle: 'fx-tone-blue',
};

// ======================
// TYPES
// ======================
type ToastType = 'success' | 'error' | 'warning' | 'info';
type UserRole = 'director' | 'employee';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

// ======================
// CONTEXT POUR LES TOASTS
// ======================
const ToastContext = React.createContext<{
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}>({ addToast: () => {}, removeToast: () => {} });

const useToast = () => React.useContext(ToastContext);

// ======================
// COMPOSANT TOAST CONTAINER
// ======================
const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    const newToast = { ...toast, id, duration: toast.duration || 5000 };
    setToasts(prev => [...prev, newToast]);

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => removeToast(id), newToast.duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <div className="fixed top-24 right-4 z-[100] space-y-3 w-96 pointer-events-none">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`fx-toast fx-toast-${toast.type} pointer-events-auto flex items-start gap-3 rounded-2xl p-4`}
          >
            <div className="pt-0.5">
              {toast.type === 'success' && <FaCheckCircle className="text-emerald-600" size={18} />}
              {toast.type === 'error' && <FaTimesCircle className="text-rose-600" size={18} />}
              {toast.type === 'warning' && <FaExclamationTriangle className="text-amber-600" size={18} />}
              {toast.type === 'info' && <FaInfoCircle className="text-blue-600" size={18} />}
            </div>
            <div className="flex-1">
              <h4 className="fx-toast-title">{toast.title}</h4>
              <p className="fx-toast-text">{toast.message}</p>
              {toast.action && (
                <button
                  onClick={() => {
                    toast.action?.onClick();
                    removeToast(toast.id);
                  }}
                  className="fx-btn fx-btn-ghost mt-3"
                >
                  {toast.action.label}
                </button>
              )}
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="fx-icon-btn"
              aria-label="Fermer"
            >
              <FaTimes size={12} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// ======================
// HOOK PERSONNALISÉ USESMARTDASHBOARD
// ======================
const useSmartDashboard = (business: any) => {
  const [predictions, setPredictions] = useState({
    peakHours: [] as number[],
    suggestedPrices: {} as Record<string, number>,
    todayRevenueForecast: 0
  });
  const [isOffline, setIsOffline] = useState(false);
  const [pendingSync, setPendingSync] = useState<any[]>([]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const compressImage = useCallback(async (file: File, maxWidth = 1200, quality = 0.8): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Compression failed'));
          }, 'image/jpeg', quality);
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const queueAction = useCallback((action: any) => {
    setPendingSync(prev => [...prev, { ...action, timestamp: Date.now() }]);
  }, []);

  return {
    predictions,
    isOffline,
    pendingSyncCount: pendingSync.length,
    compressImage,
    queueAction
  };
};

// ======================
// SMART SCANNER WIDGET
// ======================
interface SmartScannerWidgetProps {
  business: any;
  onValidate: (code: string, source?: VoucherScanSource) => Promise<void>;
  scanLoading: boolean;
  scanMessage: any;
  quickMode?: boolean;
}

const SmartScannerWidget: React.FC<SmartScannerWidgetProps> = React.memo(({
  business,
  onValidate,
  scanLoading,
  scanMessage,
  quickMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraMessage, setCameraMessage] = useState<string | null>(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerControlsRef = useRef<IScannerControls | null>(null);
  const { tx } = useTransactionI18n();

  const stopCamera = useCallback(() => {
    scannerControlsRef.current?.stop();
    scannerControlsRef.current = null;
    setIsCameraOpen(false);
    setCameraLoading(false);
  }, []);

  const handleCameraScanResult = useCallback(async (value: string) => {
    const cleanValue = value.trim();
    if (!cleanValue) return;

    setScanCode(cleanValue);
    stopCamera();
    await onValidate(cleanValue, 'camera');
    setScanCode('');
  }, [onValidate, stopCamera]);

  const startCamera = useCallback(() => {
    setCameraMessage(null);

    if (typeof window === 'undefined' || !navigator?.mediaDevices?.getUserMedia) {
      setCameraMessage(tx('cameraNotAvailable'));
      return;
    }

    if (!window.isSecureContext && window.location.hostname !== 'localhost') {
      setCameraMessage(tx('cameraRequiresHttps'));
      return;
    }

    setCameraLoading(true);
    setIsCameraOpen(true);
  }, [tx]);

  useEffect(() => {
    if (!isCameraOpen || !videoRef.current) return;

    let active = true;
    const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 250 });

    reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
      if (!active || !result) return;
      active = false;
      void handleCameraScanResult(result.getText());
    })
      .then((controls) => {
        if (!active) {
          controls.stop();
          return;
        }
        scannerControlsRef.current = controls;
        setCameraLoading(false);
        setCameraMessage(tx('cameraActive'));
      })
      .catch((error: any) => {
        active = false;
        scannerControlsRef.current = null;
        setCameraLoading(false);
        setIsCameraOpen(false);
        setCameraMessage(
          error?.name === 'NotAllowedError'
            ? tx('cameraPermissionDenied')
            : tx('cameraStartError')
        );
      });

    return () => {
      active = false;
      scannerControlsRef.current?.stop();
      scannerControlsRef.current = null;
    };
  }, [handleCameraScanResult, isCameraOpen, tx]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (scanCode.trim()) {
      onValidate(scanCode, 'manual');
      setScanCode('');
    }
  }, [scanCode, onValidate]);

  if (!isExpanded && quickMode) {
    return (
      <button
        type="button"
        onClick={() => {
          setIsExpanded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="fx-scan-cta group flex h-full w-full flex-col justify-center rounded-2xl p-6 text-left"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="fx-scan-cta-icon">
              <FaQrcode size={22} />
            </span>
            <span>
              <span className="fx-scan-cta-title">{tx('quickScanner')}</span>
              <span className="fx-scan-cta-sub">{tx('validateThisCode')}</span>
            </span>
          </div>
          <FaExpand className="fx-scan-cta-expand" />
        </div>
      </button>
    );
  }

  return (
    <section className={`fx-card overflow-hidden ${quickMode ? 'h-full' : ''}`}>
      <header className="fx-card-head">
        <div className="flex items-center gap-3">
          <span className="fx-head-icon fx-tone-indigo"><FaQrcode size={16} /></span>
          <h3 className="fx-card-title">{tx('validation')}</h3>
        </div>
        {quickMode && (
          <button onClick={() => setIsExpanded(false)} className="fx-icon-btn" aria-label="Réduire">
            <FaCompress size={14} />
          </button>
        )}
      </header>

      <div className="p-6">
        <form onSubmit={handleSubmit} className="relative w-full">
          <input
            ref={inputRef}
            type="text"
            value={scanCode}
            onChange={(e) => setScanCode(e.target.value)}
            placeholder={tx('scannerPlaceholder')}
            className="fx-code-input"
          />

          <button
            type="submit"
            disabled={scanLoading || !scanCode.trim()}
            className="fx-btn fx-btn-primary mt-4 w-full"
          >
            {scanLoading ? <FaSync className="animate-spin" /> : <FaCheckCircle />}
            {scanLoading ? tx('checking') : tx('validateThisCode')}
          </button>

          <button
            type="button"
            onClick={isCameraOpen ? stopCamera : startCamera}
            disabled={scanLoading || cameraLoading}
            className={`fx-btn mt-3 w-full ${isCameraOpen ? 'fx-btn-danger-soft' : 'fx-btn-dark'}`}
          >
            {cameraLoading ? <FaSync className="animate-spin" /> : isCameraOpen ? <FaTimes /> : <FaCamera />}
            {cameraLoading ? tx('openingCamera') : isCameraOpen ? tx('closeCamera') : tx('scanWithCamera')}
          </button>
        </form>

        {(isCameraOpen || cameraMessage) && (
          <div className="fx-camera-wrap mt-4">
            {isCameraOpen && (
              <div className="relative min-h-[260px] overflow-hidden rounded-xl bg-slate-900">
                <video ref={videoRef} className="h-[280px] w-full object-cover" muted playsInline />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-56 w-56 rounded-2xl border-2 border-white/90 shadow-[0_0_0_9999px_rgba(15,23,42,0.35)]" />
                </div>
                <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 px-4 py-3 text-center backdrop-blur">
                  <p className="fx-camera-hint">{tx('placeQrInFrame')}</p>
                  <p className="fx-camera-sub">{tx('cameraCompatible')}</p>
                </div>
              </div>
            )}
            {cameraMessage && <p className="fx-camera-msg">{cameraMessage}</p>}
          </div>
        )}

        {scanMessage && (
          <div className={`fx-note mt-4 fx-note-${scanMessage.type}`}>
            <div className="pt-0.5">
              {scanMessage.type === 'success' && <FaCheckCircle size={18} />}
              {scanMessage.type === 'error' && <FaTimesCircle size={18} />}
              {scanMessage.type === 'warning' && <FaExclamationTriangle size={18} />}
            </div>
            <div>
              <h4 className="fx-note-title">
                {scanMessage.type === 'success' ? tx('validPass') :
                 scanMessage.type === 'error' ? tx('failure') : tx('warning')}
              </h4>
              <p className="fx-note-text">{scanMessage.text}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

// ======================
// SÉLECTEUR DE RÔLE
// ======================
const RoleSelector: React.FC<{
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}> = React.memo(({ currentRole, onRoleChange }) => {
  const { tx } = useTransactionI18n();
  return (
    <div className="fx-segmented" data-commerce-panel="virtual-tour">
      <button
        onClick={() => onRoleChange('director')}
        className={`fx-segmented-item ${currentRole === 'director' ? 'is-active' : ''}`}
      >
        <FaUserShield size={14} /> {tx('director')}
      </button>
      <button
        onClick={() => onRoleChange('employee')}
        className={`fx-segmented-item ${currentRole === 'employee' ? 'is-active' : ''}`}
      >
        <FaShieldAlt size={14} /> {tx('employee')}
      </button>
    </div>
  );
});

// ======================
// COMPOSANTS DE BASE
// ======================
const StatCard = React.memo(({ title, value, icon: Icon, color }: any) => {
  const tone = `fx-tone-${color === 'slate' ? 'slate' : color}`;
  return (
    <div className="fx-stat">
      <span className={`fx-stat-icon ${tone}`}>
        <Icon size={16} />
      </span>
      <p className="fx-stat-label">{title}</p>
      <p className="fx-stat-value">{value}</p>
    </div>
  );
});

const AiMagicButton = React.memo(({ onClick, loading, label = 'Générer une description' }: any) => (
  <button type="button" onClick={onClick} disabled={loading} className="fx-ai-btn">
    {loading ? <FaSync className="animate-spin" /> : <FaMagic />}
    {loading ? 'Rédaction…' : label}
  </button>
));

// ======================
// COMPOSANT PRINCIPAL
// ======================
const FORFEO_RESERVE_MERCHANT_VISIBLE_2026_08_03 = true;

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const { tx, voucherStatus, locale, scanSource } = useTransactionI18n();
  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>('director');

  const { isOffline, pendingSyncCount, compressImage } = useSmartDashboard(business);

  const [activeTab, setActiveTab] = useState<
    'overview' | 'offersHub' | 'reserveHub' | 'marketingHub' | 'teamHub' | 'settingsHub' | 'performance' | 'combos' | 'revenues' | 'stats' | 'profile' | 'posts' | 'messages' | 'loyalty' | 'lastcall' | 'shop' | 'orders' | 'hotel' | 'wallet' | 'booking' | 'calendar' | 'reserve' | 'sign' | 'boutique' | 'scanner' | 'presale' | 'drops'
  >((() => {
    if (typeof window === 'undefined') return 'overview';
    const requestedTab = new URLSearchParams(window.location.search).get('tab') || '';
    const allowedInitialTabs = new Set([
      'overview', 'offersHub', 'reserveHub', 'marketingHub', 'teamHub', 'settingsHub',
      'performance', 'combos', 'revenues', 'stats', 'profile', 'posts', 'messages',
      'loyalty', 'lastcall', 'shop', 'orders', 'hotel', 'wallet', 'booking', 'calendar',
      'reserve', 'sign', 'boutique', 'scanner', 'presale', 'drops', 'virtual-tour',
      'integrations', 'accountLifecycle',
    ]);
    return (allowedInitialTabs.has(requestedTab) ? requestedTab : 'overview') as any;
  })());

  const [tutorials, setTutorials] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const [stats, setStats] = useState({ views: 0, clicks: 0, revenue: 0 });
  const isPremium = business?.plan_type === 'premium' || business?.is_pro === true;

  // --- ÉTATS ---
  const [aiLoading, setAiLoading] = useState({
    hotel: false,
    deal: false,
    product: false,
    post: false,
    sync: false,
    mystery: false,
    boutique: false,
    presale: false
  });
  const [uploading, setUploading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [mysteryImgProcessing, setMysteryImgProcessing] = useState(false);
  const [mysteryPreviewMode, setMysteryPreviewMode] = useState<'public' | 'secret'>('public');
  const [mysteryAdminShowRealById, setMysteryAdminShowRealById] = useState<Record<string, boolean>>({});

  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const roomFileInputRef = useRef<HTMLInputElement>(null);
  const dealFileInputRef = useRef<HTMLInputElement>(null);
  const productFileInputRef = useRef<HTMLInputElement>(null);
  const postFileInputRef = useRef<HTMLInputElement>(null);
  const mysteryFileInputRef = useRef<HTMLInputElement>(null);
  const boutiqueFileInputRef = useRef<HTMLInputElement>(null);
  const presaleFileInputRef = useRef<HTMLInputElement>(null);

  const [orders, setOrders] = useState<any[]>([]);
  const [shopVouchers, setShopVouchers] = useState<ShopVoucher[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const [reserveAccommodationId, setReserveAccommodationId] = useState<string | null>(() => (
    new URLSearchParams(window.location.search).get('reserveAccommodation')
  ));

  const openReserveCalendar = (accommodationId: string | null) => {
    setReserveAccommodationId(accommodationId);
    setCalendarKey((current) => current + 1);
    setActiveTab('calendar');

    const url = new URL(window.location.href);
    if (accommodationId) {
      url.searchParams.set('reserveAccommodation', accommodationId);
    } else {
      url.searchParams.delete('reserveAccommodation');
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  };

  useEffect(() => {
    const requestedAccommodationId = new URLSearchParams(
      window.location.search,
    ).get('reserveAccommodation');

    if (!requestedAccommodationId) return;

    setReserveAccommodationId(
      requestedAccommodationId,
    );

    setActiveTab('calendar');
  }, []);
  const [deals, setDeals] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [mysteryOffers, setMysteryOffers] = useState<any[]>([]);
  const [boutiqueForfaits, setBoutiqueForfaits] = useState<any[]>([]);
  const [boutiqueReviews, setBoutiqueReviews] = useState<any[]>([]);
  const [expandedMysteryId, setExpandedMysteryId] = useState<string | null>(null);

  // ✅ PRÉVENTE
  const [presaleOffers, setPresaleOffers] = useState<any[]>([]);
  const [newPresale, setNewPresale] = useState({
    title: '',
    description: '',
    price: '',
    original_price: '',
    features: '',
    quantity: 100,
    secret_code: '',
    image_url: ''
  });

  // ✅ DROPS GÉOLOCALISÉS
  const [drops, setDrops] = useState<any[]>([]);
  const [newDrop, setNewDrop] = useState({
    title: '',
    description: '',
    reward: '',
    radius_meters: 50,
    drop_address: '',
    drop_city: '',
    drop_postal_code: '',
    drop_region: 'Québec',
    drop_country: 'Canada'
  });

  // SCANNER
  const [scanCode, setScanCode] = useState('');
  const [scanMessage, setScanMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [validatedShopVoucher, setValidatedShopVoucher] = useState<ShopVoucher | null>(null);
  const [voucherRedeemLoading, setVoucherRedeemLoading] = useState(false);
  const [lastVoucherScanSource, setLastVoucherScanSource] = useState<VoucherScanSource>('manual');

  const [hotelDetails, setHotelDetails] = useState({
    amenities: [] as string[],
    rules: {
      check_in: '15:00',
      check_out: '11:00',
      pets_allowed: false,
      smoking_allowed: false,
      parties_allowed: false,
      custom_rules: ''
    },
    faq: [] as { question: string; answer: string }[]
  });
  const [hotelDetailsLoading, setHotelDetailsLoading] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    region: 'Québec',
    website: '',
    email: '',
    category: 'Restauration',
    image_url: '',
    latitude: '',
    longitude: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: ''
  });

  const [passwordState, setPasswordState] = useState({ new: '', confirm: '' });

  const [newRoom, setNewRoom] = useState<{
    name: string;
    description: string;
    price_per_night: string;
    capacity_adults: number;
    images: string[];
  }>({
    name: '',
    description: '',
    price_per_night: '',
    capacity_adults: 2,
    images: []
  });

  const [newDeal, setNewDeal] = useState({
    title: '',
    description: '',
    original_price: '',
    discount_price: '',
    quantity_available: 5,
    expires_in_hours: 24,
    image_url: '',
    party_size: 2,
    slot_start_local: '',
    slot_end_local: ''
  });

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    stock_quantity: 10,
    weight_kg: 0.5,
    length_cm: 10,
    width_cm: 10,
    height_cm: 10,
    image_url: ''
  });

  const [newBoutiqueForfait, setNewBoutiqueForfait] = useState({
    title: '',
    description: '',
    original_price: '',
    price: '',
    category: 'Détente',
    includes: '',
    conditions: '',
    expiration: '',
    image_url: ''
  });

  const [newPost, setNewPost] = useState({ content: '', image_url: '' });
  const [newReward, setNewReward] = useState({ title: '', cost: 500, description: '' });
  const [loyaltyScan, setLoyaltyScan] = useState({ id: '', points: 10, message: '' });

  const [newMysteryOffer, setNewMysteryOffer] = useState<any>({
    title: '',
    description: '',
    original_price: '',
    mystery_price: '',
    quantity_available: 5,
    expires_in_hours: 72,
    image_url: '',
    secret_image_url: '',
    blur_enabled: true,
    blur_radius: 16,
    reveal_after_purchase: true,
  });

  const [selectedRes, setSelectedRes] = useState<any>(null);
  const [bookingSettings, setBookingSettings] = useState<any>({
    cal_link: '',
    qlo_link: '',
    is_booking_enabled: false
  });

  const ALL_MENU_ITEMS = useMemo(() => [
    ...(isForfeoReserveMerchantPortalEnabled() ? [{ id: 'reserve', icon: <FaConciergeBell />, label: 'Portail Reserve', color: 'bg-gradient-to-br from-indigo-600 to-blue-800', special: true }] : []),
    ...(isForfeoReserveVirtualTourEnabled() ? [{ id: 'virtual-tour', icon: <FaVideo />, label: 'Visite virtuelle 360°', color: 'bg-gradient-to-br from-violet-600 to-fuchsia-700', special: true }] : []),
    ...(isForfeoReserveIntegrationsEnabled() ? [{ id: 'integrations', icon: <FaLink />, label: 'Widget & API Reserve', color: 'bg-gradient-to-br from-cyan-600 to-indigo-700', special: true }] : []),
    { id: 'performance', icon: <FaChartLine />, label: 'Performance 2.0', color: 'bg-gradient-to-br from-slate-900 to-blue-700', special: true },
    { id: 'combos', icon: <FaHandshakeIcon />, label: 'Combos Forfeo', color: 'bg-gradient-to-br from-cyan-500 to-blue-700', special: true },
    { id: 'revenues', icon: <FaDollarSign />, label: 'Revenus & commissions', color: 'bg-gradient-to-br from-emerald-500 to-teal-700', special: true },
    { id: 'scanner', icon: <FaQrcode />, label: tx('scannerQrCode'), color: 'bg-gradient-to-br from-indigo-500 to-purple-600', special: true },
    { id: 'booking', icon: <FaListAlt />, label: tx('bookingTab'), color: 'bg-gradient-to-br from-blue-500 to-cyan-500', count: reservations.filter(r => r.status === 'confirmed').length },
    { id: 'orders', icon: <FaShoppingBag />, label: tx('shopOrdersTab'), color: 'bg-gradient-to-br from-emerald-500 to-teal-500', count: orders.filter((o: any) => o.status === 'paid').length },
    { id: 'calendar', icon: <FaCalendarCheck />, label: tx('availabilityCalendar'), color: 'bg-gradient-to-br from-orange-400 to-amber-500' },
    { id: 'hotel', icon: <FaBed />, label: tx('hotelRestaurant'), color: 'bg-gradient-to-br from-sky-500 to-blue-600' },
    { id: 'drops', icon: <FaLocationArrow />, label: 'Forfeo Drops', color: 'bg-gradient-to-br from-cyan-400 to-blue-500' },
    { id: 'presale', icon: <FaTicketAlt />, label: tx('vipPresales'), color: 'bg-gradient-to-br from-yellow-500 to-amber-600' },
    { id: 'boutique', icon: <FaStore />, label: tx('packageShop'), color: 'bg-gradient-to-br from-fuchsia-500 to-pink-600' },
    { id: 'lastcall', icon: <FaFire />, label: tx('flashOffers'), color: 'bg-gradient-to-br from-red-500 to-rose-600' },
    { id: 'shop', icon: <FaBoxOpen />, label: tx('physicalProducts'), color: 'bg-gradient-to-br from-lime-500 to-emerald-600' },
    { id: 'wallet', icon: <FaCoins />, label: tx('stripeWallet'), color: 'bg-gradient-to-br from-yellow-400 to-orange-500' },
    { id: 'messages', icon: <FaEnvelope />, label: tx('messaging'), color: 'bg-gradient-to-br from-indigo-400 to-blue-500', count: unreadCount },
    { id: 'posts', icon: <FaBullhorn />, label: tx('newsPosts'), color: 'bg-gradient-to-br from-pink-500 to-rose-500', count: notificationsUnread },
    { id: 'loyalty', icon: <FaGift />, label: tx('loyaltyClub'), color: 'bg-gradient-to-br from-purple-400 to-fuchsia-500' },
    { id: 'sign', icon: <FaFileSignature />, label: 'Forfeo Sign', color: 'bg-gradient-to-br from-slate-700 to-slate-900' },
    { id: 'stats', icon: <FaChartBar />, label: tx('statistics'), color: 'bg-gradient-to-br from-cyan-400 to-blue-500' },
    { id: 'profile', icon: <FaUserEdit />, label: tx('businessProfile'), color: 'bg-gradient-to-br from-slate-400 to-slate-600' },
    { id: 'accountLifecycle', icon: <FaUserShield />, label: tx('accountManagement'), color: 'bg-gradient-to-br from-blue-600 to-indigo-700' }
  ], [orders, reservations, unreadCount, notificationsUnread, tx]);

  const allowedForEmployee = ['scanner', 'calendar', 'booking', 'reserve', 'messages', 'accountLifecycle'];
  const filteredMenuItems = userRole === 'director'
    ? ALL_MENU_ITEMS
    : ALL_MENU_ITEMS.filter(item => allowedForEmployee.includes(item.id));

  /* COMMERCE_UX1_MERCHANT_NAV_LOCK — merchant-first categorized workspace. */
  const commerceV2PrimaryNav = [
    { id: 'overview', label: "Aujourd’hui", icon: <FaHome /> },
    { id: 'offersHub', label: 'Vendre', icon: <FaStore /> },
    { id: 'reserveHub', label: 'Opérer', icon: <FaCalendarCheck /> },
    { id: 'clientsHub', label: 'Clients', icon: <FaEnvelope /> },
    { id: 'marketingHub', label: 'Marketing', icon: <FaBullhorn /> },
    { id: 'performanceHub', label: 'Performance', icon: <FaChartLine /> },
    { id: 'settingsHub', label: 'Réglages', icon: <FaUserEdit /> },
  ];

  const commerceV2EmployeeNav = new Set([
    'overview', 'reserveHub', 'clientsHub', 'settingsHub',
  ]);

  const commerceV2VisibleNav = userRole === 'director'
    ? commerceV2PrimaryNav
    : commerceV2PrimaryNav.filter(item => commerceV2EmployeeNav.has(item.id));

  const commerceV2SectionForTab = (tab: string) => {
    if (['offersHub', 'hotel', 'drops', 'presale', 'boutique', 'lastcall', 'shop', 'combos'].includes(tab)) return 'offersHub';
    if (['reserveHub', 'reserve', 'booking', 'calendar', 'orders', 'scanner', 'virtual-tour', 'integrations'].includes(tab)) return 'reserveHub';
    if (['clientsHub', 'messages', 'loyalty'].includes(tab)) return 'clientsHub';
    if (['marketingHub', 'posts'].includes(tab)) return 'marketingHub';
    if (['performanceHub', 'stats', 'performance', 'revenues', 'wallet'].includes(tab)) return 'performanceHub';
    if (['settingsHub', 'profile', 'accountLifecycle', 'sign', 'teamHub'].includes(tab)) return 'settingsHub';
    return tab;
  };

  const commerceV2ActiveNav = commerceV2SectionForTab(activeTab);

  const paidOrdersCount = orders.filter((order: any) => ['paid', 'processing', 'pending'].includes(String(order.status || '').toLowerCase())).length;
  const confirmedReservationsCount = reservations.filter((reservation: any) => String(reservation.status || '').toLowerCase() === 'confirmed').length;
  const pendingReservationsCount = reservations.filter((reservation: any) => ['pending', 'requested', 'created'].includes(String(reservation.status || '').toLowerCase())).length;
  const activeOffersCount = deals.length + boutiqueForfaits.length + mysteryOffers.length + presaleOffers.length + drops.length;
  const profileCompletionCount = [formData.name, formData.description, formData.phone, formData.address, formData.image_url].filter(Boolean).length;
  const profileCompletionPercent = Math.round((profileCompletionCount / 5) * 100);

  const commerceV2HubMeta: Record<string, {
    eyebrow: string;
    title: string;
    subtitle: string;
    actionLabel?: string;
    tools: Array<{ id: string; label: string; description: string }>;
  }> = {
    offersHub: {
      eyebrow: 'Vendre',
      title: 'Ce que vous offrez à vos clients',
      subtitle: 'Offres, forfaits, produits physiques, bundles, boutique et ventes de dernière minute.',
      actionLabel: 'Créer avec Forfy IA',
      tools: [
        { id: 'hotel', label: 'Offres & forfaits', description: 'Expériences, hébergement, restaurant et offres premium.' },
        { id: 'shop', label: 'Produits physiques', description: 'Produits, inventaire, poids, dimensions et préparation expédition.' },
        { id: 'combos', label: 'Bundles & collaborations', description: 'Combinaisons d’offres et partenariats marchands.' },
        { id: 'boutique', label: 'Boutique', description: 'Forfaits boutique et cartes d’achat expérientielles.' },
        { id: 'lastcall', label: 'Offres éclair', description: 'Dernières minutes, surplus et offres limitées.' },
        { id: 'presale', label: 'Préventes VIP', description: 'Précommandes, accès privilégiés et lancements.' },
        { id: 'drops', label: 'Drops Forfeo', description: 'Offres géolocalisées et moments spéciaux.' },
      ],
    },
    reserveHub: {
      eyebrow: 'Opérer',
      title: 'Réservations, commandes et exécution',
      subtitle: 'Tout ce que vous devez livrer, confirmer, scanner ou expédier aujourd’hui.',
      actionLabel: 'Voir à traiter',
      tools: [
        ...(isForfeoReserveMerchantPortalEnabled() ? [{ id: 'reserve', label: 'Réservations', description: 'Portail Reserve et suivi des demandes.' }] : []),
        { id: 'calendar', label: 'Agenda & disponibilités', description: 'Calendrier, plages ouvertes et disponibilité à confirmer.' },
        { id: 'booking', label: 'Booking engine', description: 'Réservations reçues et informations client.' },
        { id: 'orders', label: 'Commandes & expédition', description: 'Commandes payées, produits à préparer et flux Shippo côté serveur.' },
        { id: 'scanner', label: 'Scanner QR', description: 'Validation terrain des expériences, cartes et accès.' },
        ...(isForfeoReserveVirtualTourEnabled() ? [{ id: 'virtual-tour', label: 'Visite virtuelle', description: 'Contenus 360° liés au commerce.' }] : []),
        ...(isForfeoReserveIntegrationsEnabled() ? [{ id: 'integrations', label: 'Widget & API', description: 'Intégrations Reserve existantes.' }] : []),
      ],
    },
    clientsHub: {
      eyebrow: 'Clients',
      title: 'Relations, messages et fidélité',
      subtitle: 'Répondez, relancez, fidélisez et transformez les questions répétées en réponses claires.',
      actionLabel: 'Répondre avec Forfy IA',
      tools: [
        { id: 'messages', label: 'Messages', description: 'Questions clients, suivis et conversations non répondues.' },
        { id: 'loyalty', label: 'Fidélité', description: 'Récompenses, points et clients à faire revenir.' },
      ],
    },
    marketingHub: {
      eyebrow: 'Marketing',
      title: 'Promotions et réseaux sociaux',
      subtitle: 'Préparez vos publications à partir d’une offre réelle, puis validez avant diffusion.',
      actionLabel: 'Préparer une publication',
      tools: [
        { id: 'posts', label: 'Posts & réseaux sociaux', description: 'Nouvelles, publications et campagnes à valider.' },
      ],
    },
    performanceHub: {
      eyebrow: 'Performance',
      title: 'Revenus, statistiques et lecture Giftshop',
      subtitle: 'Comprenez ce qui marche. Les montants restent en lecture seule depuis les sources financières autorisées.',
      actionLabel: 'Analyser avec Forfy IA',
      tools: [
        { id: 'performance', label: 'Performance des offres', description: 'Vues, conversions, offres fortes et points faibles.' },
        { id: 'stats', label: 'Statistiques', description: 'Trafic, clics et signaux de demande.' },
        { id: 'revenues', label: 'Revenus', description: 'Lecture des revenus et commissions, source Giftshop.' },
        { id: 'wallet', label: 'Wallet lecture', description: 'Portefeuille marchand en consultation seulement.' },
      ],
    },
    settingsHub: {
      eyebrow: 'Réglages',
      title: 'Profil, intégrations et paramètres',
      subtitle: 'Ce qui configure votre commerce, mais ne doit pas polluer votre journée.',
      tools: [
        { id: 'profile', label: 'Profil commerce', description: 'Fiche publique, adresse, photos, horaires et informations clés.' },
        { id: 'sign', label: 'Contrats', description: 'Forfeo Sign et documents marchands.' },
        { id: 'accountLifecycle', label: 'Compte', description: 'Statut, sécurité et gestion du compte.' },
        { id: 'teamHub', label: 'Équipe et rôles', description: 'Accès internes et rôle employé/directeur.' },
      ],
    },
    teamHub: {
      eyebrow: 'Réglages',
      title: 'Équipe et rôles',
      subtitle: 'Gérez les accès quotidiens sans créer de nouvelles permissions financières.',
      tools: [
        { id: 'messages', label: 'Messages accessibles', description: 'Zone collaborative autorisée.' },
        { id: 'sign', label: 'Contrats', description: 'Documents à préparer ou signer.' },
        { id: 'accountLifecycle', label: 'Compte', description: 'Gestion du compte marchand.' },
      ],
    },
  };

  const getCommerceV2ToolStatus = (toolId: string) => {
    switch (toolId) {
      case 'hotel':
        return activeOffersCount > 0 ? `${activeOffersCount} offres au total` : 'Créer la première offre';
      case 'shop':
        return products.length > 0 ? `${products.length} produits` : 'Ajouter un produit';
      case 'combos':
        return 'Bundles marchands';
      case 'boutique':
        return boutiqueForfaits.length > 0 ? `${boutiqueForfaits.length} forfaits` : 'Aucun forfait';
      case 'lastcall':
        return mysteryOffers.length > 0 ? `${mysteryOffers.length} offres éclair` : 'Aucune active';
      case 'presale':
        return presaleOffers.length > 0 ? `${presaleOffers.length} préventes` : 'Aucune active';
      case 'drops':
        return drops.length > 0 ? `${drops.length} drops` : 'Aucun drop';
      case 'reserve':
      case 'booking':
        return pendingReservationsCount > 0 ? `${pendingReservationsCount} à confirmer` : `${confirmedReservationsCount} confirmées`;
      case 'calendar':
        return bookingSettings.is_booking_enabled ? 'Réservations ouvertes' : 'À configurer';
      case 'orders':
        return paidOrdersCount > 0 ? `${paidOrdersCount} à traiter` : 'Aucune commande';
      case 'scanner':
        return 'Validation QR';
      case 'messages':
        return unreadCount > 0 ? `${unreadCount} non lus` : 'À jour';
      case 'loyalty':
        return rewards.length > 0 ? `${rewards.length} récompenses` : 'Programme à bâtir';
      case 'posts':
        return posts.length > 0 ? `${posts.length} publications` : 'Préparer un post';
      case 'performance':
        return `${stats.views || 0} vues`;
      case 'stats':
        return 'Indicateurs';
      case 'revenues':
        return 'Source : Giftshop';
      case 'wallet':
        return 'Lecture seule';
      case 'profile':
        return `${profileCompletionPercent}% complété`;
      case 'sign':
        return 'Contrats';
      case 'accountLifecycle':
        return 'Compte';
      case 'teamHub':
        return userRole === 'director' ? 'Direction' : 'Employé';
      default:
        return 'Ouvrir';
    }
  };

  const commerceV2CurrentHub = commerceV2HubMeta[activeTab] || null;
  const commerceV2HubTools = commerceV2CurrentHub
    ? commerceV2CurrentHub.tools.filter(tool => userRole === 'director' || allowedForEmployee.includes(tool.id) || ['orders', 'teamHub', 'profile', 'sign'].includes(tool.id))
    : [];

  const commerceV2NavBadge = (navId: string) => {
    if (navId === 'overview') return Math.min(9, pendingReservationsCount + unreadCount + paidOrdersCount);
    if (navId === 'reserveHub') return Math.min(9, pendingReservationsCount + paidOrdersCount);
    if (navId === 'clientsHub') return Math.min(9, unreadCount);
    return 0;
  };

  const commerceV2OverviewCategories = commerceV2VisibleNav
    .filter(item => item.id !== 'overview')
    .map(item => ({
      ...item,
      hub: commerceV2HubMeta[item.id],
      badge: commerceV2NavBadge(item.id),
    }))
    .filter(item => item.hub);

  const merchantTodayStats = [
    { label: 'Réservations', value: confirmedReservationsCount, helper: pendingReservationsCount > 0 ? `${pendingReservationsCount} à confirmer` : 'À jour' },
    { label: 'Messages', value: unreadCount, helper: unreadCount > 0 ? 'Réponse requise' : 'Aucun non lu' },
    { label: 'Cette semaine', value: `${stats.revenue.toFixed(2)} $`, helper: 'Source revenus existante' },
  ];

  const merchantTodoItems = [
    pendingReservationsCount > 0 ? { label: `Confirmer ${pendingReservationsCount} réservation${pendingReservationsCount > 1 ? 's' : ''}`, tab: 'reserveHub' } : null,
    unreadCount > 0 ? { label: `Répondre à ${unreadCount} message${unreadCount > 1 ? 's' : ''}`, tab: 'clientsHub' } : null,
    paidOrdersCount > 0 ? { label: `Traiter ${paidOrdersCount} commande${paidOrdersCount > 1 ? 's' : ''} ou expédition`, tab: 'reserveHub' } : null,
    profileCompletionPercent < 100 ? { label: `Compléter le profil commerce (${profileCompletionPercent} %)`, tab: 'settingsHub' } : null,
    activeOffersCount === 0 ? { label: 'Créer votre première offre vendable', tab: 'offersHub' } : null,
  ].filter(Boolean) as Array<{ label: string; tab: string }>;

  /* COMMERCE_UX1C_CONTEXTUAL_FORFY_BUTTONS_LOCK — category-aware Forfy IA launcher */
  const commerceUx1cForfyActions: Record<string, {
    taskId: string;
    title: string;
    prompt: string;
    targetTab: string;
    primaryLabel: string;
  }> = {
    offersHub: {
      taskId: 'offer',
      title: 'Créer ou améliorer une offre',
      prompt: `Prépare une offre vendable pour ${formData.name || 'ce commerce'} en tenant compte des offres, produits et forfaits déjà disponibles dans le dashboard.`,
      targetTab: 'offersHub',
      primaryLabel: 'Créer avec Forfy IA',
    },
    reserveHub: {
      taskId: 'operations',
      title: 'Préparer les opérations',
      prompt: `Prépare les prochaines actions opérationnelles : ${pendingReservationsCount} réservation(s) à confirmer, ${paidOrdersCount} commande(s) ou expédition(s) à traiter, scanner QR et agenda.`,
      targetTab: 'reserveHub',
      primaryLabel: 'Préparer avec Forfy IA',
    },
    clientsHub: {
      taskId: 'client',
      title: 'Répondre aux clients',
      prompt: `Prépare les réponses et relances clients pour ${unreadCount} message(s) non lu(s), en gardant un ton humain et commercial.`,
      targetTab: 'messages',
      primaryLabel: 'Répondre avec Forfy IA',
    },
    marketingHub: {
      taskId: 'marketing',
      title: 'Promouvoir une offre réelle',
      prompt: `Prépare une campagne marketing à partir d’une offre ou d’un produit réel du commerce. Ne publie rien sans validation humaine.`,
      targetTab: 'marketingHub',
      primaryLabel: 'Préparer marketing',
    },
    performanceHub: {
      taskId: 'performance',
      title: 'Comprendre les performances',
      prompt: `Analyse les vues, clics, conversions et revenus en lecture seule. Recommande des actions, sans modifier les chiffres ni les flux financiers.`,
      targetTab: 'performanceHub',
      primaryLabel: 'Analyser avec Forfy IA',
    },
    settingsHub: {
      taskId: 'operations',
      title: 'Compléter le commerce',
      prompt: `Aide à compléter le profil commerce, les intégrations, les contrats et les paramètres sans toucher aux secrets ni aux fonctions financières.`,
      targetTab: 'settingsHub',
      primaryLabel: 'Améliorer le profil',
    },
  };

  const openForfyCommerceForCategory = (categoryId: string, selectedTool?: string) => {
    const action = commerceUx1cForfyActions[categoryId] || commerceUx1cForfyActions.offersHub;

    openForfyCommerce({
      source: 'commerce-ux1c-category-button',
      selectedCategory: categoryId,
      selectedTool: selectedTool || null,
      forfyTaskId: action.taskId,
      targetTab: action.targetTab,
      title: action.title,
      prompt: action.prompt,
      primaryLabel: action.primaryLabel,
      dashboardCounts: {
        reservationsPending: pendingReservationsCount,
        reservationsConfirmed: confirmedReservationsCount,
        ordersActionable: paidOrdersCount,
        unreadMessages: unreadCount,
        offers: activeOffersCount,
        products: products.length,
        revenue: stats.revenue,
        profileCompletionPercent,
      },
    });
  };


  /* COMMERCE_UX1D_REAL_OBJECT_PICKERS_LOCK — readonly object summaries for Forfy IA */
  const commerceUx1dLabel = (item: any, fallback: string) =>
    String(item?.title || item?.name || item?.label || item?.business_name || item?.customer_name || item?.description || fallback).slice(0, 96);

  const commerceUx1dStatus = (item: any, fallback = 'Disponible') =>
    String(item?.status || item?.state || item?.approval_status || item?.payment_status || fallback).slice(0, 42);

  const commerceUx1dId = (prefix: string, item: any, index: number) =>
    `${prefix}-${String(item?.id || item?.uuid || item?.slug || item?.order_id || index)}`;

  const commerceUx1dObjectSummaries = {
    offers: [
      ...deals.slice(0, 6).map((item: any, index: number) => ({
        id: commerceUx1dId('offer', item, index),
        type: 'offer',
        label: commerceUx1dLabel(item, `Offre ${index + 1}`),
        status: commerceUx1dStatus(item, 'Offre active'),
        helper: 'Offre ou expérience existante du dashboard',
        source: 'dashboard-offers-readonly',
      })),
      ...boutiqueForfaits.slice(0, 4).map((item: any, index: number) => ({
        id: commerceUx1dId('boutique', item, index),
        type: 'boutique',
        label: commerceUx1dLabel(item, `Forfait boutique ${index + 1}`),
        status: commerceUx1dStatus(item, 'Forfait boutique'),
        helper: 'Forfait boutique existant',
        source: 'dashboard-boutique-readonly',
      })),
      ...mysteryOffers.slice(0, 4).map((item: any, index: number) => ({
        id: commerceUx1dId('lastcall', item, index),
        type: 'lastcall',
        label: commerceUx1dLabel(item, `Offre éclair ${index + 1}`),
        status: commerceUx1dStatus(item, 'Offre éclair'),
        helper: 'Offre dernière minute existante',
        source: 'dashboard-lastcall-readonly',
      })),
      ...presaleOffers.slice(0, 3).map((item: any, index: number) => ({
        id: commerceUx1dId('presale', item, index),
        type: 'presale',
        label: commerceUx1dLabel(item, `Prévente ${index + 1}`),
        status: commerceUx1dStatus(item, 'Prévente'),
        helper: 'Prévente existante',
        source: 'dashboard-presale-readonly',
      })),
      ...drops.slice(0, 3).map((item: any, index: number) => ({
        id: commerceUx1dId('drop', item, index),
        type: 'drop',
        label: commerceUx1dLabel(item, `Drop ${index + 1}`),
        status: commerceUx1dStatus(item, 'Drop'),
        helper: 'Drop géolocalisé existant',
        source: 'dashboard-drops-readonly',
      })),
    ],
    products: products.slice(0, 8).map((item: any, index: number) => ({
      id: commerceUx1dId('product', item, index),
      type: 'product',
      label: commerceUx1dLabel(item, `Produit ${index + 1}`),
      status: commerceUx1dStatus(item, 'Produit physique'),
      helper: 'Produit physique, inventaire et préparation expédition',
      source: 'dashboard-products-readonly',
    })),
    orders: orders.slice(0, 8).map((item: any, index: number) => ({
      id: commerceUx1dId('order', item, index),
      type: 'order',
      label: commerceUx1dLabel(item, `Commande ${shortId(item?.id || index)}`),
      status: commerceUx1dStatus(item, 'Commande à traiter'),
      helper: 'Commande existante. Expédition réelle à faire plus tard côté serveur.',
      source: 'dashboard-orders-readonly',
    })),
    reservations: reservations.slice(0, 8).map((item: any, index: number) => ({
      id: commerceUx1dId('reservation', item, index),
      type: 'reservation',
      label: commerceUx1dLabel(item, `Réservation ${index + 1}`),
      status: commerceUx1dStatus(item, 'Réservation'),
      helper: 'Réservation existante. Reserve demeure autorité de disponibilité.',
      source: 'dashboard-reservations-readonly',
    })),
    messages: unreadCount > 0 ? [{
      id: 'messages-unread-summary',
      type: 'messages',
      label: `${unreadCount} message${unreadCount > 1 ? 's' : ''} client à traiter`,
      status: 'Réponse humaine requise',
      helper: 'Résumé des messages non lus du commerce',
      source: 'dashboard-messages-readonly',
    }] : [],
    marketing: posts.slice(0, 6).map((item: any, index: number) => ({
      id: commerceUx1dId('post', item, index),
      type: 'post',
      label: commerceUx1dLabel(item, `Publication ${index + 1}`),
      status: commerceUx1dStatus(item, 'Publication'),
      helper: 'Publication ou campagne existante',
      source: 'dashboard-posts-readonly',
    })),
    performance: [{
      id: 'performance-overview-readonly',
      type: 'performance',
      label: `${stats.views || 0} vues · ${stats.clicks || 0} clics · ${stats.revenue.toFixed(2)} $`,
      status: 'Lecture seule',
      helper: 'Indicateurs existants. Source financière autorisée requise pour les montants.',
      source: 'dashboard-performance-readonly',
    }],
    profile: [{
      id: 'profile-completion',
      type: 'profile',
      label: `${formData.name || 'Profil commerce'} — ${profileCompletionPercent}% complété`,
      status: profileCompletionPercent >= 100 ? 'Complet' : 'À compléter',
      helper: 'Profil commerce, photos, coordonnées et informations publiques',
      source: 'dashboard-profile-readonly',
    }],
  };

  /* COMMERCE_UX1G_ACTIVE_MODULE_FORFY_LAUNCHER_LOCK — floating Forfy IA follows the current merchant module */
  const commerceUx1gModuleLabel = (tab: string) => {
    const categoryId = commerceV2SectionForTab(tab);
    const hub = commerceV2HubMeta[categoryId];
    const tool = hub?.tools?.find((item) => item.id === tab);

    return tool?.label || hub?.title || tab;
  };

  const commerceUx1gResolveForfyContextForActiveTab = () => {
    const derivedCategory = commerceV2SectionForTab(activeTab);
    const selectedCategory = commerceUx1cForfyActions[derivedCategory] ? derivedCategory : 'offersHub';
    const action = commerceUx1cForfyActions[selectedCategory] || commerceUx1cForfyActions.offersHub;
    const selectedTool = activeTab === selectedCategory ? null : activeTab;
    const activeModuleLabel = commerceUx1gModuleLabel(activeTab);

    return {
      source: 'commerce-ux1g-active-module-floating-button',
      selectedCategory,
      selectedTool,
      activeTab,
      activeModuleLabel,
      forfyTaskId: action.taskId,
      targetTab: action.targetTab,
      title: action.title,
      prompt: `${action.prompt}

Contexte actuel du commerçant : ${activeModuleLabel}.
Module actif : ${activeTab}.
Catégorie active : ${selectedCategory}.
Travaille avec les objets réels disponibles dans ce module, sans publier, sans modifier les paiements, sans générer d’étiquette réelle.`,
      primaryLabel: action.primaryLabel,
      dashboardCounts: {
        reservationsPending: pendingReservationsCount,
        reservationsConfirmed: confirmedReservationsCount,
        ordersActionable: paidOrdersCount,
        unreadMessages: unreadCount,
        offers: activeOffersCount,
        products: products.length,
        revenue: stats.revenue,
        profileCompletionPercent,
      },
    };
  };

  const openForfyCommerce = (forfyLaunchContext: unknown = null) => {
    const explicitForfyLaunchContext =
      forfyLaunchContext &&
      typeof forfyLaunchContext === 'object' &&
      !('nativeEvent' in (forfyLaunchContext as Record<string, unknown>)) &&
      !('currentTarget' in (forfyLaunchContext as Record<string, unknown>))
        ? (forfyLaunchContext as Record<string, any>)
        : null;

    const safeForfyLaunchContext =
      explicitForfyLaunchContext || commerceUx1gResolveForfyContextForActiveTab();

    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('forfy:open', {
      detail: {
        mode: 'commerce',
        source: 'commerce-dashboard',
        businessId: business?.id || null,
        businessName: business?.name || formData?.name || null,
        businessPlan: business?.plan_type || null,
        activeTab,
        activeNav: commerceV2ActiveNav,
        userRole,
        merchantContext: {
          reservations: {
            confirmed: confirmedReservationsCount,
            pending: pendingReservationsCount,
          },
          orders: {
            actionable: paidOrdersCount,
            total: orders.length,
          },
          clients: {
            unreadMessages: unreadCount,
          },
          catalog: {
            offers: activeOffersCount,
            products: products.length,
            boutiqueForfaits: boutiqueForfaits.length,
            lastCall: mysteryOffers.length,
            presales: presaleOffers.length,
            drops: drops.length,
          },
          performance: {
            views: stats.views,
            clicks: stats.clicks,
            revenue: stats.revenue,
            source: 'dashboard-readonly',
          },
          profile: {
            completionPercent: profileCompletionPercent,
            name: formData.name || null,
            category: formData.category || null,
            city: formData.city || null,
          },
          forfyLaunch: safeForfyLaunchContext || null,
          objects: commerceUx1dObjectSummaries,
        },
      },
    }));
  };
  /* COMMERCE_V2_PREMIUM_FUNCTIONAL_PARITY_END */

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const openCommerceTab = (event: Event) => {
      const detail = (event as CustomEvent<{ tab?: string; source?: string }>).detail;
      const requestedTab = detail?.tab || '';
      const allowedTabs = new Set([
        'overview', 'offersHub', 'reserveHub', 'marketingHub', 'teamHub', 'settingsHub',
        'performance', 'combos', 'revenues', 'stats', 'profile', 'posts', 'messages',
        'loyalty', 'lastcall', 'shop', 'orders', 'hotel', 'wallet', 'booking', 'calendar',
        'reserve', 'sign', 'boutique', 'scanner', 'presale', 'drops', 'virtual-tour',
        'integrations', 'accountLifecycle',
      ]);

      if (!allowedTabs.has(requestedTab)) return;

      setActiveTab(requestedTab as any);

      const url = new URL(window.location.href);
      url.searchParams.set('tab', requestedTab);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };

    window.addEventListener('commerce:open-tab', openCommerceTab as EventListener);
    return () => window.removeEventListener('commerce:open-tab', openCommerceTab as EventListener);
  }, []);

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const blobToJpeg = (blob: Blob) =>
    new Promise<ImageBitmap>((resolve, reject) => {
      // @ts-ignore
      if (typeof createImageBitmap === 'function') {
        // @ts-ignore
        createImageBitmap(blob).then(resolve).catch(reject);
      } else {
        reject(new Error('createImageBitmap indisponible'));
      }
    });

  const blurImageUrlToBlob = async (url: string, blurPx: number): Promise<Blob> => {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`Fetch image failed (${res.status})`);
    const srcBlob = await res.blob();

    let bmp: ImageBitmap | null = null;
    try {
      bmp = await blobToJpeg(srcBlob);
    } catch {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("Impossible de charger l'image pour flou"));
        el.src = URL.createObjectURL(srcBlob);
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D non disponible');
      ctx.filter = `blur(${Math.max(0, blurPx)}px)`;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      return await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob() échoué')), 'image/jpeg', 0.92);
      });
    }

    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D non disponible');
    ctx.filter = `blur(${Math.max(0, blurPx)}px)`;
    ctx.drawImage(bmp, 0, 0);

    return await new Promise((resolve, reject) => {
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob() échoué')), 'image/jpeg', 0.92);
    });
  };

  const uploadBlobToBusinessMedia = async (path: string, blob: Blob) => {
    const { error } = await supabase.storage
      .from('business-media')
      .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage.from('business-media').getPublicUrl(path);
    return publicUrl;
  };

  const generateMysteryBlurredPublicImage = async () => {
    const src = newMysteryOffer.secret_image_url || newMysteryOffer.image_url;
    if (!src) return alert("Ajoute d'abord une image.");
    if (!business?.id) return;

    setMysteryImgProcessing(true);
    try {
      const blurPx = toNum(newMysteryOffer.blur_radius, 16);
      const blurredBlob = await blurImageUrlToBlob(src, blurPx);
      const fileName = `${business.id}/mystery_blur/${Date.now()}-mystery-blur.jpg`;
      const publicUrl = await uploadBlobToBusinessMedia(fileName, blurredBlob);

      setNewMysteryOffer((prev: any) => ({
        ...prev,
        image_url: publicUrl
      }));

      alert('✅ Image floutée générée (publique) !');
    } catch (e: any) {
      alert(`❌ Erreur flou: ${e?.message || 'Erreur inconnue'}`);
    } finally {
      setMysteryImgProcessing(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('success') === 'true' && business && !business.is_hotel_active) {
      supabase
        .from('businesses')
        .update({ is_hotel_active: true, is_pro: true, plan_type: 'premium' })
        .eq('id', business.id)
        .then(({ error }) => {
          if (!error) {
            setBusiness((prev: any) => ({
              ...prev,
              is_hotel_active: true,
              is_pro: true,
              plan_type: 'premium'
            }));
            alert('🎉 Mode Partenaire Premium activé !');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
    }

    if (params.get('connect') === 'success') {
      alert('🎉 Compte bancaire connecté à Stripe avec succès !');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchBusinessData();
    }
  }, [business]);

  useEffect(() => {
    if (business?.owner_id && business?.id) {
      const fetchUnread = async () => {
        const { count } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', business.owner_id)
          .eq('is_read', false)
          .neq('sender_id', business.owner_id);

        setUnreadCount(count || 0);
      };

      fetchUnread();

      const fetchNotifications = async () => {
        const { count } = await supabase
          .from('business_notifications')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', business.id)
          .eq('is_read', false);
        setNotificationsUnread(count || 0);
      };
      fetchNotifications();

      const sub = supabase.channel('business-dashboard-realtime')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'business_notifications', filter: `business_id=eq.${business.id}` },
          () => { setNotificationsUnread(prev => prev + 1); }
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'messages', filter: `receiver_id=eq.${business.owner_id}` },
          () => { fetchUnread(); }
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'shop_orders', filter: `business_id=eq.${business.id}` },
          (payload) => {
            alert(`🔔 Nouvelle commande de ${toNum((payload as any).new.total_amount)}$ !`);
            setOrders(prev => [(payload as any).new, ...prev]);
            setStats(prev => ({ ...prev, revenue: prev.revenue + toNum((payload as any).new.total_amount) }));
          }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'bookings', filter: `business_id=eq.${business.id}` },
          (payload) => {
            setCalendarKey(prev => prev + 1);
            fetchBusinessData();
            if ((payload as any).eventType === 'INSERT') {
              alert('🔔 Nouvelle réservation !');
            }
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'businesses', filter: `id=eq.${business.id}` },
          (payload) => {
            if (payload.new && typeof payload.new.views_count === 'number') {
              setStats(prev => ({ ...prev, views: payload.new.views_count }));
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(sub);
      };
    }
  }, [business]);

  const fetchBusinessData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
      return;
    }

    const { data: busData, error: busError } = await supabase
      .from('businesses')
      .select('*')
      .eq('owner_id', session.user.id)
      .single();

    if (busError || !busData) {
      navigate('/register-business');
      return;
    }

    setBusiness(busData);
    setFormData({
      name: busData.name || '',
      description: busData.description || '',
      phone: busData.phone || '',
      address: busData.address || '',
      city: busData.city || '',
      postal_code: busData.postal_code || '',
      region: busData.region || 'Québec',
      website: busData.website || '',
      email: busData.contact_email || session.user.email || '',
      category: busData.category || 'Restauration',
      image_url: busData.image_url || '',
      latitude: busData.latitude?.toString() || '',
      longitude: busData.longitude?.toString() || '',
      facebook_url: busData.facebook_url || '',
      instagram_url: busData.instagram_url || '',
      tiktok_url: busData.tiktok_url || ''
    });

    setStats(prev => ({
      ...prev,
      views: toNum(busData.views_count),
      clicks: toNum(busData.clicks_count)
    }));

    const hasHotelCols =
      Object.prototype.hasOwnProperty.call(busData, 'hotel_amenities') ||
      Object.prototype.hasOwnProperty.call(busData, 'hotel_rules') ||
      Object.prototype.hasOwnProperty.call(busData, 'hotel_faq');

    if (hasHotelCols) {
      setHotelDetails(prev => ({
        ...prev,
        amenities: Array.isArray(busData.hotel_amenities) ? busData.hotel_amenities : [],
        rules:
          busData.hotel_rules && typeof busData.hotel_rules === 'object'
            ? { ...prev.rules, ...busData.hotel_rules }
            : prev.rules,
        faq: Array.isArray(busData.hotel_faq) ? busData.hotel_faq : [],
      }));
    }

    const bid = busData.id;

    Promise.all([
      supabase.from('shop_orders').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('bookings').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('accommodations').select('*').eq('business_id', bid).eq('is_available', true),
      supabase.from('last_call_deals').select('*').eq('business_id', bid).gt('expires_at', new Date().toISOString()).order('expires_at', { ascending: true }),
      supabase.from('shop_products').select('*').eq('business_id', bid).eq('is_active', true).order('created_at', { ascending: false }),
      supabase.from('posts').select('*, comments:post_comments(id, content, created_at)').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('rewards').select('*').eq('business_id', bid).order('cost_points', { ascending: true }),
      supabase.from('business_booking_settings').select('*').eq('business_id', bid).maybeSingle(),
      supabase.from('mystery_hotel_offers').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('business_hotel_details').select('*').eq('business_id', bid).maybeSingle(),
      supabase.from('boutique_forfaits').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('boutique_reviews').select('*, boutique_forfaits!inner(title, business_id)').eq('boutique_forfaits.business_id', bid).order('created_at', { ascending: false }),
      supabase.from('business_tutorials').select('*').order('created_at', { ascending: false }),
      supabase.from('presale_offers').select('*').eq('business_id', bid).order('created_at', { ascending: false }),
      supabase.from('shop_order_vouchers').select('*').eq('business_id', bid).order('updated_at', { ascending: false }).limit(10)
    ]).then(results => {
      setOrders(results[0].data || []);

      const rawBookings = results[1].data || [];
      const formattedRes = rawBookings.map((b: any) => ({
        id: b.id,
        guest_name: b.guest_name || tx('customer'),
        guest_email: b.guest_email,
        start_at: b.check_in_date,
        end_at: b.check_out_date,
        status: b.status,
        payment_status: b.payment_status,
        total_price: b.total_price
      }));
      setReservations(formattedRes);

      setAccommodations(results[2].data || []);
      setDeals(results[3].data || []);
      setProducts(results[4].data || []);
      setPosts(results[5].data || []);
      setRewards(results[6].data || []);

      const settings = results[7].data;
      if (settings) {
        setBookingSettings({
          ...settings,
          cal_link: settings.cal_username ? `https://cal.com/${settings.cal_username}/${settings.cal_event_slug || ''}` : '',
          qlo_link: settings.qlo_endpoint_url || ''
        });
      }

      setMysteryOffers(results[8].data || []);

      const details = results[9].data;
      if (!hasHotelCols && details) {
        setHotelDetails(prev => ({
          ...prev,
          amenities: details.amenities || [],
          rules: details.rules && typeof details.rules === 'object'
            ? { ...prev.rules, ...details.rules }
            : prev.rules,
          faq: Array.isArray(details.faq) ? details.faq : []
        }));
      }

      setBoutiqueForfaits(results[10].data || []);
      setBoutiqueReviews(results[11].data || []);
      setTutorials(results[12].data || []);
      setPresaleOffers(results[13].data || []);
      setShopVouchers((results[14]?.data || []) as ShopVoucher[]);

      const ordersRevenue = (results[0].data || [])
        .filter((o: any) => o.status === 'paid' || o.status === 'picked_up')
        .reduce((sum: number, o: any) => sum + toNum(o.total_amount), 0);

      const resRevenue = formattedRes
        .filter((r: any) => r.payment_status === 'paid')
        .reduce((sum: number, r: any) => sum + toNum(r.total_price), 0);

      setStats(prev => ({ ...prev, revenue: ordersRevenue + resRevenue }));
      setLoading(false);
    });
  };

  const getErrorMessage = (errorCode: string): string => {
    const messages: Record<string, string> = {
      'legacy_pickup_not_found': tx('voucherNotFound') || 'Code introuvable',
      'voucher_not_found': tx('voucherNotFound') || 'Code introuvable',
      'voucher_invalid': tx('invalidVoucher') || 'Code non valide',
      'invalid_user': 'Session expirée. Reconnectez-vous.',
      'forbidden': 'Non autorisé pour cette entreprise',
      'experience_pass_not_found': tx('voucherNotFound') || 'Code introuvable',
      'experience_business_mismatch': 'Code appartient à une autre entreprise',
    };
    return messages[errorCode] || (errorCode || tx('unknown') || 'Erreur inconnue');
  };

  const handleValidateVoucher = useCallback(async (
    codeToValidate?: string,
    source: VoucherScanSource = 'manual'
  ) => {
    const code = codeToValidate || scanCode;

    if (!code.trim()) {
      alert(tx('enterVoucherCode'));
      return;
    }

    if (!business?.id) {
      alert(tx('businessNotFound'));
      return;
    }

    setScanLoading(true);
    setScanMessage(null);
    setValidatedShopVoucher(null);
    setLastVoucherScanSource(source);

    try {
      const response = await validateMerchantVoucher(
        code.trim(),
        business.id,
        source
      );

      if (!response?.ok || !response.voucher) {
        throw new Error(response?.error || 'voucher_invalid');
      }

      const voucher = response.voucher;
      const status = String(
        voucher.effective_status || voucher.status || ''
      ).toLowerCase();

      const client =
        voucher.order?.customer_name ||
        voucher.customer_email ||
        voucher.order?.customer_email ||
        tx('customer');

      const title =
        voucher.title ||
        voucher.item?.title ||
        tx('voucherForfeo');

      const amount = formatVoucherAmount(
        voucher.amount_cents,
        voucher.currency || 'cad'
      );

      setValidatedShopVoucher(voucher);

      if (status === 'active') {
        setScanMessage({
          type: 'success',
          text: `${tx('voucherActiveFor')} ${client}. ${title} — ${amount}. ${tx('canMarkRedeemed')}`
        });
      } else if (status === 'redeemed') {
        setScanMessage({
          type: 'warning',
          text: `${tx('voucherAlreadyUsed')}${
            voucher.redeemed_at
              ? ` — ${tx('usedOn')} ${new Date(
                  voucher.redeemed_at
                ).toLocaleDateString(locale)}`
              : ''
          }.`
        });
      } else if (status === 'expired') {
        setScanMessage({
          type: 'error',
          text: tx('voucherExpiredCannotUse')
        });
      } else if (status === 'cancelled' || status === 'canceled') {
        setScanMessage({
          type: 'error',
          text: tx('voucherCancelledCannotUse')
        });
      } else {
        setScanMessage({
          type: 'warning',
          text: `${tx('voucherFoundStatus')} : ${voucherStatus(status)}.`
        });
      }

      setScanCode('');
    } catch (error: any) {
      console.error('Scanner error:', error?.message, 'Full error:', error);
      setScanMessage({
        type: 'error',
        text: getErrorMessage(error?.message)
      });
    } finally {
      setScanLoading(false);
    }
  }, [business?.id, scanCode, tx, locale, voucherStatus]);

  const handleRedeemValidatedVoucher = useCallback(async () => {
    if (!validatedShopVoucher) return;
    if (!business?.id) return alert(tx('businessNotFound'));

    const status = String(validatedShopVoucher.effective_status || validatedShopVoucher.status || '').toLowerCase();
    if (status !== 'active') {
      setScanMessage({ type: 'warning', text: tx('cannotRedeemInactive') });
      return;
    }

    setVoucherRedeemLoading(true);
    try {
      const response = await redeemMerchantVoucher(
        validatedShopVoucher.secure_token || validatedShopVoucher.voucher_code,
        business.id,
        {
          source: lastVoucherScanSource || 'manual',
          note: 'Validation depuis le BusinessDashboard Forfeo Explore',
        }
      );
      if (!response?.ok || !response.voucher) {
        throw new Error(response?.error || 'redeem_failed');
      }

      setValidatedShopVoucher(response.voucher);
      setShopVouchers(prev => [
        response.voucher as ShopVoucher,
        ...prev.filter((v: ShopVoucher) => v.id !== response.voucher?.id),
      ].slice(0, 10));
      setScanMessage({
        type: 'success',
        text: `✅ ${response.voucher.voucher_code} — ${tx('voucherRedeemedSuccess')}`,
      });
      fetchBusinessData();
    } catch (err: any) {
      setScanMessage({ type: 'error', text: `${tx('redeemError')} : ${err.message}` });
    } finally {
      setVoucherRedeemLoading(false);
    }
  }, [business?.id, lastVoucherScanSource, validatedShopVoucher, tx]);

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'profile' | 'room' | 'deal' | 'product' | 'post' | 'mystery' | 'boutique' | 'presale'
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;

    setUploading(true);
    try {
      const files = Array.from(e.target.files);
      const roomRemaining = Math.max(0, 10 - (newRoom.images?.length || 0));
      const filesToUpload = type === 'room' ? files.slice(0, roomRemaining) : [files[0]];

      for (const file of filesToUpload) {
        const compressed = await compressImage(file, type === 'profile' ? 400 : 1200, 0.85);
        const fileName = `${business.id}/${type}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '')}`;

        const { error } = await supabase.storage
          .from('business-media')
          .upload(fileName, compressed, { upsert: true });

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('business-media')
          .getPublicUrl(fileName);

        if (type === 'profile') {
          setFormData(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'room') {
          setNewRoom((prev: any) => ({ ...prev, images: [...prev.images, publicUrl] }));
        } else if (type === 'deal') {
          setNewDeal(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'product') {
          setNewProduct(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'post') {
          setNewPost(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'boutique') {
          setNewBoutiqueForfait(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'presale') {
          setNewPresale(prev => ({ ...prev, image_url: publicUrl }));
        } else if (type === 'mystery') {
          setNewMysteryOffer((prev: any) => ({
            ...prev,
            secret_image_url: publicUrl,
            image_url: publicUrl
          }));
          setMysteryPreviewMode('secret');
        }
      }
    } catch (err: any) {
      alert(`Erreur d'upload : ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const simulateAiGeneration = (
    field: string,
    type: 'hotel' | 'deal' | 'product' | 'post' | 'mystery' | 'boutique' | 'presale'
  ) => {
    setAiLoading(prev => ({ ...prev, [type]: true }));

    setTimeout(() => {
      const descriptions: any = {
        hotel: "Plongez dans le luxe de notre suite signature...",
        deal: "🔥 OFFRE EXPLOSIVE ! Profitez de -50%...",
        product: "Découvrez l'élégance artisanale...",
        post: "✨ Grande nouvelle !...",
        mystery: "🕵️‍♂️ OFFRE HÔTEL MYSTÈRE : Laissez-vous surprendre...",
        boutique: "Offrez-vous un moment de détente absolue avec ce forfait exclusif...",
        presale: "🌟 Accès VIP exclusif. Obtenez votre place avant tout le monde..."
      };

      if (type === 'hotel') setNewRoom((prev: any) => ({ ...prev, description: descriptions.hotel }));
      else if (type === 'deal') setNewDeal(prev => ({ ...prev, description: descriptions.deal }));
      else if (type === 'product') setNewProduct(prev => ({ ...prev, description: descriptions.product }));
      else if (type === 'post') setNewPost(prev => ({ ...prev, content: descriptions.post }));
      else if (type === 'boutique') setNewBoutiqueForfait(prev => ({ ...prev, description: descriptions.boutique }));
      else if (type === 'mystery') setNewMysteryOffer((prev: any) => ({ ...prev, description: descriptions.mystery }));
      else if (type === 'presale') setNewPresale(prev => ({ ...prev, description: descriptions.presale }));

      setAiLoading(prev => ({ ...prev, [type]: false }));
    }, 1500);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    let lat = formData.latitude ? parseFloat(formData.latitude) : null;
    let lng = formData.longitude ? parseFloat(formData.longitude) : null;

    if (formData.address && formData.city && (!lat || !lng)) {
      try {
        const query = `${formData.address}, ${formData.city}, ${formData.region || ''}, Canada`;
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`);
        const data = await response.json();

        if (data && data.length > 0) {
          lat = parseFloat(data[0].lat);
          lng = parseFloat(data[0].lon);
          setFormData(prev => ({ ...prev, latitude: data[0].lat, longitude: data[0].lon }));
        }
      } catch (err) {
        console.warn('Geocoding failed', err);
      }
    }

    const updates = { ...formData, latitude: lat, longitude: lng };
    const { error } = await supabase.from('businesses').update(updates).eq('id', business.id);

    if (!error) {
      alert('✅ Profil mis à jour (Coordonnées GPS synchronisées) !');
      setBusiness({ ...business, ...updates });
    } else {
      alert('Erreur lors de la sauvegarde.');
    }

    setSaving(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordState.new !== passwordState.confirm) return alert('Les mots de passe ne correspondent pas.');
    if (passwordState.new.length < 6) return alert('Le mot de passe doit faire au moins 6 caractères.');

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwordState.new });

    if (!error) {
      alert('Mot de passe mis à jour avec succès !');
      setPasswordState({ new: '', confirm: '' });
    } else {
      alert(`Erreur lors de la mise à jour : ${error.message}`);
    }

    setSaving(false);
  };

  const handleSaveHotelDetails = async () => {
    if (!business?.id) return;
    setHotelDetailsLoading(true);

    try {
      const updates = {
        hotel_amenities: hotelDetails.amenities,
        hotel_rules: hotelDetails.rules,
        hotel_faq: hotelDetails.faq,
      };

      const { error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', business.id);

      if (error) throw error;

      setBusiness((prev: any) => ({ ...prev, ...updates }));
      alert('✅ Équipements, Règles et FAQ sauvegardés !');
    } catch (e: any) {
      alert(`Erreur sauvegarde : ${e?.message || 'Erreur inconnue'}`);
    } finally {
      setHotelDetailsLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!window.confirm('Redirection vers Stripe...')) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: { business_id: business.id, return_url: window.location.origin }
      });

      if (error) throw error;
      if (data?.url) window.location.href = data.url;
      else throw new Error(data?.error || 'Erreur inconnue');
    } catch (error: any) {
      alert(`Erreur Stripe : ${error.message}`);
      setLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!newRoom.name || !newRoom.price_per_night) return alert('Infos requises.');

    const { data: createdRoom, error } = await supabase.from('accommodations').insert({
      business_id: business.id,
      name: newRoom.name,
      description: newRoom.description,
      price_per_night: parseFloat(newRoom.price_per_night),
      capacity_adults: newRoom.capacity_adults,
      is_available: true,
      images: newRoom.images,
      image_url: newRoom.images[0] || null
    }).select('id').single();

    if (!error && createdRoom?.id) {
      try {
        await syncForfeoReserveAccommodationInventory({
          businessId: business.id,
          accommodationId: createdRoom.id,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } catch (syncError) {
        console.warn('reserve_accommodation_autoprovision_retry_required', syncError);
      }
      alert('Chambre ajoutée !');
      setNewRoom({ name: '', description: '', price_per_night: '', capacity_adults: 2, images: [] });
      fetchBusinessData();
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.title || !newDeal.discount_price) {
      alert('Titre et prix promo requis');
      return;
    }

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + parseInt(newDeal.expires_in_hours.toString() || '24'));

    const slotStart = newDeal.slot_start_local ? new Date(newDeal.slot_start_local).toISOString() : null;
    const slotEnd = newDeal.slot_end_local ? new Date(newDeal.slot_end_local).toISOString() : null;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('last_call_deals')
        .insert({
          id: crypto.randomUUID(),
          public_id: crypto.randomUUID(),
          title: newDeal.title,
          discount_price: parseFloat(newDeal.discount_price),
          expires_at: expiresAt.toISOString(),
          business_id: business.id,
          description: newDeal.description || null,
          original_price: newDeal.original_price ? parseFloat(newDeal.original_price) : null,
          quantity_available: newDeal.quantity_available || 5,
          image_url: newDeal.image_url || formData.image_url || null,
          party_size: toNum(newDeal.party_size, 2),
          slot_start: slotStart,
          slot_end: slotEnd,
          category_hint: business?.category || null,
          region_hint: business?.region || null,
          discount_percent: newDeal.original_price
            ? Math.round(((parseFloat(newDeal.original_price) - parseFloat(newDeal.discount_price)) / parseFloat(newDeal.original_price)) * 100)
            : null,
          created_at: new Date().toISOString()
        });

      if (error) {
        console.error('❌ Erreur insertion:', error);
        alert(`Erreur: ${error.message}`);
        return;
      }

      alert('✅ Offre Flash publiée avec succès !');
      setNewDeal({
        title: '',
        description: '',
        original_price: '',
        discount_price: '',
        quantity_available: 5,
        expires_in_hours: 24,
        image_url: '',
        party_size: 2,
        slot_start_local: '',
        slot_end_local: ''
      });
      fetchBusinessData();
    } catch (error: any) {
      console.error('❌ Exception:', error);
      alert(`❌ Erreur: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) return alert('Infos requises.');

    const { error } = await supabase.from('shop_products').insert({
      business_id: business.id,
      ...newProduct,
      price: parseFloat(newProduct.price),
      is_active: true
    });

    if (!error) {
      alert('Produit ajouté !');
      fetchBusinessData();
    }
  };

  const handleCreateBoutiqueForfait = async () => {
    if (!newBoutiqueForfait.title || !newBoutiqueForfait.price || !newBoutiqueForfait.original_price) {
      return alert('Titre, prix régulier et prix réduit sont requis.');
    }

    setSaving(true);
    try {
      const original = parseFloat(newBoutiqueForfait.original_price);
      const price = parseFloat(newBoutiqueForfait.price);
      const discount = Math.round(((original - price) / original) * 100);

      const includesList = newBoutiqueForfait.includes.split('\n').filter(i => i.trim() !== '');
      const conditionsList = newBoutiqueForfait.conditions.split('\n').filter(i => i.trim() !== '');

      const payload = {
        title: newBoutiqueForfait.title,
        business_id: business.id,
        business_name: business.name,
        location: business.city || business.region,
        category: newBoutiqueForfait.category,
        original_price: original,
        price: price,
        discount: discount,
        description: newBoutiqueForfait.description,
        includes: includesList,
        conditions: conditionsList,
        expiration: newBoutiqueForfait.expiration,
        image: newBoutiqueForfait.image_url || business.image_url,
        is_active: true
      };

      const { error } = await supabase.from('boutique_forfaits').insert(payload);
      if (error) throw error;

      alert('✅ Forfait publié dans la boutique !');
      setNewBoutiqueForfait({
        title: '',
        description: '',
        original_price: '',
        price: '',
        category: 'Détente',
        includes: '',
        conditions: '',
        expiration: '',
        image_url: ''
      });
      fetchBusinessData();
    } catch (e: any) {
      alert(`Erreur: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePresaleOffer = async () => {
    if (!newPresale.title || !newPresale.price || !newPresale.secret_code) {
      return alert('Titre, prix et code secret sont requis.');
    }

    setSaving(true);
    try {
      const safeCode = newPresale.secret_code.toLowerCase().trim();

      await supabase
        .from('presale_codes')
        .upsert({ code: safeCode, is_active: true })
        .select()
        .maybeSingle();

      const payload = {
        business_id: business.id,
        title: newPresale.title,
        description: newPresale.description,
        price: parseFloat(newPresale.price),
        original_price: newPresale.original_price ? parseFloat(newPresale.original_price) : null,
        features: newPresale.features.split('\n').filter(f => f.trim()),
        quantity_available: newPresale.quantity,
        secret_code: safeCode,
        image_url: newPresale.image_url || business.image_url
      };

      const { error } = await supabase.from('presale_offers').insert(payload);
      if (error) throw error;

      alert('✅ Offre VIP Prévente créée et liée au code secret !');
      setNewPresale({
        title: '',
        description: '',
        price: '',
        original_price: '',
        features: '',
        quantity: 100,
        secret_code: '',
        image_url: ''
      });
      fetchBusinessData();
    } catch (e: any) {
      alert(`Erreur de création: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const fetchDropsData = async () => {
    if (!business?.id) return;

    try {
      const { data, error } = await supabase
        .from('forfeo_drops')
        .select('*')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      if (!error && data) setDrops(data);
    } catch (e) {
      console.log("La table forfeo_drops n'est pas encore créée.");
    }
  };

  useEffect(() => {
    if (activeTab === 'drops') fetchDropsData();

    if (activeTab === 'posts' && business?.id && notificationsUnread > 0) {
      supabase.from('business_notifications')
        .update({ is_read: true })
        .eq('business_id', business.id)
        .eq('is_read', false)
        .then(() => setNotificationsUnread(0));
    }
  }, [activeTab, business?.id, notificationsUnread]);

  const handleCreateDrop = async () => {
    if (!newDrop.title || !newDrop.reward) {
      return alert("Titre et récompense requis.");
    }

    let finalLat = business?.latitude ? Number(business.latitude) : null;
    let finalLng = business?.longitude ? Number(business.longitude) : null;

    const hasCustomAddress =
      !!newDrop.drop_address?.trim() &&
      !!newDrop.drop_city?.trim();

    setSaving(true);

    try {
      if (hasCustomAddress) {
        const fullAddress = [
          newDrop.drop_address?.trim(),
          newDrop.drop_city?.trim(),
          newDrop.drop_region?.trim(),
          newDrop.drop_postal_code?.trim(),
          newDrop.drop_country?.trim() || 'Canada'
        ]
          .filter(Boolean)
          .join(', ');

        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`
        );
        const geoData = await response.json();

        if (geoData && geoData.length > 0) {
          finalLat = parseFloat(geoData[0].lat);
          finalLng = parseFloat(geoData[0].lon);
        } else {
          return alert("Impossible de géolocaliser l’adresse du drop. Vérifiez l’adresse saisie.");
        }
      }

      if (!finalLat || !finalLng) {
        return alert("Aucune coordonnée GPS disponible. Sauvegardez d’abord l’adresse du commerce ou entrez une adresse précise pour le drop.");
      }

      const payload = {
        business_id: business.id,
        title: newDrop.title.trim(),
        description: newDrop.description?.trim() || null,
        reward: newDrop.reward.trim(),
        radius_meters: Number(newDrop.radius_meters) || 50,

        latitude: finalLat,
        longitude: finalLng,

        business_name: business.name || formData.name || null,
        business_address: business.address || formData.address || null,
        business_city: business.city || formData.city || null,
        business_postal_code: business.postal_code || formData.postal_code || null,
        business_region: business.region || formData.region || null,

        drop_address: newDrop.drop_address?.trim() || null,
        drop_city: newDrop.drop_city?.trim() || null,
        drop_postal_code: newDrop.drop_postal_code?.trim() || null,
        drop_region: newDrop.drop_region?.trim() || 'Québec',
        drop_country: newDrop.drop_country?.trim() || 'Canada',

        is_active: true
      };

      const { error } = await supabase
        .from('forfeo_drops')
        .insert(payload);

      if (error) throw error;

      alert("✅ Drop créé avec succès ! Il est visible sur le radar.");

      setNewDrop({
        title: '',
        description: '',
        reward: '',
        radius_meters: 50,
        drop_address: '',
        drop_city: '',
        drop_postal_code: '',
        drop_region: 'Québec',
        drop_country: 'Canada'
      });

      fetchDropsData();
    } catch (e: any) {
      console.error("Erreur création drop:", e);
      alert("Erreur technique : " + (e?.message || "Impossible de créer le drop."));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishPost = async () => {
    if (!newPost.content) return alert('Contenu requis.');

    setIsPosting(true);
    const { error } = await supabase
      .from('posts')
      .insert({
        business_id: business.id,
        content: newPost.content,
        image_url: newPost.image_url || null
      });

    if (!error) {
      alert('Publié !');
      setNewPost({ content: '', image_url: '' });
      fetchBusinessData();
    }

    setIsPosting(false);
  };

  const handleAddReward = async () => {
    if (!newReward.title) return alert('Titre requis');

    const { error } = await supabase.from('rewards').insert({
      business_id: business.id,
      title: newReward.title,
      description: newReward.description,
      cost_points: newReward.cost
    });

    if (!error) {
      alert('Récompense ajoutée !');
      setNewReward({ title: '', cost: 500, description: '' });
      fetchBusinessData();
    }
  };

  const handleValidateLoyalty = async () => {
    if (!loyaltyScan.id) return alert('ID Client requis.');

    setLoyaltyScan(prev => ({ ...prev, message: 'Validation...' }));
    try {
      const { error } = await supabase.rpc('add_points', {
        p_user_id: loyaltyScan.id.trim(),
        p_amount: loyaltyScan.points,
        p_action: 'PURCHASE',
        p_desc: `Achat chez ${business.name}`
      });

      if (error) throw error;

      setLoyaltyScan({ id: '', points: 10, message: '✅ Points envoyés !' });
    } catch (e: any) {
      setLoyaltyScan(prev => ({ ...prev, message: '❌ Erreur ID.' }));
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedRes?.id) return;
    if (!window.confirm('Annuler ?')) return;

    const { error } = await supabase
      .from('bookings')
      .update({ status: 'cancelled' })
      .eq('id', selectedRes.id);

    if (!error) {
      alert('Annulé.');
      setSelectedRes(null);
      fetchBusinessData();
    } else {
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleDelete = async (table: string, id: string) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (!error && table === 'accommodations' && business?.id) {
      try {
        await syncForfeoReserveAccommodationInventory({
          businessId: business.id,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } catch (syncError) {
        console.warn('reserve_accommodation_archive_retry_required', syncError);
      }
    }
    fetchBusinessData();
  };

  const mirrorMysteryToLastCallAndAccommodations = async (created: any) => {
    if (!created?.id) return;

    try {
      const expiresAt = created.expires_at || null;
      const qty = created.quantity_available ?? null;
      const mysteryPrice = created.mystery_price ?? created.price ?? null;
      const originalPrice = created.original_price ?? null;
      const publicImg = created.image_url || null;

      const lcAttempts: any[] = [
        {
          business_id: business.id,
          title: `🏨 Hôtel Mystère — ${created.title || 'Offre Mystère'}`,
          description: created.description || '',
          original_price: originalPrice,
          discount_price: mysteryPrice,
          quantity_available: qty ?? 1,
          expires_at: expiresAt,
          image_url: publicImg,
          category_hint: 'Hébergement',
          is_mystery: true,
          mystery_offer_id: created.id,
          reveal_after_purchase: true,
        },
        {
          business_id: business.id,
          title: `🏨 Hôtel Mystère — ${created.title || 'Offre Mystère'}`,
          description: created.description || '',
          original_price: originalPrice,
          discount_price: mysteryPrice,
          quantity_available: qty ?? 1,
          expires_at: expiresAt,
          image_url: publicImg,
        },
        {
          business_id: business.id,
          title: `🏨 Hôtel Mystère — ${created.title || 'Offre Mystère'}`,
          description: created.description || '',
          discount_price: mysteryPrice,
          expires_at: expiresAt,
          image_url: publicImg,
        }
      ];

      for (const p of lcAttempts) {
        const { error } = await supabase.from('last_call_deals').insert(p);
        if (!error) break;
      }
    } catch (e) {
      console.warn('Mirror failed', e);
    }
  };

  const handleCreateMysteryOffer = async () => {
    if (!newMysteryOffer.title || !newMysteryOffer.mystery_price) {
      return alert('Titre + Prix Mystère requis.');
    }

    const expiresAt = new Date();
    expiresAt.setHours(
      expiresAt.getHours() + parseInt(newMysteryOffer.expires_in_hours.toString() || '72')
    );

    let publicImg = newMysteryOffer.image_url || null;
    if (
      newMysteryOffer.blur_enabled &&
      newMysteryOffer.secret_image_url &&
      publicImg === newMysteryOffer.secret_image_url
    ) {
      const ok = window.confirm('⚠️ Image non floutée. Continuer avec placeholder ?');
      if (!ok) return;
      publicImg = 'https://placehold.co/800x500?text=Hotel+Mystere';
    }
    if (!publicImg) publicImg = 'https://placehold.co/800x500?text=Hotel+Mystere';

    const payload = {
      business_id: business.id,
      title: newMysteryOffer.title,
      description: newMysteryOffer.description,
      original_price: newMysteryOffer.original_price
        ? parseFloat(newMysteryOffer.original_price)
        : null,
      mystery_price: parseFloat(newMysteryOffer.mystery_price),
      quantity_available: toNum(newMysteryOffer.quantity_available, 1),
      expires_at: expiresAt.toISOString(),
      image_url: publicImg,
      secret_image_url: newMysteryOffer.secret_image_url || null,
      blur_radius: toNum(newMysteryOffer.blur_radius, 16),
      reveal_after_purchase: true
    };

    const { data, error } = await supabase
      .from('mystery_hotel_offers')
      .insert(payload)
      .select('*')
      .maybeSingle();

    if (!error) {
      alert('✅ Offre Hôtel Mystère créée !');
      await mirrorMysteryToLastCallAndAccommodations(data || payload);
      setNewMysteryOffer({
        title: '',
        description: '',
        original_price: '',
        mystery_price: '',
        quantity_available: 5,
        expires_in_hours: 72,
        image_url: '',
        secret_image_url: '',
        blur_enabled: true,
        blur_radius: 16,
        reveal_after_purchase: true
      });
      setMysteryPreviewMode('public');
      fetchBusinessData();
    } else {
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleAiSmartSync = async () => {
    if (!window.confirm("Lancer l'analyse IA ?")) return;

    setAiLoading(prev => ({ ...prev, sync: true }));
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      alert('✅ Synchronisation IA terminée.');
      fetchBusinessData();
    } catch (e: any) {
      alert(`Erreur: ${e.message}`);
    } finally {
      setAiLoading(prev => ({ ...prev, sync: false }));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // ======================
  // COMPOSANT FORFEO SIGN
  // ======================
  const ForfeoSignSection = React.memo(({ business }: { business: any }) => {
    const [activeSignTab, setActiveSignTab] = useState<'create' | 'sent' | 'received'>('create');
    const [contractsList, setContractsList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const [contractForm, setContractForm] = useState({
      template: 'service',
      recipientEmail: '',
      recipientName: '',
      recipientCompany: '',
      subject: '',
      content: '',
      aiPrompt: '',
      expiryDays: 7,
      requireSignature: true,
      generateWithAI: false
    });

    const [signingContract, setSigningContract] = useState<any>(null);
    const [signatureData, setSignatureData] = useState('');
    const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawingSignatureRef = useRef(false);

    const configureSignatureCanvas = useCallback(() => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const width = Math.max(320, Math.round(rect.width || 520));
      const height = Math.max(128, Math.round(rect.height || 160));
      const dpr = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = '100%';
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = 'rgba(15, 23, 42, 0.12)';
      ctx.shadowBlur = 0.6;
      setSignatureData('');
    }, []);

    const getSignaturePoint = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }, []);

    const commitSignaturePreview = useCallback(() => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;
      setSignatureData(canvas.toDataURL('image/png'));
    }, []);

    const handleSignaturePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const canvas = signatureCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      canvas.setPointerCapture?.(e.pointerId);
      isDrawingSignatureRef.current = true;

      const { x, y } = getSignaturePoint(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 0.1, y + 0.1);
      ctx.stroke();
      commitSignaturePreview();
    }, [commitSignaturePreview, getSignaturePoint]);

    const handleSignaturePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      if (!isDrawingSignatureRef.current) return;

      const canvas = signatureCanvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const { x, y } = getSignaturePoint(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      commitSignaturePreview();
    }, [commitSignaturePreview, getSignaturePoint]);

    const handleSignaturePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingSignatureRef.current) return;

      const canvas = signatureCanvasRef.current;
      if (canvas) {
        try {
          canvas.releasePointerCapture?.(e.pointerId);
        } catch {
          // Le navigateur peut déjà avoir relâché le pointer capture.
        }
      }

      isDrawingSignatureRef.current = false;
      commitSignaturePreview();
    }, [commitSignaturePreview]);

    useEffect(() => {
      if (!signingContract) return;
      const raf = window.requestAnimationFrame(() => configureSignatureCanvas());
      return () => window.cancelAnimationFrame(raf);
    }, [configureSignatureCanvas, signingContract]);

    useEffect(() => {
      fetchContracts();
    }, [business?.id]);

    const fetchContracts = async () => {
      if (!business?.id) return;

      const { data, error } = await supabase
        .from('forfeo_sign_contracts')
        .select('*')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setContractsList(data);
      }
    };

    const generateContractWithAI = async () => {
      if (!contractForm.aiPrompt?.trim()) {
        alert('Veuillez décrire le contrat souhaité');
        return;
      }

      setAiLoading(true);

      try {
        const { data, error } = await supabase.functions.invoke('generate-contract', {
          body: {
            prompt: contractForm.aiPrompt,
            template: contractForm.template,
            businessName: business?.name || 'Forfeo Explore',
            recipientName: contractForm.recipientName || '',
            recipientCompany: contractForm.recipientCompany || '',
          }
        });

        if (error) throw error;
        if (!data?.success) throw new Error(data?.error || 'Erreur de génération');

        setContractForm((prev: any) => ({
          ...prev,
          subject: data.subject || prev.subject,
          content: data.content || prev.content,
        }));
      } catch (err: any) {
        console.error('generateContractWithAI error:', err);
        alert(`Erreur IA: ${err?.message || "Assurez-vous que l'Edge Function est déployée."}`);
      } finally {
        setAiLoading(false);
      }
    };

    const getContractSignUrl = (contractId: string) => {
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://forfeoexplore.com';

      return `${origin}/sign/${contractId}`;
    };

    const escapeHtml = (value: any) =>
      String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');

    const formatContractDate = (value?: string | null) => {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleString(locale);
      } catch {
        return value;
      }
    };

    const getContractPdfHtml = (contract: any) => {
      const businessName = business?.name || contract?.business_name || 'Forfeo';
      const signUrl = contract?.id ? getContractSignUrl(contract.id) : '';
      const statusLabel = getStatusLabel(contract?.status || 'pending');
      const signatureBlock = contract?.signature_data
        ? `<div style="margin-top:18px;border:1px solid #dbe3ef;border-radius:14px;padding:14px;background:#fff;">
             <div style="font-size:12px;color:#64748b;font-weight:600;margin-bottom:8px;">Signature enregistrée</div>
             <img src="${contract.signature_data}" style="max-width:260px;max-height:120px;object-fit:contain;border-bottom:1px solid #cbd5e1;" />
             <div style="font-size:12px;color:#475569;margin-top:10px;">
               Signé par ${escapeHtml(contract?.signed_by || contract?.recipient_name || contract?.recipient_email || 'Signataire')}
               ${contract?.signed_at ? ` le ${escapeHtml(formatContractDate(contract.signed_at))}` : ''}
             </div>
           </div>`
        : `<div style="margin-top:18px;border:1px dashed #cbd5e1;border-radius:14px;padding:18px;color:#64748b;background:#f8fafc;">
             Signature en attente.
           </div>`;

      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(contract?.subject || 'Contrat Forfeo Sign')}</title>
  <style>
    @page { size: Letter; margin: 18mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #101828; background: #ffffff; }
    .page { width: 100%; max-width: 820px; margin: 0 auto; }
    .header { padding: 22px 24px; border-radius: 18px; background: #f2f6ff; border: 1px solid #dde7fb; color: #101828; margin-bottom: 24px; }
    .brand { font-size: 12px; font-weight: 700; color: #2c6bf6; }
    h1 { font-size: 25px; line-height: 1.25; margin: 8px 0 0; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 18px 0 24px; }
    .box { border: 1px solid #e6e9f0; border-radius: 14px; padding: 14px; background: #fafbfd; }
    .label { font-size: 12px; color: #667085; font-weight: 600; margin-bottom: 6px; }
    .value { font-size: 13px; font-weight: 600; color: #101828; }
    .content { white-space: pre-wrap; font-family: "Courier New", monospace; font-size: 12px; line-height: 1.65; border: 1px solid #e6e9f0; border-radius: 16px; padding: 22px; background: #ffffff; }
    .footer { margin-top: 22px; color: #667085; font-size: 11px; line-height: 1.5; border-top: 1px solid #e6e9f0; padding-top: 14px; }
    .no-print { margin: 0 auto 18px; max-width: 820px; display: flex; gap: 10px; justify-content: flex-end; }
    .no-print button { border: 0; border-radius: 10px; padding: 12px 16px; font-weight: 600; cursor: pointer; }
    .primary { background: #2c6bf6; color: white; }
    .secondary { background: #eef1f6; color: #101828; }
    @media print { .no-print { display: none !important; } body { background: white; } }
  </style>
</head>
<body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Fermer</button>
    <button class="primary" onclick="window.print()">Télécharger / enregistrer en PDF</button>
  </div>
  <main class="page">
    <section class="header">
      <div class="brand">Forfeo Sign</div>
      <h1>${escapeHtml(contract?.subject || 'Contrat')}</h1>
    </section>

    <section class="meta">
      <div class="box">
        <div class="label">Émetteur</div>
        <div class="value">${escapeHtml(businessName)}</div>
      </div>
      <div class="box">
        <div class="label">Destinataire</div>
        <div class="value">${escapeHtml(contract?.recipient_name || contract?.recipient_email || '—')}${contract?.recipient_company ? ` — ${escapeHtml(contract.recipient_company)}` : ''}</div>
      </div>
      <div class="box">
        <div class="label">Statut</div>
        <div class="value">${escapeHtml(statusLabel)}</div>
      </div>
      <div class="box">
        <div class="label">Expire le</div>
        <div class="value">${escapeHtml(formatContractDate(contract?.expiry_date))}</div>
      </div>
    </section>

    <section class="content">${escapeHtml(contract?.content || 'Aucun contenu')}</section>

    ${signatureBlock}

    <section class="footer">
      Document généré depuis Forfeo Sign. Lien public du contrat : ${escapeHtml(signUrl)}
    </section>
  </main>
  <script>
    setTimeout(function(){ window.focus(); }, 200);
  </script>
</body>
</html>`;
    };

    const openContractPdf = (contract: any) => {
      const html = getContractPdfHtml(contract);
      const printWindow = window.open('', '_blank', 'width=950,height=900');

      if (!printWindow) {
        alert("Le navigateur a bloqué l'ouverture du PDF. Autorisez les fenêtres contextuelles pour Forfeo Explore.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (e) {
          console.warn('Impression PDF non déclenchée automatiquement:', e);
        }
      }, 600);
    };

    const sendContractEmailForContract = async (contract: any, signUrl?: string) => {
      const finalSignUrl = signUrl || getContractSignUrl(contract.id);

      const { data: emailData, error: emailError } = await supabase.functions.invoke(
        'forfeo-sign-send-contract-email',
        {
          body: {
            contractId: contract.id,
            recipientEmail: contract.recipient_email || contractForm.recipientEmail.trim(),
            recipientName: contract.recipient_name || contractForm.recipientName.trim(),
            businessName: business?.name,
            subject: contract.subject || contractForm.subject,
            signUrl: finalSignUrl
          }
        }
      );

      if (emailError || emailData?.success === false) {
        return {
          success: false,
          error:
            emailData?.error ||
            emailError?.message ||
            "L'Edge Function forfeo-sign-send-contract-email n'a pas retourné de succès."
        };
      }

      return { success: true, data: emailData };
    };

    const handleResendContractEmail = async (contract: any) => {
      if (!contract?.id) return;

      const signUrl = getContractSignUrl(contract.id);
      setLoading(true);

      try {
        const result = await sendContractEmailForContract(contract, signUrl);

        if (!result.success) {
          await navigator.clipboard?.writeText(signUrl).catch(() => undefined);
          alert(`Le courriel n'a pas été envoyé. Le lien a été copié : ${signUrl}\n\nErreur : ${result.error}`);
          return;
        }

        alert('✅ Courriel renvoyé avec succès.');
      } catch (e: any) {
        await navigator.clipboard?.writeText(signUrl).catch(() => undefined);
        alert(`Erreur lors du renvoi. Le lien a été copié : ${signUrl}\n\n${e?.message || 'Erreur inconnue'}`);
      } finally {
        setLoading(false);
      }
    };

    const cancelContract = async (
      contractId: string,
    ) => {
      const { data, error } =
        await supabase.functions.invoke(
          'manage-sign-contract',
          {
            body: {
              action: 'cancel',
              contractId,
            },
          },
        );

      if (
        error ||
        data?.success === false
      ) {
        throw new Error(
          data?.error ||
          error?.message ||
          "Annulation impossible"
        );
      }

      return data;
    };

    const handleCreateContract = async () => {
      if (!contractForm.recipientEmail || !contractForm.content) {
        alert('Email du destinataire et contenu requis');
        return;
      }

      setLoading(true);
      try {
        const {
          data: { session }
        } = await supabase.auth.getSession();

        const { data: contract, error } = await supabase
          .from('forfeo_sign_contracts')
          .insert({
            business_id: business.id,
            template_type: contractForm.template,
            recipient_email: contractForm.recipientEmail.trim(),
            recipient_name: contractForm.recipientName.trim(),
            recipient_company: contractForm.recipientCompany.trim(),
            subject: contractForm.subject || `Contrat - ${business?.name}`,
            content: contractForm.content,
            status: 'pending',
            expiry_date: new Date(
              Date.now() + contractForm.expiryDays * 24 * 60 * 60 * 1000
            ).toISOString(),
            require_signature: contractForm.requireSignature,
            created_by: session?.user?.id
          })
          .select()
          .single();

        if (error) throw error;

        const signUrl = getContractSignUrl(contract.id);
        const emailResult = await sendContractEmailForContract(contract, signUrl);

        if (!emailResult.success) {
          console.warn('Email non envoyé, mais contrat créé:', emailResult.error);
          await navigator.clipboard?.writeText(signUrl).catch(() => undefined);
          alert(
            `Contrat créé, mais l'envoi de l'email a échoué. Le lien direct a été copié lorsque possible : ${signUrl}`
          );
        } else {
          alert('✅ Contrat créé, archivé dans le dashboard et envoyé par email !');
        }

        setContractForm({
          template: 'service',
          recipientEmail: '',
          recipientName: '',
          recipientCompany: '',
          subject: '',
          content: '',
          aiPrompt: '',
          expiryDays: 7,
          requireSignature: true,
          generateWithAI: false
        });

        fetchContracts();
        setActiveSignTab('sent');
      } catch (error: any) {
        alert(`Erreur: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const handleSignContract = async (contractId: string) => {
      if (!signatureData) {
        alert('Veuillez dessiner votre signature');
        return;
      }

      try {
        const {
          data: { session }
        } = await supabase.auth.getSession();

        const { data, error } = await supabase.functions.invoke('sign-contract', {
          body: {
            contractId,
            signatureData,
            signedBy: session?.user?.email || signingContract?.recipient_email || 'Signataire'
          }
        });

        if (error || data?.success === false) {
          throw new Error(data?.error || error?.message || 'Signature impossible');
        }

        alert('✅ Contrat signé avec succès !');
        setSigningContract(null);
        setSignatureData('');
        fetchContracts();
      } catch (error: any) {
        alert(`Erreur signature: ${error.message}`);
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'signed':
          return 'fx-badge-green';
        case 'pending':
          return 'fx-badge-amber';
        case 'expired':
          return 'fx-badge-red';
        case 'cancelled':
          return 'fx-badge-slate';
        default:
          return 'fx-badge-slate';
      }
    };

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'signed':
          return 'Signé';
        case 'pending':
          return 'En attente';
        case 'expired':
          return 'Expiré';
        case 'cancelled':
          return 'Annulé';
        default:
          return status;
      }
    };

    return (
      <div className="space-y-6">
        <section className="fx-panel-hero">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="fx-eyebrow">Forfeo Sign</p>
              <h2 className="fx-panel-title">Contrats et signature électronique</h2>
              <p className="fx-panel-sub">
                Rédigez un contrat, envoyez-le par courriel et suivez la signature.
              </p>
            </div>
            <div className="fx-kpi">
              <span className="fx-kpi-value">{contractsList.filter(c => c.status === 'pending').length}</span>
              <span className="fx-kpi-label">en attente de signature</span>
            </div>
          </div>
        </section>

        <div className="fx-tabs">
          {[
            { id: 'create', label: 'Nouveau contrat', icon: <FaPlus /> },
            { id: 'sent', label: 'Envoyés', icon: <FaPaperPlane /> },
            { id: 'received', label: 'À signer', icon: <FaSignature /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSignTab(tab.id as any)}
              className={`fx-tab ${activeSignTab === tab.id ? 'is-active' : ''}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {activeSignTab === 'create' && (
          <div className="fx-card p-6 sm:p-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="space-y-6">
                <h3 className="fx-section-title"><FaFileContract /> Configuration</h3>

                <div>
                  <label className="label">Type de contrat</label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {CONTRACT_TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => setContractForm({ ...contractForm, template: template.id })}
                        className={`fx-choice ${contractForm.template === template.id ? 'is-active' : ''}`}
                      >
                        <span className="fx-choice-icon">{template.icon}</span>
                        <span className="fx-choice-label">{template.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">Courriel du destinataire *</label>
                    <input
                      type="email"
                      value={contractForm.recipientEmail}
                      onChange={e =>
                        setContractForm({ ...contractForm, recipientEmail: e.target.value })
                      }
                      className="input-field"
                      placeholder="client@entreprise.com"
                    />
                  </div>
                  <div>
                    <label className="label">Nom du destinataire</label>
                    <input
                      type="text"
                      value={contractForm.recipientName}
                      onChange={e =>
                        setContractForm({ ...contractForm, recipientName: e.target.value })
                      }
                      className="input-field"
                      placeholder="Jean Dupont"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Entreprise destinataire</label>
                  <input
                    type="text"
                    value={contractForm.recipientCompany}
                    onChange={e =>
                      setContractForm({ ...contractForm, recipientCompany: e.target.value })
                    }
                    className="input-field"
                    placeholder="Nom de l'entreprise"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">Expire dans (jours)</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={contractForm.expiryDays}
                      onChange={e =>
                        setContractForm({
                          ...contractForm,
                          expiryDays: parseInt(e.target.value) || 7
                        })
                      }
                      className="input-field text-center"
                    />
                  </div>
                  <label htmlFor="requireSignature" className="fx-check-row">
                    <input
                      type="checkbox"
                      id="requireSignature"
                      checked={contractForm.requireSignature}
                      onChange={e =>
                        setContractForm({
                          ...contractForm,
                          requireSignature: e.target.checked
                        })
                      }
                    />
                    <span>Demander une signature</span>
                  </label>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="fx-section-title"><FaRobot /> Contenu du contrat</h3>

                <div className="fx-ai-box">
                  <label className="label">Rédiger avec l'IA</label>
                  <textarea
                    value={contractForm.aiPrompt}
                    onChange={e => setContractForm({ ...contractForm, aiPrompt: e.target.value })}
                    placeholder="Décrivez le contrat souhaité (ex : prestation de services sur 3 mois…)"
                    className="input-field h-24 resize-none"
                  />
                  <button
                    onClick={generateContractWithAI}
                    disabled={aiLoading || !contractForm.aiPrompt.trim()}
                    className="fx-btn fx-btn-primary mt-3 w-full"
                  >
                    {aiLoading ? <FaSync className="animate-spin" /> : <FaMagic />}
                    {aiLoading ? 'Rédaction…' : 'Rédiger le contrat'}
                  </button>
                </div>

                <div>
                  <label className="label">Objet du contrat</label>
                  <input
                    type="text"
                    value={contractForm.subject}
                    onChange={e => setContractForm({ ...contractForm, subject: e.target.value })}
                    className="input-field"
                    placeholder="Objet du contrat"
                  />
                </div>

                <div>
                  <label className="label">Contenu *</label>
                  <textarea
                    value={contractForm.content}
                    onChange={e => setContractForm({ ...contractForm, content: e.target.value })}
                    className="input-field h-64 resize-none"
                    placeholder="Le contenu du contrat apparaîtra ici…"
                  />
                </div>

                <button
                  onClick={handleCreateContract}
                  disabled={loading || !contractForm.recipientEmail || !contractForm.content}
                  className="fx-btn fx-btn-dark w-full"
                >
                  {loading ? <FaSync className="animate-spin" /> : <FaPaperPlane />}
                  {loading ? 'Envoi…' : 'Envoyer le contrat'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSignTab === 'sent' && (
          <div className="fx-card p-6 sm:p-8">
            <h3 className="fx-section-title mb-6"><FaHistory /> Contrats envoyés</h3>

            {contractsList.length === 0 ? (
              <div className="fx-empty">
                <FaFileSignature size={28} />
                <p>Aucun contrat envoyé pour l'instant.</p>
                <button onClick={() => setActiveSignTab('create')} className="fx-link">
                  Créer un premier contrat
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {contractsList.map(contract => (
                  <div key={contract.id} className="fx-row">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-3">
                          <h4 className="fx-row-title">{contract.subject}</h4>
                          <span className={`fx-badge ${getStatusColor(contract.status)}`}>
                            {getStatusLabel(contract.status)}
                          </span>
                        </div>
                        <p className="fx-row-sub">
                          Pour <strong>{contract.recipient_name || contract.recipient_email}</strong>
                          {contract.recipient_company && ` • ${contract.recipient_company}`}
                        </p>
                        <div className="fx-row-meta">
                          <span><FaCalendarAlt /> {new Date(contract.created_at).toLocaleDateString(locale)}</span>
                          <span><FaClock /> Expire le {new Date(contract.expiry_date).toLocaleDateString(locale)}</span>
                          <span>
                            <FaFileContract />{' '}
                            {CONTRACT_TEMPLATES.find(t => t.id === contract.template_type)?.label || contract.template_type}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button onClick={() => openContractPdf(contract)} className="fx-icon-btn" title="Télécharger le PDF">
                          <FaDownload />
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(getContractSignUrl(contract.id))}
                          className="fx-icon-btn"
                          title="Copier le lien"
                        >
                          <FaCopy />
                        </button>
                        <button
                          onClick={() => window.open(getContractSignUrl(contract.id), '_blank')}
                          className="fx-icon-btn"
                          title="Voir le contrat"
                        >
                          <FaEye />
                        </button>
                        {contract.status === 'pending' && (
                          <button
                            onClick={() => handleResendContractEmail(contract)}
                            className="fx-icon-btn"
                            title="Renvoyer par courriel"
                          >
                            <FaPaperPlane />
                          </button>
                        )}
                        {contract.status === 'pending' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Annuler ce contrat ?')) return;
                              try {
                                await cancelContract(
                                  contract.id
                                );

                                await fetchContracts();
                              } catch (error: any) {
                                alert(
                                  "Erreur annulation: " +
                                  (
                                    error?.message ||
                                    "Erreur inconnue"
                                  )
                                );
                              }
                            }}
                            className="fx-icon-btn fx-icon-btn-danger"
                            title="Annuler"
                          >
                            <FaBan />
                          </button>
                        )}
                      </div>
                    </div>

                    {contract.signed_at && (
                      <div className="fx-note fx-note-success mt-4">
                        <FaCheckCircle />
                        <p className="fx-note-text">
                          Signé le {new Date(contract.signed_at).toLocaleString()} par {contract.signed_by}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSignTab === 'received' && (
          <div className="fx-card p-6 sm:p-8">
            <h3 className="fx-section-title mb-6"><FaSignature /> Contrats à signer</h3>
            <div className="fx-empty">
              <FaEnvelope size={28} />
              <p>Les contrats qui vous sont envoyés apparaîtront ici.</p>
              <p className="fx-empty-sub">Vous recevrez un courriel dès qu'un contrat arrive.</p>
            </div>
          </div>
        )}

        {signingContract && (
          <div className="fx-modal-backdrop">
            <div className="fx-modal max-w-lg p-8">
              <h3 className="fx-section-title mb-2"><FaPenFancy /> Signer le contrat</h3>
              <p className="fx-row-sub mb-6">{signingContract.subject}</p>

              <div className="fx-signature-box">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="label mb-0">Dessinez votre signature</p>
                  <button type="button" onClick={configureSignatureCanvas} className="fx-link">
                    Effacer
                  </button>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-2">
                  <canvas
                    ref={signatureCanvasRef}
                    className="block w-full cursor-crosshair select-none rounded-lg bg-white touch-none"
                    style={{ touchAction: 'none' }}
                    onPointerDown={handleSignaturePointerDown}
                    onPointerMove={handleSignaturePointerMove}
                    onPointerUp={handleSignaturePointerUp}
                    onPointerLeave={handleSignaturePointerUp}
                    onPointerCancel={handleSignaturePointerUp}
                  />
                </div>
                <p className="fx-help mt-2 text-center">
                  Votre signature s'affiche ici. Vous pouvez l'effacer avant de confirmer.
                </p>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setSigningContract(null);
                    setSignatureData('');
                  }}
                  className="fx-btn fx-btn-outline flex-1"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleSignContract(signingContract.id)}
                  disabled={!signatureData}
                  className="fx-btn fx-btn-primary flex-1"
                >
                  Confirmer la signature
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  });

  if (loading) {
    return (
      <div className="fx-loading">
        <div className="fx-spinner" />
      </div>
    );
  }

  if (!business) return null;

  return (
    <div className="business-pixel-shell-v4r2 fx-shell commerce-v2-shell flex min-h-screen flex-col font-sans" data-commerce-shell="v218" data-commerce-visual-final="v219" data-commerce-v220="root">
      <ToastContainer />

      {/* COMMERCE_V2_SIDEBAR_START */}
      <aside className="commerce-v2-sidebar" aria-label="Navigation espace Commerce">
        <div className="commerce-v2-brand">
          <span className="commerce-v2-brand-mark"><FaStore /></span>
          <span className="commerce-v2-brand-copy">
            <strong>FORFEO <em>COMMERCE</em></strong>
            <small>ESPACE PARTENAIRE</small>
          </span>
        </div>

        <div className="commerce-v2-business-chip">
          <span className="commerce-v2-business-avatar">
            {formData.image_url ? <img src={formData.image_url} alt="" /> : <FaStore />}
          </span>
          <span className="min-w-0">
            <strong>{formData.name || 'Mon commerce'}</strong>
            <small>{isPremium ? 'Partenaire Premium' : 'Partenaire Forfeo'}</small>
          </span>
        </div>

        <nav className="commerce-v2-nav">
          {commerceV2VisibleNav.map(item => {
            const badge = commerceV2NavBadge(item.id);

            return (
              <button
                key={item.id}
                type="button"
                className={`commerce-v2-nav-item ${commerceV2ActiveNav === item.id ? 'is-active' : ''} ${item.id === 'settingsHub' ? 'commerce-v2-nav-item--settings' : ''}`}
                onClick={() => setActiveTab(item.id as any)}
              >
                <span className="commerce-v2-nav-icon">{item.icon}</span>
                <span className="commerce-v2-nav-label">{item.label}</span>
                {badge > 0 && <span className="commerce-v2-nav-badge">{badge}</span>}
              </button>
            );
          })}
        </nav>

        <div className="commerce-v2-sidebar-footer">
          <div className="commerce-v2-footer-row">
            <button type="button" onClick={() => navigate(-1)}><FaArrowRight className="rotate-180" /> Retour</button>
            <button type="button" onClick={() => navigate('/')}><FaHome /> Accueil</button>
          </div>
          <button type="button" onClick={handleLogout} className="commerce-v2-logout"><FaSignOutAlt /> Déconnexion</button>
        </div>
      </aside>

      <button
        type="button"
        className="commerce-v2-forfy-launcher"
        onClick={() => openForfyCommerce()}
        aria-label="Ouvrir Forfy IA"
      >
        <FaRobot /> Forfy IA
      </button>
      {/* COMMERCE_V2_SIDEBAR_END */}

      {selectedRes && (
        <div className="fx-modal-backdrop">
          <div className="fx-modal max-w-md overflow-hidden">
            <button onClick={() => setSelectedRes(null)} className="fx-modal-close" aria-label="Fermer">
              <FaTimes />
            </button>

            <div className="fx-modal-head">
              <h3 className="fx-modal-title">Détails de la réservation</h3>
              <p className="fx-modal-sub">Référence {shortId(selectedRes.id)}</p>
            </div>

            <div className="space-y-4 p-6">
              <p className="fx-row-title">{selectedRes.guest_name}</p>
              <div className="fx-kv">
                <span>Arrivée</span>
                <strong>{new Date(selectedRes.start_at).toLocaleDateString(locale)}</strong>
              </div>
              <div className="fx-kv">
                <span>Départ</span>
                <strong>{new Date(selectedRes.end_at).toLocaleDateString(locale)}</strong>
              </div>
              <div className="fx-kv fx-kv-total">
                <span>Total</span>
                <strong>{toNum(selectedRes.total_price).toFixed(2)} $</strong>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setSelectedRes(null);
                    setActiveTab('messages');
                  }}
                  className="fx-btn fx-btn-primary flex-1"
                >
                  Écrire au client
                </button>
                <button onClick={handleCancelReservation} className="fx-btn fx-btn-danger-soft flex-1">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="business-pixel-content-v4r2 commerce-v2-content mx-auto flex w-full min-w-0 max-w-[1500px] flex-col gap-5 px-4 py-6 sm:gap-7 sm:px-6 sm:py-10">
        {activeTab !== 'overview' && (
          <div className="fx-backbar">
            <button onClick={() => setActiveTab('overview')} className="fx-btn fx-btn-outline">
              <FaArrowRight className="rotate-180" size={13} /> Retour au menu
            </button>
          </div>
        )}

        <main className="commerce-v214-workspace w-full" data-commerce-v214-global-visual-lock data-commerce-active-tab={activeTab}>
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <section className="fx-hero">
                <div className="fx-hero-main">
                  <button
                    type="button"
                    className="fx-hero-logo"
                    onClick={() => profileFileInputRef.current?.click()}
                    aria-label="Changer le logo"
                  >
                    {formData.image_url ? (
                      <img src={formData.image_url} alt="" />
                    ) : (
                      <FaCamera />
                    )}
                    <span className="fx-hero-logo-overlay">Modifier</span>
                  </button>

                  <input
                    type="file"
                    ref={profileFileInputRef}
                    onChange={e => handleUpload(e, 'profile')}
                    className="hidden"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <p className="fx-eyebrow">Espace partenaire</p>
                      {isPremium && (
                        <span className="fx-badge fx-badge-gold"><FaCrown size={10} /> {tx('premiumPartner')}</span>
                      )}
                    </div>
                    <h1 className="fx-hero-title">
                      {formData.name || tx('businessDashboardTitleFallback')}
                    </h1>
                    <p className="fx-hero-sub">{tx('businessDashboardSubtitle')}</p>
                  </div>
                </div>

                <div className="fx-hero-actions">
                  <RoleSelector currentRole={userRole} onRoleChange={setUserRole} />
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/place/${business.id}`} target="_blank" className="fx-btn fx-btn-outline">
                      <FaExternalLinkAlt size={12} /> {tx('viewMyListing')}
                    </Link>
                    <button onClick={handleLogout} className="fx-btn fx-btn-quiet-danger">
                      <FaSignOutAlt size={12} /> {tx('signOut')}
                    </button>
                  </div>
                </div>
              </section>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="grid grid-cols-2 gap-4 lg:col-span-2 xl:grid-cols-4">
                  <StatCard
                    title={tx('views')}
                    value={isPremium ? stats.views : <FaLock size={16} />}
                    icon={FaEye}
                    color="blue"
                  />
                  <StatCard
                    title={tx('revenue')}
                    value={`${stats.revenue.toFixed(2)} $`}
                    icon={FaCoins}
                    color="green"
                  />
                  <button
                    type="button"
                    onClick={() => setActiveTab('orders')}
                    className="fx-stat-link h-full text-left"
                  >
                    <StatCard
                      title={tx('orders')}
                      value={orders.filter((o: any) => o.status === 'paid').length}
                      icon={FaShoppingBag}
                      color="purple"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('scanner')}
                    className="fx-stat-link h-full text-left"
                  >
                    <StatCard title={tx('validation')} value="Scan" icon={FaQrcode} color="indigo" />
                  </button>
                </div>

                <div className="lg:col-span-1">
                  <SmartScannerWidget
                    business={business}
                    onValidate={handleValidateVoucher}
                    scanLoading={scanLoading}
                    scanMessage={scanMessage}
                    quickMode={true}
                  />
                </div>
              </div>

              <section className="commerce-v2-ux1-today" data-commerce-ux1-today="true">
                <div className="commerce-v2-ux1-today__head">
                  <div>
                    <p className="fx-eyebrow">Aujourd’hui</p>
                    <h2>Votre commerce en un coup d’œil</h2>
                    <span>{new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  </div>
                  <button type="button" className="commerce-v2-ux1-forfy-chip" onClick={() => openForfyCommerce()}>
                    <FaRobot /> Forfy IA
                  </button>
                </div>

                <div className="commerce-v2-ux1-stats" data-commerce-ux1-real-stats="true">
                  {merchantTodayStats.map(item => (
                    <article key={item.label} className="commerce-v2-ux1-stat">
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                      <small>{item.helper}</small>
                    </article>
                  ))}
                </div>

                <div className="commerce-v2-ux1-forfy-card" data-commerce-ux1-next-action="true">
                  <div>
                    <p className="fx-eyebrow"><FaRobot /> Suggestion Forfy IA</p>
                    <h3>{merchantTodoItems[0]?.label || 'Préparer une offre plus vendable'}</h3>
                    <span>Forfy prépare. Vous décidez. Rien n’est publié sans votre validation.</span>
                  </div>
                  <div className="commerce-v2-ux1-forfy-card__actions">
                    <button type="button" className="fx-btn fx-btn-primary" onClick={() => setActiveTab((merchantTodoItems[0]?.tab || 'offersHub') as any)}>
                      Continuer
                    </button>
                    <button type="button" className="fx-btn fx-btn-outline" onClick={() => openForfyCommerce()}>
                      Ouvrir Forfy IA
                    </button>
                  </div>
                </div>

                <div className="commerce-v2-ux1-todo" data-commerce-ux1-todo="true">
                  <h3>À faire</h3>
                  {(merchantTodoItems.length ? merchantTodoItems.slice(0, 5) : [{ label: 'Tout est à jour. Créer ou améliorer une offre.', tab: 'offersHub' }]).map(item => (
                    <button key={item.label} type="button" onClick={() => setActiveTab(item.tab as any)}>
                      <FaCheckCircle />
                      <span>{item.label}</span>
                      <FaArrowRight />
                    </button>
                  ))}
                </div>
              </section>

              <section className="commerce-v2-ux1-category-hub" data-commerce-ux1-category-hub="true">
                <div className="commerce-v2-ux1-category-hub__head">
                  <div>
                    <p className="fx-eyebrow">Catégories marchandes</p>
                    <h2>Choisissez ce que vous voulez faire</h2>
                  </div>
                  <small>Les modules existants restent tous accessibles, mais rangés par métier.</small>
                </div>

                <div className="commerce-v2-ux1-category-list">
                  {commerceV2OverviewCategories.map(category => (
                    <button
                      key={category.id}
                      type="button"
                      className="commerce-v2-ux1-category-row"
                      onClick={() => setActiveTab(category.id as any)}
                    >
                      <span className="commerce-v2-ux1-category-row__main">
                        <strong>{category.label}</strong>
                        <small>{category.hub.subtitle}</small>
                      </span>
                      <span className="commerce-v2-ux1-category-row__status">
                        {category.badge > 0 ? `${category.badge} à traiter` : category.hub.actionLabel || 'Ouvrir'}
                      </span>
                      <FaArrowRight />
                    </button>
                  ))}
                </div>
              </section>

              {tutorials && tutorials.length > 0 && (
                <section className="fx-divider-top">
                  <h2 className="fx-group-title"><FaLightbulb /> Aide et tutoriels</h2>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {tutorials.map(tuto => (
                      <article key={tuto.id} className="fx-card p-6">
                        <span className="fx-badge fx-badge-teal mb-3">{tuto.category}</span>
                        <h3 className="fx-row-title mb-2">{tuto.title}</h3>
                        <p className="fx-body whitespace-pre-wrap">{tuto.content}</p>
                        {tuto.video_url && (
                          <a
                            href={tuto.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className="fx-btn fx-btn-outline mt-5 w-full"
                          >
                            <FaVideo size={14} /> Voir la vidéo
                          </a>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {commerceV2CurrentHub && (
            <div className="commerce-v2-hub space-y-6">
              <section className="commerce-v2-hub-hero">
                <div>
                  <p className="fx-eyebrow">{commerceV2CurrentHub.eyebrow}</p>
                  <h2>{commerceV2CurrentHub.title}</h2>
                  <p>{commerceV2CurrentHub.subtitle}</p>
                </div>
                {activeTab === 'teamHub' && (
                  <RoleSelector currentRole={userRole} onRoleChange={setUserRole} />
                )}
              </section>

              {commerceV2CurrentHub && (
                <div
                  className="commerce-v2-ux1c-forfy-category-cta"
                  data-commerce-ux1c-forfy-category-cta="true"
                >
                  <div>
                    <p className="fx-eyebrow"><FaRobot /> Forfy IA contextuel</p>
                    <h3>{commerceUx1cForfyActions[activeTab]?.title || 'Préparer la prochaine action'}</h3>
                    <span>
                      Forfy IA utilise le contexte de ce commerce : offres, produits, commandes, réservations, messages et performance disponibles.
                    </span>
                  </div>

                  <button
                    type="button"
                    className="fx-btn fx-btn-primary"
                    onClick={() => openForfyCommerceForCategory(activeTab)}
                  >
                    {commerceUx1cForfyActions[activeTab]?.primaryLabel || 'Ouvrir Forfy IA'}
                  </button>
                </div>
              )}

              <div className="commerce-v2-hub-list" data-commerce-ux1-hub-list="true">
                {commerceV2HubTools.map(tool => (
                  <button
                    type="button"
                    key={tool.id}
                    className="commerce-v2-hub-row"
                    onClick={() => tool.id === 'accountLifecycle' ? navigate('/gestion-compte') : setActiveTab(tool.id as any)}
                  >
                    <span className="commerce-v2-hub-row-copy">
                      <strong>{tool.label}</strong>
                      <small>{tool.description}</small>
                    </span>
                    <span className="commerce-v2-hub-row-status">{getCommerceV2ToolStatus(tool.id)}</span>
                    <FaArrowRight />
                  </button>
                ))}
              </div>

              {activeTab === 'marketingHub' && (
                <div className="commerce-v2-boundary-note">
                  <FaShieldAlt />
                  <div>
                    <strong>Publication contrôlée</strong>
                    <p>Les publications Forfeo existantes restent actives. L’autopost Facebook, Instagram, TikTok et LinkedIn n’est pas simulé par V2 : il sera branché dans un lot Pulse dédié avec validation humaine.</p>
                  </div>
                </div>
              )}

              {activeTab === 'teamHub' && (
                <div className="commerce-v2-boundary-note">
                  <FaUsers />
                  <div>
                    <strong>Autorités préservées</strong>
                    <p>V2 réunit les accès et outils existants. Il ne crée pas de nouveaux rôles serveur ni de permissions financières.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'performance' && business && (
            <MerchantPerformancePanel
              business={business}
              stats={stats}
              orders={orders}
              reservations={reservations}
              shopVouchers={shopVouchers}
              deals={deals}
              products={products}
              boutiqueForfaits={boutiqueForfaits}
              boutiqueReviews={boutiqueReviews}
              locale={locale}
              onNavigate={(tab) => setActiveTab(tab as any)}
            />
          )}

          {activeTab === 'combos' && business && (
            <MerchantCombosPanel business={business} />
          )}

          {activeTab === 'revenues' && business && (
            <MerchantRevenuePanel business={business} />
          )}

          {activeTab === 'profile' && (
            <div className="mx-auto max-w-3xl space-y-6">
              <div className="fx-card p-6 sm:p-8">
                <h2 className="fx-page-title mb-6"><FaUserEdit /> Profil de l'entreprise</h2>

                <form onSubmit={handleSaveProfile} className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="label">Nom de l'entreprise</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="label">Catégorie</label>
                      <select
                        value={formData.category}
                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                        className="input-field"
                      >
                        {CATEGORIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label">Téléphone</label>
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="label">Adresse (numéro et rue)</label>
                      <input
                        type="text"
                        value={formData.address || ''}
                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                        className="input-field"
                        placeholder="123 rue Principale"
                      />
                    </div>

                    <div>
                      <label className="label">Ville</label>
                      <input
                        type="text"
                        value={formData.city || ''}
                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                        className="input-field"
                        placeholder="Québec"
                      />
                    </div>

                    <div>
                      <label className="label">Code postal</label>
                      <input
                        type="text"
                        value={formData.postal_code || ''}
                        onChange={e => setFormData({ ...formData, postal_code: e.target.value })}
                        className="input-field"
                        placeholder="G1R 4P5"
                      />
                    </div>

                    <div className="hidden md:col-span-2">
                      <input
                        type="text"
                        value={formData.latitude || ''}
                        onChange={e => setFormData({ ...formData, latitude: e.target.value })}
                      />
                      <input
                        type="text"
                        value={formData.longitude || ''}
                        onChange={e => setFormData({ ...formData, longitude: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Description</label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      className="input-field resize-none"
                    ></textarea>
                  </div>

                  <div className="fx-divider-top pt-6">
                    <h3 className="fx-section-title mb-4">Réseaux sociaux</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="fx-input-icon">
                        <FaFacebook />
                        <input
                          type="text"
                          placeholder="Lien Facebook"
                          value={formData.facebook_url}
                          onChange={e => setFormData({ ...formData, facebook_url: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div className="fx-input-icon">
                        <FaInstagram />
                        <input
                          type="text"
                          placeholder="Lien Instagram"
                          value={formData.instagram_url}
                          onChange={e => setFormData({ ...formData, instagram_url: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div className="fx-input-icon">
                        <FaTiktok />
                        <input
                          type="text"
                          placeholder="Lien TikTok"
                          value={formData.tiktok_url}
                          onChange={e => setFormData({ ...formData, tiktok_url: e.target.value })}
                          className="input-field"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button type="submit" disabled={saving} className="fx-btn fx-btn-primary">
                      {saving ? <FaSync className="animate-spin" /> : <FaSave />}
                      Enregistrer et localiser
                    </button>
                  </div>
                </form>
              </div>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-5"><FaLock /> Sécurité</h3>
                <form onSubmit={handleUpdatePassword}>
                  <div className="mb-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="label">Nouveau mot de passe</label>
                      <input
                        type="password"
                        value={passwordState.new}
                        onChange={e => setPasswordState({ ...passwordState, new: e.target.value })}
                        className="input-field"
                        placeholder="••••••"
                      />
                    </div>
                    <div>
                      <label className="label">Confirmer</label>
                      <input
                        type="password"
                        value={passwordState.confirm}
                        onChange={e => setPasswordState({ ...passwordState, confirm: e.target.value })}
                        className="input-field"
                        placeholder="••••••"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" disabled={saving || !passwordState.new} className="fx-btn fx-btn-outline">
                      Mettre à jour
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'presale' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <p className="fx-eyebrow">Préventes VIP</p>
                <h2 className="fx-panel-title">Offres protégées par un code secret</h2>
                <p className="fx-panel-sub">
                  Vos clients entrent le code, passent par la file d'attente sécurisée, puis achètent.
                </p>
              </section>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaPlus /> Créer une prévente</h3>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => presaleFileInputRef.current?.click()}
                      className="fx-dropzone aspect-square w-full"
                    >
                      {newPresale.image_url ? (
                        <img src={newPresale.image_url} alt="" />
                      ) : (
                        <>
                          <FaCamera size={26} />
                          <span>{uploading ? 'Téléversement…' : 'Photo ou affiche'}</span>
                        </>
                      )}
                    </button>
                    <input
                      type="file"
                      ref={presaleFileInputRef}
                      onChange={e => handleUpload(e, 'presale')}
                      className="hidden"
                    />
                  </div>

                  <div className="space-y-5 lg:col-span-2">
                    <div>
                      <label className="label">Titre de l'offre</label>
                      <input
                        type="text"
                        placeholder="Passeport VIP Platine"
                        value={newPresale.title}
                        onChange={e => setNewPresale({ ...newPresale, title: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="fx-highlight">
                        <label className="label"><FaUserSecret /> Code secret</label>
                        <input
                          type="text"
                          placeholder="FORFEO2026"
                          value={newPresale.secret_code}
                          onChange={e => setNewPresale({ ...newPresale, secret_code: e.target.value })}
                          className="input-field font-mono uppercase tracking-wider"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="label">Prix VIP ($)</label>
                          <input
                            type="number"
                            placeholder="89"
                            value={newPresale.price}
                            onChange={e => setNewPresale({ ...newPresale, price: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="label">Quantité</label>
                          <input
                            type="number"
                            placeholder="100"
                            value={newPresale.quantity}
                            onChange={e =>
                              setNewPresale({ ...newPresale, quantity: parseInt(e.target.value) || 0 })
                            }
                            className="input-field"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="label">Description</label>
                      <textarea
                        placeholder="Ce que vos clients obtiennent…"
                        rows={3}
                        value={newPresale.description}
                        onChange={e => setNewPresale({ ...newPresale, description: e.target.value })}
                        className="input-field resize-none"
                      ></textarea>
                      <AiMagicButton
                        onClick={() => simulateAiGeneration('description', 'presale')}
                        loading={aiLoading.presale}
                      />
                    </div>

                    <div>
                      <label className="label">Avantages inclus (un par ligne)</label>
                      <textarea
                        placeholder="Accès coupe-file&#10;Consommation incluse"
                        rows={3}
                        value={newPresale.features}
                        onChange={e => setNewPresale({ ...newPresale, features: e.target.value })}
                        className="input-field resize-none"
                      ></textarea>
                    </div>

                    <button
                      onClick={handleCreatePresaleOffer}
                      disabled={saving}
                      className="fx-btn fx-btn-primary w-full"
                    >
                      {saving ? 'Création…' : "Créer l'offre VIP"}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="fx-group-title"><FaTicketAlt /> Préventes en ligne</h3>
                {presaleOffers.length === 0 ? (
                  <div className="fx-empty"><p>Aucune prévente publiée.</p></div>
                ) : (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    {presaleOffers.map((offer: any) => (
                      <article key={offer.id} className="fx-card p-5">
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <div className="flex min-w-0 gap-4">
                            <img
                              src={offer.image_url || 'https://via.placeholder.com/150'}
                              className="fx-thumb"
                              alt=""
                            />
                            <div className="min-w-0">
                              <h4 className="fx-row-title truncate">{offer.title}</h4>
                              <p className="fx-price">{offer.price} $</p>
                              <p className="fx-row-sub">{offer.quantity_available} places restantes</p>
                            </div>
                          </div>
                          <span className="fx-badge fx-badge-amber font-mono">{offer.secret_code}</span>
                        </div>
                        <div className="fx-card-foot">
                          <span className="fx-help">
                            Ajoutée le {new Date(offer.created_at).toLocaleDateString(locale)}
                          </span>
                          <button
                            onClick={() => handleDelete('presale_offers', offer.id)}
                            className="fx-icon-btn fx-icon-btn-danger"
                            title="Supprimer"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'scanner' && (
            <div className="mx-auto max-w-4xl space-y-6">
              <SmartScannerWidget
                business={business}
                onValidate={handleValidateVoucher}
                scanLoading={scanLoading}
                scanMessage={scanMessage}
                quickMode={false}
              />

              {validatedShopVoucher && (
                <div className="fx-card p-6 sm:p-8">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="fx-badge fx-badge-blue"><FaTicketAlt size={10} /> Voucher Forfeo</span>
                        <span
                          className={`fx-badge ${
                            String(validatedShopVoucher.effective_status || validatedShopVoucher.status).toLowerCase() === 'active'
                              ? 'fx-badge-green'
                              : String(validatedShopVoucher.effective_status || validatedShopVoucher.status).toLowerCase() === 'redeemed'
                                ? 'fx-badge-slate'
                                : 'fx-badge-red'
                          }`}
                        >
                          {voucherStatus(validatedShopVoucher.effective_status || validatedShopVoucher.status)}
                        </span>
                      </div>

                      <h3 className="fx-page-title mb-1">
                        {validatedShopVoucher.title || validatedShopVoucher.item?.title || tx('voucherForfeo')}
                      </h3>
                      <p className="fx-row-sub mb-6">
                        {tx('voucherCode')} : <span className="fx-mono">{validatedShopVoucher.voucher_code}</span>
                      </p>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="fx-mini">
                          <p className="fx-mini-label">{tx('customer')}</p>
                          <p className="fx-mini-value">
                            {validatedShopVoucher.order?.customer_name || validatedShopVoucher.customer_email || validatedShopVoucher.order?.customer_email || tx('customer')}
                          </p>
                        </div>
                        <div className="fx-mini">
                          <p className="fx-mini-label">{tx('value')}</p>
                          <p className="fx-mini-value fx-text-green">
                            {formatVoucherAmount(validatedShopVoucher.amount_cents, validatedShopVoucher.currency || 'cad')}
                          </p>
                        </div>
                        <div className="fx-mini">
                          <p className="fx-mini-label">{tx('order')}</p>
                          <p className="fx-mini-value fx-mono">
                            {validatedShopVoucher.order_id ? shortId(validatedShopVoucher.order_id) : '—'}
                          </p>
                        </div>
                        <div className="fx-mini">
                          <p className="fx-mini-label">
                            {validatedShopVoucher.redeemed_at ? tx('usedOn') : tx('expiresOn')}
                          </p>
                          <p className="fx-mini-value">
                            {validatedShopVoucher.redeemed_at
                              ? new Date(validatedShopVoucher.redeemed_at).toLocaleString(locale)
                              : validatedShopVoucher.expires_at
                                ? new Date(validatedShopVoucher.expires_at).toLocaleDateString(locale)
                                : tx('noDateDefined')}
                          </p>
                        </div>
                      </div>

                      {String(validatedShopVoucher.effective_status || validatedShopVoucher.status).toLowerCase() === 'redeemed' && (
                        <div className="fx-note fx-note-success mt-5">
                          <FaShieldAlt />
                          <div>
                            <h4 className="fx-note-title">{tx('forfeoProofRecorded')}</h4>
                            <p className="fx-note-text">
                              {tx('forfeoProofRecordedText')} {business?.name ? `(${business.name})` : ''}
                            </p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              <div className="fx-mini">
                                <p className="fx-mini-label">{tx('source')}</p>
                                <p className="fx-mini-value">{scanSource(lastVoucherScanSource)}</p>
                              </div>
                              <div className="fx-mini">
                                <p className="fx-mini-label">{tx('status')}</p>
                                <p className="fx-mini-value">{tx('redeemedNotReusable')}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <aside className="fx-action-panel lg:w-72">
                      <p className="fx-mini-label">{tx('merchantAction')}</p>
                      {String(validatedShopVoucher.effective_status || validatedShopVoucher.status).toLowerCase() === 'active' ? (
                        <>
                          <p className="fx-action-text">{tx('voucherIsActiveConfirm')}</p>
                          <button
                            onClick={handleRedeemValidatedVoucher}
                            disabled={voucherRedeemLoading}
                            className="fx-btn fx-btn-success w-full"
                          >
                            {voucherRedeemLoading ? <FaSync className="animate-spin" /> : <FaCheckDouble />}
                            {voucherRedeemLoading ? tx('confirming') : tx('markRedeemed')}
                          </button>
                        </>
                      ) : (
                        <p className="fx-action-text">{tx('noActionForStatus')}</p>
                      )}
                    </aside>
                  </div>
                </div>
              )}

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaHistory /> Validations récentes</h3>

                <div className="space-y-6">
                  <div>
                    <p className="label">Vouchers Forfeo utilisés</p>
                    <div className="space-y-3">
                      {shopVouchers
                        .filter((v: any) => String(v.status).toLowerCase() === 'redeemed')
                        .slice(0, 5)
                        .map((voucher: any) => (
                          <div key={voucher.id} className="fx-list-item">
                            <div>
                              <p className="fx-row-title">{voucher.title || tx('voucherForfeo')}</p>
                              <p className="fx-row-sub">
                                {tx('voucherCode')} : <span className="fx-mono">{voucher.voucher_code}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="fx-badge fx-badge-green">{voucherStatus('redeemed')}</span>
                              <p className="fx-help mt-1">
                                {new Date(voucher.redeemed_at || voucher.updated_at || voucher.created_at).toLocaleDateString(locale)}
                              </p>
                            </div>
                          </div>
                        ))}
                      {shopVouchers.filter((v: any) => String(v.status).toLowerCase() === 'redeemed').length === 0 && (
                        <p className="fx-empty-inline">{tx('noRecentRedeemedVouchers')}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="label">{tx('legacyOrderCodes')}</p>
                    <div className="space-y-3">
                      {orders
                        .filter((o: any) => o.status === 'picked_up' || o.fulfillment_status === 'fulfilled')
                        .slice(0, 5)
                        .map((order: any) => (
                          <div key={order.id} className="fx-list-item">
                            <div>
                              <p className="fx-row-title">{order.customer_name || tx('customer')}</p>
                              <p className="fx-row-sub">
                                {tx('voucherCode')} : <span className="fx-mono">{order.pickup_code}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="fx-badge fx-badge-green">{tx('validated')}</span>
                              <p className="fx-help mt-1">
                                {new Date(order.updated_at || order.created_at).toLocaleDateString(locale)}
                              </p>
                            </div>
                          </div>
                        ))}
                      {orders.filter((o: any) => o.status === 'picked_up' || o.fulfillment_status === 'fulfilled').length === 0 && (
                        <p className="fx-empty-inline">{tx('noHistoricalValidation')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="mx-auto max-w-5xl">
              <div className="fx-card p-6 sm:p-8">
                <h2 className="fx-page-title mb-6"><FaShoppingBag /> {tx('orderHistory')}</h2>
                {orders.length === 0 ? (
                  <div className="fx-empty"><p>{tx('noOrderRecorded')}</p></div>
                ) : (
                  <div className="fx-table-wrap">
                    <table className="fx-table">
                      <thead>
                        <tr>
                          <th>{tx('reference')}</th>
                          <th>Date</th>
                          <th>{tx('customer')}</th>
                          <th>{tx('amount')}</th>
                          <th className="text-right">{tx('status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o: any) => (
                          <tr key={o.id}>
                            <td className="fx-mono">{o.pickup_code || shortId(o.id)}</td>
                            <td>{new Date(o.created_at).toLocaleDateString(locale)}</td>
                            <td>{o.customer_name || o.customer_email || tx('anonymous')}</td>
                            <td className="fx-text-green">{toNum(o.total_amount).toFixed(2)} $</td>
                            <td className="text-right">
                              <span
                                className={`fx-badge ${
                                  o.status === 'paid'
                                    ? 'fx-badge-blue'
                                    : o.status === 'picked_up' || o.fulfillment_status === 'fulfilled'
                                      ? 'fx-badge-green'
                                      : 'fx-badge-slate'
                                }`}
                              >
                                {o.status === 'paid'
                                  ? tx('paid')
                                  : o.status === 'picked_up' || o.fulfillment_status === 'fulfilled'
                                    ? tx('validated')
                                    : o.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'sign' && (
            <div className="mx-auto max-w-5xl">
              <ForfeoSignSection business={business} />
            </div>
          )}

          {activeTab === 'boutique' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="fx-eyebrow">Boutique</p>
                    <h2 className="fx-panel-title">Forfaits à valider sur place</h2>
                    <p className="fx-panel-sub">Vos clients achètent en ligne et présentent un code QR.</p>
                  </div>
                  <span className="fx-badge fx-badge-violet"><FaQrcode size={10} /> Validation sur place</span>
                </div>
              </section>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaPlus /> Créer un forfait</h3>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => boutiqueFileInputRef.current?.click()}
                      className="fx-dropzone aspect-square w-full"
                    >
                      {newBoutiqueForfait.image_url ? (
                        <img src={newBoutiqueForfait.image_url} alt="" />
                      ) : (
                        <>
                          <FaCamera size={26} />
                          <span>{uploading ? 'Téléversement…' : 'Photo principale'}</span>
                        </>
                      )}
                    </button>
                    <input
                      type="file"
                      ref={boutiqueFileInputRef}
                      onChange={e => handleUpload(e, 'boutique')}
                      className="hidden"
                    />
                  </div>

                  <div className="space-y-5 lg:col-span-2">
                    <div>
                      <label className="label">Titre du forfait</label>
                      <input
                        type="text"
                        placeholder="Escapade détente nordique pour 2"
                        value={newBoutiqueForfait.title}
                        onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, title: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="label">Prix régulier</label>
                        <input
                          type="number"
                          placeholder="150"
                          value={newBoutiqueForfait.original_price}
                          onChange={e =>
                            setNewBoutiqueForfait({ ...newBoutiqueForfait, original_price: e.target.value })
                          }
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="label">Prix du forfait</label>
                        <input
                          type="number"
                          placeholder="89"
                          value={newBoutiqueForfait.price}
                          onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, price: e.target.value })}
                          className="input-field fx-input-accent"
                        />
                      </div>
                      <div>
                        <label className="label">Catégorie</label>
                        <select
                          value={newBoutiqueForfait.category}
                          onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, category: e.target.value })}
                          className="input-field"
                        >
                          {CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="label">Description</label>
                      <textarea
                        placeholder="Décrivez l'expérience…"
                        rows={3}
                        value={newBoutiqueForfait.description}
                        onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, description: e.target.value })}
                        className="input-field resize-none"
                      ></textarea>
                      <AiMagicButton
                        onClick={() => simulateAiGeneration('description', 'boutique')}
                        loading={aiLoading.boutique}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="label">Inclusions (une par ligne)</label>
                        <textarea
                          placeholder="Repas 3 services&#10;Bouteille de vin"
                          rows={4}
                          value={newBoutiqueForfait.includes}
                          onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, includes: e.target.value })}
                          className="input-field resize-none"
                        ></textarea>
                      </div>
                      <div>
                        <label className="label">Conditions (une par ligne)</label>
                        <textarea
                          placeholder="Valide le jeudi seulement&#10;Réservation requise"
                          rows={4}
                          value={newBoutiqueForfait.conditions}
                          onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, conditions: e.target.value })}
                          className="input-field resize-none"
                        ></textarea>
                      </div>
                    </div>

                    <div>
                      <label className="label">Validité</label>
                      <input
                        type="text"
                        placeholder="Valide jusqu'au 31 décembre 2026"
                        value={newBoutiqueForfait.expiration}
                        onChange={e => setNewBoutiqueForfait({ ...newBoutiqueForfait, expiration: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <button
                      onClick={handleCreateBoutiqueForfait}
                      disabled={saving}
                      className="fx-btn fx-btn-primary w-full"
                    >
                      {saving ? 'Publication…' : 'Publier dans la boutique'}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="fx-group-title"><FaListAlt /> Forfaits en ligne</h3>
                {boutiqueForfaits.length === 0 ? (
                  <div className="fx-empty"><p>Aucun forfait publié.</p></div>
                ) : (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    {boutiqueForfaits.map((forfait: any) => (
                      <article key={forfait.id} className="fx-card fx-card-split">
                        <div className="fx-split-media">
                          <img src={forfait.image || 'https://via.placeholder.com/200'} alt="" />
                          <span className="fx-discount">-{forfait.discount}%</span>
                        </div>
                        <div className="fx-split-body">
                          <div>
                            <h4 className="fx-row-title line-clamp-1">{forfait.title}</h4>
                            <p className="fx-row-sub line-clamp-2">{forfait.description}</p>
                          </div>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="fx-price-old">{forfait.original_price} $</p>
                              <p className="fx-price">{forfait.price} $</p>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/boutique/${forfait.id}`} target="_blank" className="fx-icon-btn" title="Voir">
                                <FaEye size={12} />
                              </Link>
                              <button
                                onClick={() => handleDelete('boutique_forfaits', forfait.id)}
                                className="fx-icon-btn fx-icon-btn-danger"
                                title="Supprimer"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>

              <div className="fx-divider-top pt-6">
                <h3 className="fx-group-title"><FaStar /> Avis de vos clients</h3>
                {boutiqueReviews.length === 0 ? (
                  <div className="fx-empty">
                    <p>Aucun avis pour l'instant.</p>
                    <p className="fx-empty-sub">Les avis arrivent après l'expérience vécue par vos clients.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {boutiqueReviews.map((review: any) => (
                      <article key={review.id} className="fx-card p-5">
                        <div className="mb-3 flex flex-col justify-between gap-3 md:flex-row md:items-center">
                          <div>
                            <p className="fx-row-title">{review.user_name}</p>
                            <p className="fx-row-sub">
                              Après l'achat de <strong>{review.boutique_forfaits?.title}</strong>
                            </p>
                          </div>
                          <div className="md:text-right">
                            <div className="fx-stars">
                              {[1, 2, 3, 4, 5].map(star => (
                                <FaStar key={star} size={13} className={star <= review.rating ? 'is-on' : ''} />
                              ))}
                            </div>
                            <span className="fx-help">
                              {new Date(review.created_at).toLocaleDateString(locale)}
                            </span>
                          </div>
                        </div>
                        {review.comment && <p className="fx-quote">{review.comment}</p>}
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'wallet' && (
            <div className="mx-auto max-w-4xl space-y-6">
              <section className="fx-panel-hero">
                <p className="fx-eyebrow">Revenus</p>
                <h2 className="fx-panel-title">Encaissements Stripe</h2>
                <p className="fx-panel-sub">Suivez vos ventes et vos virements automatiques.</p>
              </section>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="fx-stat">
                  <span className="fx-stat-icon fx-tone-green"><FaCoins size={16} /></span>
                  <p className="fx-stat-label">Total encaissé sur Forfeo</p>
                  <p className="fx-stat-value">{stats.revenue.toFixed(2)} $</p>
                </div>
                <div className="fx-stat">
                  <span className="fx-stat-icon fx-tone-blue"><FaSync size={16} /></span>
                  <p className="fx-stat-label">En transit chez Stripe</p>
                  <p className="fx-stat-value">…</p>
                </div>
              </div>

              {!business.stripe_account_id ? (
                <div className="fx-card flex flex-col items-start justify-between gap-5 p-6 sm:flex-row sm:items-center sm:p-8">
                  <div>
                    <h3 className="fx-section-title mb-1">Connecter Stripe</h3>
                    <p className="fx-body">
                      Reliez votre compte bancaire pour recevoir les paiements de vos clients.
                    </p>
                  </div>
                  <button onClick={handleConnectStripe} disabled={loading} className="fx-btn fx-btn-primary whitespace-nowrap">
                    {loading ? 'Chargement…' : 'Configurer les virements'} <FaArrowRight size={12} />
                  </button>
                </div>
              ) : (
                <div className="fx-note fx-note-success">
                  <FaCheckCircle size={18} />
                  <div>
                    <h4 className="fx-note-title">Compte bancaire connecté</h4>
                    <p className="fx-note-text">Vos virements sont envoyés automatiquement.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'hotel' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="fx-eyebrow">Hébergement</p>
                    <h2 className="fx-panel-title">Chambres, équipements et règles</h2>
                    <p className="fx-panel-sub">Tout ce que vos voyageurs voient avant de réserver.</p>
                  </div>
                  <button onClick={() => openReserveCalendar(null)} className="fx-btn fx-btn-outline whitespace-nowrap">
                    <FaCalendarCheck size={13} /> Gérer les disponibilités
                  </button>
                </div>
              </section>

              <BusinessDiningSection businessId={business?.id} />

              <div className="fx-card overflow-hidden">
                <header className="fx-card-head">
                  <div>
                    <h3 className="fx-card-title">Informations de l'établissement</h3>
                    <p className="fx-row-sub">Équipements, règles de la maison et questions fréquentes.</p>
                  </div>
                  <button onClick={handleSaveHotelDetails} disabled={hotelDetailsLoading} className="fx-btn fx-btn-primary">
                    {hotelDetailsLoading ? <FaSync className="animate-spin" /> : <FaSave />}
                    Enregistrer
                  </button>
                </header>

                <div className="space-y-8 p-6 sm:p-8">
                  <div>
                    <h4 className="fx-section-title mb-4"><FaStar /> Équipements</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {Object.entries(HOTEL_AMENITIES_OPTIONS).map(([category, opts]) => (
                        <div key={category} className="fx-subcard">
                          <h5 className="fx-subcard-title">{category}</h5>
                          <div className="space-y-2">
                            {opts.map((opt) => {
                              const isSelected = hotelDetails.amenities.includes(opt.label);
                              return (
                                <label key={opt.label} className="fx-check-line">
                                  <span className={`fx-check-box ${isSelected ? 'is-on' : ''}`}>
                                    {isSelected && <FaCheck size={9} />}
                                  </span>
                                  <input
                                    type="checkbox"
                                    className="hidden"
                                    checked={isSelected}
                                    onChange={() => {
                                      const newAmenities = isSelected
                                        ? hotelDetails.amenities.filter(a => a !== opt.label)
                                        : [...hotelDetails.amenities, opt.label];
                                      setHotelDetails(prev => ({ ...prev, amenities: newAmenities }));
                                    }}
                                  />
                                  <span className={`fx-check-text ${isSelected ? 'is-on' : ''}`}>
                                    {opt.icon} {opt.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="fx-divider-top pt-6">
                    <h4 className="fx-section-title mb-4"><FaGavel /> Règles de la maison</h4>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className="label">Arrivée</label>
                            <input
                              type="time"
                              value={hotelDetails.rules.check_in}
                              onChange={e =>
                                setHotelDetails(prev => ({
                                  ...prev,
                                  rules: { ...prev.rules, check_in: e.target.value }
                                }))
                              }
                              className="input-field"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="label">Départ</label>
                            <input
                              type="time"
                              value={hotelDetails.rules.check_out}
                              onChange={e =>
                                setHotelDetails(prev => ({
                                  ...prev,
                                  rules: { ...prev.rules, check_out: e.target.value }
                                }))
                              }
                              className="input-field"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">Règles spécifiques</label>
                          <textarea
                            placeholder="Pas de bruit après 22 h, remise des clés en main propre…"
                            className="input-field h-24 resize-none"
                            value={hotelDetails.rules.custom_rules || ''}
                            onChange={e =>
                              setHotelDetails(prev => ({
                                ...prev,
                                rules: { ...prev.rules, custom_rules: e.target.value }
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        {[
                          { key: 'smoking_allowed', label: 'Fumeur autorisé', icon: <FaSmokingBan /> },
                          { key: 'pets_allowed', label: 'Animaux acceptés', icon: <FaDog /> },
                          { key: 'parties_allowed', label: 'Fêtes et événements autorisés', icon: <FaGlassCheers /> },
                        ].map((rule) => (
                          <div key={rule.key} className="fx-switch-row">
                            <span className="fx-switch-label">{rule.icon} {rule.label}</span>
                            <label className="inline-flex cursor-pointer select-none items-center">
                              <span className="sr-only">{rule.label}</span>
                              <div className={`toggle ${!!(hotelDetails.rules as any)[rule.key] ? 'toggle-on' : ''}`}>
                                <span className="toggle-knob" />
                              </div>
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={!!(hotelDetails.rules as any)[rule.key]}
                                onChange={e =>
                                  setHotelDetails(prev => ({
                                    ...prev,
                                    rules: { ...prev.rules, [rule.key]: e.target.checked }
                                  }))
                                }
                              />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="fx-divider-top pt-6">
                    <h4 className="fx-section-title mb-4"><FaQuestionCircle /> Questions fréquentes</h4>
                    <div className="space-y-4">
                      {hotelDetails.faq.map((item, idx) => (
                        <div key={idx} className="fx-subcard relative">
                          <button
                            onClick={() =>
                              setHotelDetails(prev => ({
                                ...prev,
                                faq: prev.faq.filter((_, i) => i !== idx)
                              }))
                            }
                            className="fx-icon-btn fx-icon-btn-danger absolute right-3 top-3"
                            title="Supprimer"
                          >
                            <FaTrash size={11} />
                          </button>
                          <input
                            type="text"
                            placeholder="Y a-t-il un stationnement ?"
                            className="fx-inline-input"
                            value={item.question}
                            onChange={e => {
                              const newFaq = [...hotelDetails.faq];
                              newFaq[idx].question = e.target.value;
                              setHotelDetails(prev => ({ ...prev, faq: newFaq }));
                            }}
                          />
                          <textarea
                            placeholder="Votre réponse…"
                            rows={2}
                            className="input-field mt-2 resize-none"
                            value={item.answer}
                            onChange={e => {
                              const newFaq = [...hotelDetails.faq];
                              newFaq[idx].answer = e.target.value;
                              setHotelDetails(prev => ({ ...prev, faq: newFaq }));
                            }}
                          />
                        </div>
                      ))}
                      <button
                        onClick={() =>
                          setHotelDetails(prev => ({
                            ...prev,
                            faq: [...prev.faq, { question: '', answer: '' }]
                          }))
                        }
                        className="fx-add-row"
                      >
                        <FaPlus size={12} /> Ajouter une question
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaPlus /> Ajouter une chambre</h3>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="label">Nom</label>
                        <input
                          type="text"
                          placeholder="Suite Deluxe"
                          value={newRoom.name}
                          onChange={e => setNewRoom({ ...newRoom, name: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div className="w-1/3">
                        <label className="label">Capacité</label>
                        <input
                          type="number"
                          min="1"
                          value={newRoom.capacity_adults}
                          onChange={e =>
                            setNewRoom({ ...newRoom, capacity_adults: parseInt(e.target.value) || 1 })
                          }
                          className="input-field text-center"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label">Prix par nuit ($)</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={newRoom.price_per_night}
                        onChange={e => setNewRoom({ ...newRoom, price_per_night: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="label">Description</label>
                      <textarea
                        rows={5}
                        placeholder="Ce que la chambre offre…"
                        value={newRoom.description}
                        onChange={e => setNewRoom({ ...newRoom, description: e.target.value })}
                        className="input-field resize-none"
                      ></textarea>
                      <AiMagicButton
                        onClick={() => simulateAiGeneration('description', 'hotel')}
                        loading={aiLoading.hotel}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Photos (10 maximum)</label>
                    <div className="mb-4 grid grid-cols-3 gap-3">
                      {newRoom.images.map((img: string, i: number) => (
                        <div key={i} className="fx-photo">
                          <img src={img} alt="" />
                          <button
                            onClick={() =>
                              setNewRoom((prev: any) => ({
                                ...prev,
                                images: prev.images.filter((_: any, idx: number) => idx !== i)
                              }))
                            }
                            className="fx-photo-remove"
                            aria-label="Retirer"
                          >
                            <FaTimes size={9} />
                          </button>
                        </div>
                      ))}

                      {newRoom.images.length < 10 && (
                        <button
                          type="button"
                          onClick={() => roomFileInputRef.current?.click()}
                          className="fx-dropzone aspect-square"
                        >
                          {uploading ? <FaSync className="animate-spin" /> : <FaCamera size={20} />}
                          <span>{uploading ? 'Téléversement…' : 'Ajouter'}</span>
                        </button>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={roomFileInputRef}
                      onChange={e => handleUpload(e, 'room')}
                      className="hidden"
                      multiple
                      accept="image/*"
                    />
                  </div>
                </div>

                <button onClick={handleAddRoom} className="fx-btn fx-btn-primary mt-6 w-full">
                  <FaPlus size={12} /> Créer la chambre
                </button>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {accommodations.map((room: any) => (
                  <article key={room.id} className="fx-card overflow-hidden">
                    <div className="fx-media">
                      <img
                        src={room.images?.[0] || room.image_url || 'https://via.placeholder.com/400x300'}
                        alt=""
                      />
                      <span className="fx-media-chip"><FaUsers size={11} /> {room.capacity_adults} pers.</span>
                    </div>
                    <div className="p-5">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <h4 className="fx-row-title">{room.name}</h4>
                        <p className="fx-price">{room.price_per_night} $</p>
                      </div>
                      <p className="fx-row-sub mb-5 line-clamp-2">{room.description}</p>
                      <div className="fx-card-foot">
                        <button onClick={() => openReserveCalendar(room.id)} className="fx-btn fx-btn-outline flex-1">
                          <FaCalendarAlt size={12} /> Disponibilités
                        </button>
                        <button
                          onClick={() => handleDelete('accommodations', room.id)}
                          className="fx-icon-btn fx-icon-btn-danger"
                          title="Supprimer"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="fx-card p-6 sm:p-8">
                <div className="mb-6 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
                  <div>
                    <h3 className="fx-section-title"><FaEyeSlash /> Hôtel mystère</h3>
                    <p className="fx-row-sub">
                      L'image publique est floutée ; l'image réelle reste secrète jusqu'à l'achat.
                    </p>
                  </div>
                  <span className="fx-badge fx-badge-slate">{mysteryOffers.length} offre(s) active(s)</span>
                </div>

                <div className="fx-subcard p-5 sm:p-6">
                  <h4 className="fx-section-title mb-5"><FaPlus /> Créer une offre mystère</h4>

                  <div className="flex flex-col gap-6 md:flex-row">
                    <div className="w-full space-y-4 md:w-1/3">
                      <button
                        type="button"
                        onClick={() => mysteryFileInputRef.current?.click()}
                        className="fx-dropzone aspect-[4/5] w-full"
                      >
                        {(() => {
                          const showUrl =
                            mysteryPreviewMode === 'secret' && newMysteryOffer.secret_image_url
                              ? newMysteryOffer.secret_image_url
                              : newMysteryOffer.image_url;

                          if (showUrl) {
                            const shouldBlurPreview =
                              mysteryPreviewMode === 'secret' && newMysteryOffer.blur_enabled;
                            return (
                              <img
                                src={showUrl}
                                alt=""
                                style={
                                  shouldBlurPreview
                                    ? { filter: `blur(${toNum(newMysteryOffer.blur_radius, 16)}px)` }
                                    : undefined
                                }
                              />
                            );
                          }

                          return (
                            <>
                              <FaCamera size={26} />
                              <span>{uploading ? 'Téléversement…' : 'Ajouter une photo'}</span>
                            </>
                          );
                        })()}
                      </button>

                      <input
                        type="file"
                        ref={mysteryFileInputRef}
                        onChange={e => handleUpload(e, 'mystery')}
                        className="hidden"
                        accept="image/*"
                      />

                      <div className="fx-card p-4">
                        <div className="flex items-center justify-between">
                          <p className="label mb-0">Aperçu</p>
                          <div className="fx-segmented fx-segmented-sm">
                            <button
                              type="button"
                              onClick={() => setMysteryPreviewMode('public')}
                              className={`fx-segmented-item ${mysteryPreviewMode === 'public' ? 'is-active' : ''}`}
                            >
                              Publique
                            </button>
                            <button
                              type="button"
                              onClick={() => setMysteryPreviewMode('secret')}
                              className={`fx-segmented-item ${mysteryPreviewMode === 'secret' ? 'is-active' : ''}`}
                            >
                              Réelle
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="fx-switch-label"><FaEyeSlash /> Flouter l'image publique</span>
                            <input
                              type="checkbox"
                              checked={!!newMysteryOffer.blur_enabled}
                              onChange={e =>
                                setNewMysteryOffer((p: any) => ({ ...p, blur_enabled: e.target.checked }))
                              }
                            />
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="fx-help">Intensité</span>
                            <input
                              type="range"
                              min={6}
                              max={28}
                              value={toNum(newMysteryOffer.blur_radius, 16)}
                              onChange={e =>
                                setNewMysteryOffer((p: any) => ({ ...p, blur_radius: parseInt(e.target.value) }))
                              }
                              className="w-full"
                              disabled={!newMysteryOffer.blur_enabled}
                            />
                            <span className="fx-mini-value w-12 text-right">
                              {toNum(newMysteryOffer.blur_radius, 16)}px
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={generateMysteryBlurredPublicImage}
                            disabled={mysteryImgProcessing || !newMysteryOffer.blur_enabled}
                            className="fx-btn fx-btn-primary w-full"
                          >
                            {mysteryImgProcessing ? <FaSync className="animate-spin" /> : <FaEyeSlash />}
                            {mysteryImgProcessing ? 'Génération…' : 'Générer le flou'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 space-y-5">
                      <div>
                        <label className="label">Titre</label>
                        <input
                          type="text"
                          placeholder="Escapade mystère, 1 nuit"
                          value={newMysteryOffer.title}
                          onChange={e => setNewMysteryOffer({ ...newMysteryOffer, title: e.target.value })}
                          className="input-field"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        <div className="col-span-2">
                          <label className="label">Prix mystère ($)</label>
                          <input
                            type="number"
                            placeholder="149"
                            value={newMysteryOffer.mystery_price}
                            onChange={e =>
                              setNewMysteryOffer({ ...newMysteryOffer, mystery_price: e.target.value })
                            }
                            className="input-field fx-input-accent"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="label">Prix original</label>
                          <input
                            type="number"
                            placeholder="249"
                            value={newMysteryOffer.original_price}
                            onChange={e =>
                              setNewMysteryOffer({ ...newMysteryOffer, original_price: e.target.value })
                            }
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="label">Quantité</label>
                          <input
                            type="number"
                            min={1}
                            value={newMysteryOffer.quantity_available}
                            onChange={e =>
                              setNewMysteryOffer({
                                ...newMysteryOffer,
                                quantity_available: parseInt(e.target.value || '1')
                              })
                            }
                            className="input-field text-center"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="label">Expire dans (heures)</label>
                          <input
                            type="number"
                            min={1}
                            value={newMysteryOffer.expires_in_hours}
                            onChange={e =>
                              setNewMysteryOffer({
                                ...newMysteryOffer,
                                expires_in_hours: parseInt(e.target.value || '72')
                              })
                            }
                            className="input-field"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="label">Description</label>
                        <textarea
                          rows={4}
                          placeholder="Décrivez l'expérience sans révéler l'établissement."
                          value={newMysteryOffer.description}
                          onChange={e =>
                            setNewMysteryOffer({ ...newMysteryOffer, description: e.target.value })
                          }
                          className="input-field resize-none"
                        ></textarea>
                        <AiMagicButton
                          onClick={() => simulateAiGeneration('description', 'mystery')}
                          loading={aiLoading.mystery}
                        />
                      </div>

                      <button onClick={handleCreateMysteryOffer} className="fx-btn fx-btn-primary w-full">
                        <FaEyeSlash size={13} /> Publier l'offre mystère
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="fx-group-title"><FaListAlt /> Vos offres mystère</h4>
                  {mysteryOffers.length === 0 ? (
                    <div className="fx-empty">
                      <FaEyeSlash size={26} />
                      <p>Aucune offre mystère publiée.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      {mysteryOffers.map((o: any) => {
                        const showReal = !!mysteryAdminShowRealById[o.id];
                        const imgUrl =
                          !!o?.secret_image_url && showReal
                            ? o.secret_image_url
                            : o?.image_url ||
                              formData.image_url ||
                              'https://placehold.co/800x500?text=Hotel+Mystere';
                        const shouldBlurCss =
                          !showReal && (!o?.image_url || o?.image_url === o?.secret_image_url);

                        return (
                          <article key={o.id} className="fx-card overflow-hidden">
                            <div className="fx-media">
                              <img
                                src={imgUrl}
                                alt=""
                                style={shouldBlurCss ? { filter: 'blur(18px)' } : undefined}
                              />
                              <span className="fx-media-chip"><FaHotel size={11} /> Offre mystère</span>

                              {!!o.secret_image_url && (
                                <button
                                  onClick={() =>
                                    setMysteryAdminShowRealById((p) => ({ ...p, [o.id]: !p[o.id] }))
                                  }
                                  className="fx-media-action"
                                >
                                  {showReal ? <FaEyeSlash /> : <FaEye />}
                                  {showReal ? 'Masquer' : 'Voir'} l'image réelle
                                </button>
                              )}
                            </div>

                            <div className="p-5">
                              <div className="flex items-start justify-between gap-4">
                                <h5 className="fx-row-title truncate">{o?.title || 'Offre mystère'}</h5>
                                <p className="fx-price">{toNum(o.mystery_price).toFixed(0)} $</p>
                              </div>

                              <button
                                onClick={() => setExpandedMysteryId((prev) => (prev === o.id ? null : o.id))}
                                className="fx-btn fx-btn-outline mt-4 w-full"
                              >
                                <FaEye size={12} /> {expandedMysteryId === o.id ? 'Masquer' : 'Voir'} les détails
                              </button>

                              {expandedMysteryId === o.id && (
                                <p className="fx-quote mt-3">{o?.description || '—'}</p>
                              )}

                              <div className="fx-card-foot">
                                <button
                                  onClick={() => handleDelete('mystery_hotel_offers', o.id)}
                                  className="fx-btn fx-btn-danger-soft flex-1"
                                >
                                  <FaTrash size={12} /> Supprimer
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'drops' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <p className="fx-eyebrow">Forfeo Drops</p>
                <h2 className="fx-panel-title">Récompenses géolocalisées</h2>
                <p className="fx-panel-sub">
                  Déposez une surprise autour de votre établissement ; elle apparaît sur le radar des visiteurs.
                </p>
              </section>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaPlus /> Créer un drop</h3>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="label">Titre</label>
                    <input
                      type="text"
                      value={newDrop.title}
                      onChange={e => setNewDrop({ ...newDrop, title: e.target.value })}
                      className="input-field"
                      placeholder="Surprise devant la boutique"
                    />
                  </div>

                  <div>
                    <label className="label">Récompense</label>
                    <input
                      type="text"
                      value={newDrop.reward}
                      onChange={e => setNewDrop({ ...newDrop, reward: e.target.value })}
                      className="input-field"
                      placeholder="Café gratuit"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="label">Description</label>
                    <textarea
                      rows={3}
                      value={newDrop.description}
                      onChange={e => setNewDrop({ ...newDrop, description: e.target.value })}
                      className="input-field resize-none"
                      placeholder="Expliquez la surprise et comment la réclamer…"
                    ></textarea>
                  </div>

                  <div className="md:col-span-2">
                    <label className="label">Adresse du drop (numéro et rue)</label>
                    <input
                      type="text"
                      value={newDrop.drop_address}
                      onChange={e => setNewDrop({ ...newDrop, drop_address: e.target.value })}
                      className="input-field"
                      placeholder="700 Place D’Youville"
                    />
                  </div>

                  <div>
                    <label className="label">Ville</label>
                    <input
                      type="text"
                      value={newDrop.drop_city}
                      onChange={e => setNewDrop({ ...newDrop, drop_city: e.target.value })}
                      className="input-field"
                      placeholder="Québec"
                    />
                  </div>

                  <div>
                    <label className="label">Code postal</label>
                    <input
                      type="text"
                      value={newDrop.drop_postal_code}
                      onChange={e => setNewDrop({ ...newDrop, drop_postal_code: e.target.value })}
                      className="input-field"
                      placeholder="G1R 5M9"
                    />
                  </div>

                  <div>
                    <label className="label">Province ou région</label>
                    <input
                      type="text"
                      value={newDrop.drop_region}
                      onChange={e => setNewDrop({ ...newDrop, drop_region: e.target.value })}
                      className="input-field"
                      placeholder="Québec"
                    />
                  </div>

                  <div>
                    <label className="label">Pays</label>
                    <input
                      type="text"
                      value={newDrop.drop_country}
                      onChange={e => setNewDrop({ ...newDrop, drop_country: e.target.value })}
                      className="input-field"
                      placeholder="Canada"
                    />
                  </div>

                  <div>
                    <label className="label">Rayon (mètres)</label>
                    <input
                      type="number"
                      min="10"
                      step="10"
                      value={newDrop.radius_meters}
                      onChange={e =>
                        setNewDrop({ ...newDrop, radius_meters: parseInt(e.target.value) || 50 })
                      }
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-6">
                  <button onClick={handleCreateDrop} disabled={saving} className="fx-btn fx-btn-primary">
                    {saving ? 'Création…' : 'Créer le drop'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reserve' && business && isForfeoReserveMerchantPortalEnabled() && (
            <div className="mx-auto max-w-7xl" data-commerce-v218-host="reserve-portal">
              <ReserveMerchantPortal
                businessId={business.id}
                businessName={business.name}
                onUpdate={fetchBusinessData}
              />
            </div>
          )}

          {activeTab === 'virtual-tour' && business && isForfeoReserveVirtualTourEnabled() && (
            <div className="mx-auto max-w-7xl" data-commerce-v218-host="virtual-tour">
              <ReserveVirtualTourManager
                businessId={business.id}
                businessName={business.name}
              />
            </div>
          )}

          {activeTab === 'integrations' && business && isForfeoReserveIntegrationsEnabled() && (
            <div className="mx-auto max-w-7xl" data-commerce-v218-host="integrations">
              <ReservePartnerIntegrationCenter
                businessId={business.id}
                businessName={business.name}
              />
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="commerce-reserve-calendar-v21 mx-auto w-full min-w-0 max-w-7xl overflow-x-auto" data-commerce-v218-host="calendar">
              <ReserveMerchantCalendar
                key={`reserve-${calendarKey}-${reserveAccommodationId || 'all'}`}
                businessId={business.id}
                preferredAccommodationId={reserveAccommodationId || undefined}
                onUpdate={fetchBusinessData}
              />
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="fx-chat-shell mx-auto h-[calc(100dvh-9rem)] min-h-0 w-full min-w-0 max-w-6xl overflow-hidden lg:h-[760px]" data-commerce-v218-host="crm">
              <ChatSystem userType="business" userId={business.owner_id} businessId={business.id} />
            </div>
          )}

          {activeTab === 'booking' && (
            <div className="commerce-reserve-booking-v21 mx-auto w-full min-w-0 max-w-7xl overflow-x-hidden" data-commerce-v218-host="booking-engine">
              {isForfeoReserveBookingEnabled() ? (
                <>
                  {isForfeoReservePolicyEnabled() && (
                    <ReserveCommercialPolicies
                      businessId={business.id}
                      onUpdate={fetchBusinessData}
                    />
                  )}
                  <ReserveBookingEngine
                    businessId={business.id}
                    onUpdate={fetchBusinessData}
                  />
                </>
              ) : (
                <div className="fx-card p-6 sm:p-8">
                  <h2 className="fx-page-title mb-6"><FaListAlt /> Réservations</h2>
                  {reservations.length > 0 ? (
                    <div className="fx-table-wrap">
                      <table className="fx-table">
                        <thead>
                          <tr>
                            <th>{tx('customer')}</th>
                            <th>Arrivée</th>
                            <th>Départ</th>
                            <th>{tx('status')}</th>
                            <th className="text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservations.map((res: any) => (
                            <tr key={res.id}>
                              <td>{res.guest_name || tx('anonymous')}</td>
                              <td>{new Date(res.start_at).toLocaleDateString(locale)}</td>
                              <td>{new Date(res.end_at).toLocaleDateString(locale)}</td>
                              <td>
                                <span
                                  className={`fx-badge ${
                                    res.status === 'confirmed'
                                      ? 'fx-badge-green'
                                      : res.status === 'blocked'
                                        ? 'fx-badge-slate'
                                        : 'fx-badge-amber'
                                  }`}
                                >
                                  {res.status}
                                </span>
                              </td>
                              <td className="text-right">
                                <button onClick={() => setSelectedRes(res)} className="fx-btn fx-btn-outline">
                                  Détails
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="fx-empty"><p>Aucune réservation pour le moment.</p></div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'lastcall' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <p className="fx-eyebrow">Dernière chance</p>
                <h2 className="fx-panel-title">Offres de dernière minute</h2>
                <p className="fx-panel-sub">Remplissez vos places libres avec une promotion à durée limitée.</p>
              </section>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="fx-card p-6">
                  <h3 className="fx-section-title mb-5">Créer une offre flash</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="label">Titre</label>
                      <input
                        type="text"
                        placeholder="-50 % ce soir"
                        value={newDeal.title}
                        onChange={e => setNewDeal({ ...newDeal, title: e.target.value })}
                        className="input-field"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Prix régulier</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={newDeal.original_price}
                          onChange={e => setNewDeal({ ...newDeal, original_price: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="label">Prix promo</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={newDeal.discount_price}
                          onChange={e => setNewDeal({ ...newDeal, discount_price: e.target.value })}
                          className="input-field fx-input-hot"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Quantité</label>
                        <input
                          type="number"
                          value={newDeal.quantity_available}
                          onChange={e =>
                            setNewDeal({ ...newDeal, quantity_available: parseInt(e.target.value) || 0 })
                          }
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="label">Durée (heures)</label>
                        <input
                          type="number"
                          value={newDeal.expires_in_hours}
                          onChange={e =>
                            setNewDeal({ ...newDeal, expires_in_hours: parseInt(e.target.value) || 24 })
                          }
                          className="input-field"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label">Description</label>
                      <textarea
                        placeholder="Ce que comprend l'offre…"
                        value={newDeal.description}
                        onChange={e => setNewDeal({ ...newDeal, description: e.target.value })}
                        className="input-field h-24 resize-none"
                      ></textarea>
                      <AiMagicButton
                        onClick={() => simulateAiGeneration('description', 'deal')}
                        loading={aiLoading.deal}
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => dealFileInputRef.current?.click()}
                        className="fx-dropzone h-16 w-16 shrink-0"
                      >
                        {newDeal.image_url ? <img src={newDeal.image_url} alt="" /> : <FaCamera />}
                      </button>
                      <input
                        type="file"
                        ref={dealFileInputRef}
                        onChange={e => handleUpload(e, 'deal')}
                        className="hidden"
                      />
                      <button
                        onClick={handleCreateDeal}
                        disabled={saving}
                        className="fx-btn fx-btn-hot flex-1"
                      >
                        {saving ? 'Publication…' : "Publier l'offre"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {deals.length === 0 && (
                    <div className="fx-empty"><p>Aucune offre flash en cours.</p></div>
                  )}
                  {deals.map((deal: any) => (
                    <article key={deal.id} className="fx-card flex gap-4 p-4">
                      <img
                        src={deal.image_url || 'https://via.placeholder.com/100'}
                        className="fx-thumb"
                        alt=""
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <h4 className="fx-row-title truncate">{deal.title}</h4>
                          <span className="fx-price-hot">{deal.discount_price} $</span>
                        </div>
                        <p className="fx-price-old">{deal.original_price} $</p>
                        <div className="fx-row-meta mt-2">
                          <span>
                            <FaClock />{' '}
                            {new Date(deal.expires_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          <span className="fx-badge fx-badge-slate">{deal.quantity_available} dispo</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete('last_call_deals', deal.id)}
                        className="fx-icon-btn fx-icon-btn-danger self-start"
                        title="Supprimer"
                      >
                        <FaTrash size={12} />
                      </button>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'shop' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="fx-eyebrow">Boutique en ligne</p>
                    <h2 className="fx-panel-title">Produits physiques</h2>
                    <p className="fx-panel-sub">Les commandes sont expédiées ou récupérées sur place.</p>
                  </div>
                  <span className="fx-badge fx-badge-blue"><FaShippingFast size={11} /> Livraison requise</span>
                </div>
              </section>

              <div className="fx-card p-6">
                <div className="flex flex-col items-start gap-4 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => productFileInputRef.current?.click()}
                    className="fx-dropzone h-24 w-24 shrink-0"
                  >
                    {newProduct.image_url ? (
                      <img src={newProduct.image_url} alt="" />
                    ) : (
                      <>
                        <FaCamera size={20} />
                        <span>Photo</span>
                      </>
                    )}
                  </button>
                  <input
                    type="file"
                    ref={productFileInputRef}
                    onChange={e => handleUpload(e, 'product')}
                    className="hidden"
                  />

                  <div className="grid w-full flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      placeholder="Nom du produit"
                      value={newProduct.name}
                      onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                      className="input-field"
                    />
                    <input
                      type="number"
                      placeholder="Prix ($)"
                      value={newProduct.price}
                      onChange={e => setNewProduct({ ...newProduct, price: e.target.value })}
                      className="input-field"
                    />
                    <div className="sm:col-span-2">
                      <input
                        type="text"
                        placeholder="Description courte…"
                        value={newProduct.description}
                        onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                        className="input-field"
                      />
                      <AiMagicButton
                        onClick={() => simulateAiGeneration('description', 'product')}
                        loading={aiLoading.product}
                      />
                    </div>
                  </div>

                  <button onClick={handleAddProduct} className="fx-btn fx-btn-primary w-full sm:w-auto">
                    <FaPlus size={12} /> Ajouter
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {products.map((p: any) => (
                  <article key={p.id} className="fx-card overflow-hidden">
                    <div className="fx-media fx-media-sm">
                      <img src={p.image_url || 'https://via.placeholder.com/150'} alt="" />
                      <button
                        onClick={() => handleDelete('shop_products', p.id)}
                        className="fx-media-remove"
                        aria-label="Supprimer"
                      >
                        <FaTrash size={11} />
                      </button>
                    </div>
                    <div className="p-4">
                      <h4 className="fx-row-title truncate">{p.name}</h4>
                      <p className="fx-price">{p.price} $</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'posts' && (
            <div className="mx-auto max-w-3xl">
              <div className="fx-card p-6 sm:p-8">
                <h2 className="fx-page-title mb-6"><FaBullhorn /> Fil d'actualité</h2>

                <div className="fx-composer">
                  <textarea
                    className="fx-composer-input"
                    rows={3}
                    placeholder="Quoi de neuf aujourd'hui ?"
                    value={newPost.content}
                    onChange={e => setNewPost({ ...newPost, content: e.target.value })}
                  ></textarea>

                  {newPost.image_url && (
                    <div className="relative mt-4">
                      <img src={newPost.image_url} className="h-48 w-full rounded-xl object-cover" alt="" />
                      <button
                        onClick={() => setNewPost({ ...newPost, image_url: '' })}
                        className="fx-media-remove"
                        aria-label="Retirer l'image"
                      >
                        <FaTimes size={11} />
                      </button>
                    </div>
                  )}

                  <div className="fx-composer-foot">
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => postFileInputRef.current?.click()}
                        className="fx-link"
                      >
                        <FaCamera /> Photo
                      </button>
                      <button
                        onClick={() => simulateAiGeneration('content', 'post')}
                        className="fx-link"
                      >
                        <FaMagic /> Rédiger avec l'IA
                      </button>
                      <input
                        type="file"
                        ref={postFileInputRef}
                        onChange={e => handleUpload(e, 'post')}
                        className="hidden"
                      />
                    </div>
                    <button onClick={handlePublishPost} disabled={isPosting} className="fx-btn fx-btn-primary">
                      {isPosting ? 'Publication…' : 'Publier'}
                    </button>
                  </div>
                </div>

                <div className="mt-8 space-y-6">
                  {posts.map((post: any) => (
                    <article key={post.id} className="fx-post">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src={formData.image_url || 'https://via.placeholder.com/40'}
                            className="fx-avatar"
                            alt=""
                          />
                          <div>
                            <p className="fx-row-title">{formData.name}</p>
                            <p className="fx-help">{new Date(post.created_at).toLocaleDateString(locale)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete('posts', post.id)}
                          className="fx-icon-btn fx-icon-btn-danger"
                          title="Supprimer"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                      <p className="fx-body mb-3">{post.content}</p>
                      {post.image_url && (
                        <img src={post.image_url} className="mb-3 w-full rounded-xl" alt="" />
                      )}
                      <div className="fx-row-meta">
                        <span><FaCheckDouble /> Vu par {toNum(post.views_count)}</span>
                      </div>

                      {post.comments && post.comments.length > 0 && (
                        <div className="fx-divider-top mt-4 space-y-3 pt-4">
                          <p className="label">Commentaires ({post.comments.length})</p>
                          {post.comments.map((comment: any) => (
                            <div key={comment.id} className="fx-comment">
                              <div className="flex-1">
                                <p className="fx-body">{comment.content}</p>
                                <p className="fx-help">
                                  {new Date(comment.created_at).toLocaleDateString(locale)} à{' '}
                                  {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <button
                                onClick={() => handleDelete('post_comments', comment.id)}
                                className="fx-icon-btn fx-icon-btn-danger"
                                title="Supprimer ce commentaire"
                              >
                                <FaTrash size={11} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'loyalty' && (
            <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
              <div className="fx-card p-6 sm:p-8">
                <h2 className="fx-page-title mb-6"><FaQrcode /> Ajouter des points</h2>
                <div className="fx-subcard p-6 text-center">
                  <FaQrcode size={44} className="fx-muted-icon" />
                  <p className="fx-body my-4">
                    Scannez le code du client, ou entrez son identifiant à la main.
                  </p>
                  <input
                    type="text"
                    placeholder="Identifiant du client"
                    value={loyaltyScan.id}
                    onChange={e => setLoyaltyScan({ ...loyaltyScan, id: e.target.value })}
                    className="input-field text-center font-mono"
                  />
                  <div className="my-4 flex items-center justify-center gap-4">
                    <button
                      onClick={() =>
                        setLoyaltyScan(prev => ({ ...prev, points: Math.max(5, prev.points - 5) }))
                      }
                      className="fx-step-btn"
                    >
                      −
                    </button>
                    <span className="fx-points">{loyaltyScan.points} pts</span>
                    <button
                      onClick={() => setLoyaltyScan(prev => ({ ...prev, points: prev.points + 5 }))}
                      className="fx-step-btn"
                    >
                      +
                    </button>
                  </div>
                  <button onClick={handleValidateLoyalty} className="fx-btn fx-btn-primary w-full">
                    Créditer les points
                  </button>
                  {loyaltyScan.message && <p className="fx-help mt-3">{loyaltyScan.message}</p>}
                </div>
              </div>

              <div className="fx-card p-6 sm:p-8">
                <h2 className="fx-page-title mb-6"><FaGift /> Récompenses</h2>
                <div className="mb-6 space-y-3">
                  {rewards.length === 0 && <p className="fx-empty-inline">Aucune récompense créée.</p>}
                  {rewards.map((r: any) => (
                    <div key={r.id} className="fx-list-item">
                      <div>
                        <p className="fx-row-title">{r.title}</p>
                        <p className="fx-row-sub">{r.cost_points} points</p>
                      </div>
                      <button
                        onClick={() => handleDelete('rewards', r.id)}
                        className="fx-icon-btn fx-icon-btn-danger"
                        title="Supprimer"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="fx-subcard p-5">
                  <h3 className="fx-section-title mb-4">Nouvelle récompense</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Café gratuit"
                      value={newReward.title}
                      onChange={e => setNewReward({ ...newReward, title: e.target.value })}
                      className="input-field"
                    />
                    <input
                      type="number"
                      placeholder="Coût en points"
                      value={newReward.cost}
                      onChange={e => setNewReward({ ...newReward, cost: parseInt(e.target.value) || 0 })}
                      className="input-field"
                    />
                    <button onClick={handleAddReward} className="fx-btn fx-btn-primary w-full">
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="mx-auto max-w-5xl space-y-6">
              <section className="fx-panel-hero">
                <p className="fx-eyebrow">Statistiques</p>
                <h2 className="fx-panel-title">Activité de votre fiche</h2>
                <p className="fx-panel-sub">Données en temps réel sur Forfeo Explore.</p>
              </section>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="fx-stat">
                  <span className="fx-stat-icon fx-tone-blue"><FaEye size={16} /></span>
                  <p className="fx-stat-label">Vues de la fiche</p>
                  <p className="fx-stat-value">{stats.views}</p>
                </div>
                <div className="fx-stat">
                  <span className="fx-stat-icon fx-tone-violet"><FaMousePointer size={16} /></span>
                  <p className="fx-stat-label">Interactions</p>
                  <p className="fx-stat-value">{stats.clicks}</p>
                </div>
                <div className="fx-stat">
                  <span className="fx-stat-icon fx-tone-green"><FaCoins size={16} /></span>
                  <p className="fx-stat-label">Chiffre d'affaires</p>
                  <p className="fx-stat-value">{stats.revenue.toFixed(2)} $</p>
                </div>
              </div>

              <div className="fx-card p-6 sm:p-8">
                <h3 className="fx-section-title mb-6"><FaChartBar /> Sept derniers jours</h3>
                <div className="fx-chart">
                  {[40, 60, 35, 70, 50, 80, 95].map((h, i) => (
                    <div key={i} className="fx-chart-col">
                      <div className="fx-chart-bar" style={{ height: `${h}%` }}>
                        <span className="fx-chart-tip">{h * 12}</span>
                      </div>
                      <span className="fx-chart-label">J-{6 - i}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <style>{`
/* =========================================================
   FORFEO EXPLORE — Design system du tableau de bord marchand
   Aligné sur la page publique de réservation (clair, sobre).
   Portée : .business-pixel-shell-v4r2.fx-shell (spécificité
   volontairement élevée pour neutraliser tout thème sombre
   hérité d'un CSS global).
   ========================================================= */

.business-pixel-shell-v4r2.fx-shell {
  --fx-bg: #f4f6fa;
  --fx-surface: #ffffff;
  --fx-surface-2: #f8fafc;
  --fx-border: #e5e9f0;
  --fx-border-strong: #d7dee9;
  --fx-ink: #0f1729;
  --fx-ink-2: #475467;
  --fx-muted: #667085;
  --fx-primary: #2c6bf6;
  --fx-primary-dark: #1f56d3;
  --fx-primary-soft: #eef3ff;
  --fx-green: #067647;
  --fx-green-soft: #ecfdf3;
  --fx-red: #c4320a;
  --fx-red-soft: #fef3f2;
  --fx-amber: #b54708;
  --fx-amber-soft: #fffaeb;
  --fx-violet: #6941c6;
  --fx-violet-soft: #f4f3ff;
  --fx-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 8px 24px -16px rgba(16, 24, 40, .18);
  --fx-radius: 16px;

  background: var(--fx-bg) !important;
  color: var(--fx-ink) !important;
  -webkit-font-smoothing: antialiased;
}

.business-pixel-shell-v4r2.fx-shell *,
.business-pixel-shell-v4r2.fx-shell *::before,
.business-pixel-shell-v4r2.fx-shell *::after { box-sizing: border-box; }

.business-pixel-shell-v4r2.fx-shell h1,
.business-pixel-shell-v4r2.fx-shell h2,
.business-pixel-shell-v4r2.fx-shell h3,
.business-pixel-shell-v4r2.fx-shell h4,
.business-pixel-shell-v4r2.fx-shell h5,
.business-pixel-shell-v4r2.fx-shell p,
.business-pixel-shell-v4r2.fx-shell span,
.business-pixel-shell-v4r2.fx-shell td,
.business-pixel-shell-v4r2.fx-shell th,
.business-pixel-shell-v4r2.fx-shell strong { color: inherit; }

/* ---------- Chargement ---------- */
.fx-loading {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: #f4f6fa;
}
.fx-spinner {
  width: 34px; height: 34px; border-radius: 999px;
  border: 3px solid #dbe3f2; border-top-color: #2c6bf6;
  animation: fxspin .8s linear infinite;
}
@keyframes fxspin { to { transform: rotate(360deg); } }

/* ---------- Typographie ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-eyebrow {
  font-size: 12px; font-weight: 600; color: var(--fx-primary) !important; margin: 0 0 2px;
}
.business-pixel-shell-v4r2.fx-shell .fx-page-title {
  font-size: 22px; font-weight: 700; letter-spacing: -.01em;
  color: var(--fx-ink) !important; display: flex; align-items: center; gap: 10px;
}
.business-pixel-shell-v4r2.fx-shell .fx-page-title svg { color: var(--fx-primary); }
.business-pixel-shell-v4r2.fx-shell .fx-group-title {
  font-size: 16px; font-weight: 700; color: var(--fx-ink) !important;
  margin: 0 0 16px; display: flex; align-items: center; gap: 8px;
}
.business-pixel-shell-v4r2.fx-shell .fx-group-title svg { color: var(--fx-primary); }
.business-pixel-shell-v4r2.fx-shell .fx-section-title {
  font-size: 15px; font-weight: 700; color: var(--fx-ink) !important;
  display: flex; align-items: center; gap: 8px;
}
.business-pixel-shell-v4r2.fx-shell .fx-section-title svg { color: var(--fx-primary); }
.business-pixel-shell-v4r2.fx-shell .fx-body {
  font-size: 14px; line-height: 1.6; color: var(--fx-ink-2) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-help {
  font-size: 12px; color: var(--fx-muted) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--fx-primary) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-text-green { color: var(--fx-green) !important; }
.business-pixel-shell-v4r2.fx-shell .fx-muted-icon { color: #cbd5e1; margin: 0 auto; display: block; }

.business-pixel-shell-v4r2.fx-shell .label {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--fx-ink-2) !important; margin-bottom: 6px;
  text-transform: none; letter-spacing: 0;
}

/* ---------- Champs ---------- */
.business-pixel-shell-v4r2.fx-shell .input-field {
  width: 100%; padding: 11px 14px; font-size: 14px; font-weight: 500;
  background: var(--fx-surface) !important; color: var(--fx-ink) !important;
  border: 1px solid var(--fx-border-strong) !important; border-radius: 10px;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .input-field::placeholder { color: #98a2b3 !important; }
.business-pixel-shell-v4r2.fx-shell .input-field:focus {
  outline: none; border-color: var(--fx-primary) !important;
  box-shadow: 0 0 0 3px rgba(44, 107, 246, .14);
}
.business-pixel-shell-v4r2.fx-shell select.input-field { appearance: auto; }
.business-pixel-shell-v4r2.fx-shell .fx-input-accent {
  background: var(--fx-primary-soft) !important; border-color: #c9daff !important;
  color: var(--fx-primary-dark) !important; font-weight: 700;
}
.business-pixel-shell-v4r2.fx-shell .fx-input-hot {
  background: var(--fx-red-soft) !important; border-color: #fcd7cd !important;
  color: var(--fx-red) !important; font-weight: 700;
}
.business-pixel-shell-v4r2.fx-shell .fx-inline-input {
  width: 100%; background: transparent !important; border: 0; padding: 0 34px 0 0;
  font-size: 14px; font-weight: 600; color: var(--fx-ink) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-inline-input:focus { outline: none; }
.business-pixel-shell-v4r2.fx-shell .fx-input-icon { position: relative; }
.business-pixel-shell-v4r2.fx-shell .fx-input-icon > svg {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  color: #98a2b3; pointer-events: none;
}
.business-pixel-shell-v4r2.fx-shell .fx-input-icon .input-field { padding-left: 38px; }

.business-pixel-shell-v4r2.fx-shell .fx-code-input {
  width: 100%; text-align: center; padding: 14px; border-radius: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 18px; font-weight: 700; letter-spacing: .08em;
  background: var(--fx-surface-2) !important; color: var(--fx-ink) !important;
  border: 1px solid var(--fx-border-strong) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-code-input:focus {
  outline: none; background: #fff !important; border-color: var(--fx-primary) !important;
  box-shadow: 0 0 0 3px rgba(44, 107, 246, .14);
}

.business-pixel-shell-v4r2.fx-shell .fx-check-row {
  display: flex; align-items: center; gap: 10px; padding: 11px 14px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 10px; font-size: 14px; font-weight: 500; color: var(--fx-ink-2) !important;
  cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .fx-check-line {
  display: flex; align-items: center; gap: 10px; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .fx-check-box {
  width: 18px; height: 18px; border-radius: 5px; flex: none;
  border: 1px solid var(--fx-border-strong); background: #fff;
  display: flex; align-items: center; justify-content: center; color: #fff;
  transition: all .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-check-box.is-on {
  background: var(--fx-primary); border-color: var(--fx-primary);
}
.business-pixel-shell-v4r2.fx-shell .fx-check-text {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 14px; color: var(--fx-ink-2) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-check-text.is-on { color: var(--fx-primary-dark) !important; font-weight: 600; }
.business-pixel-shell-v4r2.fx-shell .fx-check-text svg { color: #98a2b3; }
.business-pixel-shell-v4r2.fx-shell .fx-check-text.is-on svg { color: var(--fx-primary); }

/* ---------- Boutons ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 16px; border-radius: 10px; border: 1px solid transparent;
  font-size: 14px; font-weight: 600; line-height: 1.2; cursor: pointer;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
  text-decoration: none; white-space: nowrap;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn:disabled { opacity: .5; cursor: not-allowed; }
.business-pixel-shell-v4r2.fx-shell .fx-btn-primary {
  background: var(--fx-primary) !important; color: #fff !important; border-color: var(--fx-primary) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-primary:hover:not(:disabled) { background: var(--fx-primary-dark) !important; }
.business-pixel-shell-v4r2.fx-shell .fx-btn-dark {
  background: #0b2a57 !important; color: #fff !important; border-color: #0b2a57 !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-dark:hover:not(:disabled) { background: #123a73 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-btn-success {
  background: #067647 !important; color: #fff !important; border-color: #067647 !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-hot {
  background: #d9480f !important; color: #fff !important; border-color: #d9480f !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-outline {
  background: var(--fx-surface) !important; color: var(--fx-ink) !important;
  border-color: var(--fx-border-strong) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-outline:hover:not(:disabled) {
  background: var(--fx-primary-soft) !important; border-color: #c9daff !important; color: var(--fx-primary-dark) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-ghost {
  background: rgba(255,255,255,.7) !important; color: inherit !important; padding: 6px 12px; font-size: 13px;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-danger-soft {
  background: var(--fx-red-soft) !important; color: var(--fx-red) !important; border-color: #fcd7cd !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-btn-quiet-danger {
  background: var(--fx-surface) !important; color: var(--fx-red) !important; border-color: #fcd7cd !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-icon-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px; flex: none;
  background: var(--fx-surface) !important; color: var(--fx-ink-2) !important;
  border: 1px solid var(--fx-border-strong) !important; cursor: pointer;
  transition: all .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-icon-btn:hover {
  color: var(--fx-primary) !important; border-color: #c9daff !important; background: var(--fx-primary-soft) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-icon-btn-danger:hover {
  color: var(--fx-red) !important; border-color: #fcd7cd !important; background: var(--fx-red-soft) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-link {
  display: inline-flex; align-items: center; gap: 6px; background: none; border: 0; padding: 0;
  font-size: 13px; font-weight: 600; color: var(--fx-primary) !important; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .fx-link:hover { color: var(--fx-primary-dark) !important; }
.business-pixel-shell-v4r2.fx-shell .fx-step-btn {
  width: 32px; height: 32px; border-radius: 999px; font-size: 16px; font-weight: 700;
  background: var(--fx-surface) !important; color: var(--fx-ink) !important;
  border: 1px solid var(--fx-border-strong) !important; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .fx-add-row {
  width: 100%; padding: 12px; border-radius: 10px; cursor: pointer;
  border: 1px dashed var(--fx-border-strong) !important; background: var(--fx-surface) !important;
  color: var(--fx-ink-2) !important; font-size: 14px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.business-pixel-shell-v4r2.fx-shell .fx-add-row:hover {
  border-color: var(--fx-primary) !important; color: var(--fx-primary) !important; background: var(--fx-primary-soft) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-ai-btn {
  margin-top: 8px; display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 600; cursor: pointer;
  background: var(--fx-violet-soft) !important; color: var(--fx-violet) !important;
  border: 1px solid #e4e0fb !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-ai-btn:disabled { opacity: .55; }

/* ---------- Structure ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-card {
  background: var(--fx-surface) !important;
  border: 1px solid var(--fx-border) !important;
  border-radius: var(--fx-radius);
  box-shadow: var(--fx-shadow);
}
.business-pixel-shell-v4r2.fx-shell .fx-card-head {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 18px 20px; border-bottom: 1px solid var(--fx-border) !important;
  background: var(--fx-surface-2) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-card-title {
  font-size: 15px; font-weight: 700; color: var(--fx-ink) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-card-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-subcard {
  background: var(--fx-surface-2) !important;
  border: 1px solid var(--fx-border) !important;
  border-radius: 12px; padding: 16px;
}
.business-pixel-shell-v4r2.fx-shell .fx-subcard-title {
  font-size: 13px; font-weight: 700; color: var(--fx-ink) !important; margin-bottom: 10px;
}
.business-pixel-shell-v4r2.fx-shell .fx-divider-top { border-top: 1px solid var(--fx-border) !important; }
.business-pixel-shell-v4r2.fx-shell .fx-backbar { display: flex; }

.business-pixel-shell-v4r2.fx-shell .fx-panel-hero {
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%) !important;
  border: 1px solid var(--fx-border) !important;
  border-radius: var(--fx-radius); padding: 24px;
  box-shadow: var(--fx-shadow);
}
.business-pixel-shell-v4r2.fx-shell .fx-panel-title {
  font-size: 22px; font-weight: 700; letter-spacing: -.015em; color: var(--fx-ink) !important; margin: 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-panel-sub {
  font-size: 14px; color: var(--fx-muted) !important; margin: 6px 0 0; max-width: 62ch;
}

/* ---------- En-tête ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-hero {
  display: flex; flex-direction: column; gap: 20px;
  background: var(--fx-surface) !important;
  border: 1px solid var(--fx-border) !important;
  border-radius: 20px; padding: 24px; box-shadow: var(--fx-shadow);
}
@media (min-width: 900px) {
  .business-pixel-shell-v4r2.fx-shell .fx-hero { flex-direction: row; align-items: center; justify-content: space-between; }
}
.business-pixel-shell-v4r2.fx-shell .fx-hero-main { display: flex; align-items: center; gap: 18px; min-width: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-hero-logo {
  position: relative; width: 76px; height: 76px; border-radius: 16px; overflow: hidden; flex: none;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  cursor: pointer; color: #98a2b3; display: flex; align-items: center; justify-content: center;
  padding: 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-hero-logo img { width: 100%; height: 100%; object-fit: cover; }
.business-pixel-shell-v4r2.fx-shell .fx-hero-logo-overlay {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(15, 23, 42, .55); color: #fff; font-size: 12px; font-weight: 600;
  opacity: 0; transition: opacity .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-hero-logo:hover .fx-hero-logo-overlay { opacity: 1; }
.business-pixel-shell-v4r2.fx-shell .fx-hero-title {
  font-size: 28px; font-weight: 700; letter-spacing: -.02em; color: var(--fx-ink) !important; margin: 0;
  overflow-wrap: anywhere;
}
.business-pixel-shell-v4r2.fx-shell .fx-hero-sub {
  font-size: 14px; color: var(--fx-muted) !important; margin: 4px 0 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-hero-actions {
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
}

.business-pixel-shell-v4r2.fx-shell .fx-segmented {
  display: inline-flex; gap: 4px; padding: 4px; border-radius: 10px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-segmented-item {
  display: inline-flex; align-items: center; gap: 7px; padding: 7px 14px; border-radius: 8px;
  font-size: 13px; font-weight: 600; border: 0; cursor: pointer;
  background: transparent !important; color: var(--fx-muted) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-segmented-item.is-active {
  background: var(--fx-surface) !important; color: var(--fx-ink) !important;
  box-shadow: 0 1px 3px rgba(16,24,40,.1);
}
.business-pixel-shell-v4r2.fx-shell .fx-segmented-sm .fx-segmented-item { padding: 5px 10px; font-size: 12px; }

.business-pixel-shell-v4r2.fx-shell .fx-tabs {
  display: inline-flex; flex-wrap: wrap; gap: 4px; padding: 4px; border-radius: 10px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-tab {
  display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border-radius: 8px;
  font-size: 14px; font-weight: 600; border: 0; cursor: pointer;
  background: transparent !important; color: var(--fx-muted) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-tab.is-active {
  background: var(--fx-surface) !important; color: var(--fx-primary) !important;
  box-shadow: 0 1px 3px rgba(16,24,40,.1);
}

/* ---------- Statistiques ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-stat {
  background: var(--fx-surface) !important;
  border: 1px solid var(--fx-border) !important;
  border-radius: var(--fx-radius); padding: 18px; height: 100%;
  box-shadow: var(--fx-shadow);
}
.business-pixel-shell-v4r2.fx-shell .fx-stat-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px; margin-bottom: 12px;
}
.business-pixel-shell-v4r2.fx-shell .fx-stat-label {
  font-size: 13px; font-weight: 500; color: var(--fx-muted) !important; margin: 0 0 4px;
}
.business-pixel-shell-v4r2.fx-shell .fx-stat-value {
  font-size: 26px; font-weight: 700; letter-spacing: -.02em; color: var(--fx-ink) !important;
  margin: 0; display: flex; align-items: center; gap: 8px;
}
.business-pixel-shell-v4r2.fx-shell .fx-stat-link {
  display: block; width: 100%; padding: 0; border: 0; background: none; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .fx-stat-link:hover .fx-stat { border-color: #c9daff !important; }

.business-pixel-shell-v4r2.fx-shell .fx-kpi { text-align: right; }
.business-pixel-shell-v4r2.fx-shell .fx-kpi-value {
  display: block; font-size: 30px; font-weight: 700; color: var(--fx-primary) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-kpi-label { font-size: 13px; color: var(--fx-muted) !important; }

/* ---------- Tuiles du menu ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-tile {
  position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 14px;
  padding: 20px; border-radius: var(--fx-radius); cursor: pointer; text-align: left;
  background: var(--fx-surface) !important;
  border: 1px solid var(--fx-border) !important;
  box-shadow: var(--fx-shadow);
  transition: border-color .15s ease, transform .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-tile:hover {
  border-color: #c9daff !important; transform: translateY(-2px);
}
.business-pixel-shell-v4r2.fx-shell .fx-tile-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 42px; height: 42px; border-radius: 11px; font-size: 18px;
}
.business-pixel-shell-v4r2.fx-shell .fx-tile-label {
  font-size: 14.5px; font-weight: 600; line-height: 1.35; color: var(--fx-ink) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-tile-count {
  position: absolute; top: 14px; right: 14px; min-width: 22px; height: 22px; padding: 0 7px;
  border-radius: 999px; background: #d92d20 !important; color: #fff !important;
  font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
}

/* Teintes d'icônes */
.business-pixel-shell-v4r2.fx-shell .fx-tone-blue { background: #eef3ff !important; color: #2c6bf6 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-indigo { background: #eef2ff !important; color: #4f46e5 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-violet { background: #f4f3ff !important; color: #6941c6 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-purple { background: #f4f3ff !important; color: #6941c6 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-cyan { background: #ecfdff !important; color: #0e7490 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-green { background: #ecfdf3 !important; color: #067647 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-amber { background: #fffaeb !important; color: #b54708 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-yellow { background: #fffaeb !important; color: #b54708 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-orange { background: #fff4ed !important; color: #c4320a !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-red { background: #fef3f2 !important; color: #c4320a !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-pink { background: #fdf2fa !important; color: #c11574 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-tone-slate { background: #f2f4f7 !important; color: #475467 !important; }

/* ---------- Scanner rapide ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta {
  background: linear-gradient(135deg, #2c6bf6 0%, #4f46e5 100%) !important;
  border: 0; cursor: pointer; box-shadow: 0 10px 24px -14px rgba(44,107,246,.9);
}
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 46px; height: 46px; border-radius: 12px;
  background: rgba(255,255,255,.18); color: #fff;
}
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta-title {
  display: block; font-size: 16px; font-weight: 700; color: #fff !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta-sub {
  display: block; font-size: 13px; color: rgba(255,255,255,.82) !important; margin-top: 2px;
}
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta-expand { color: rgba(255,255,255,.6); }
.business-pixel-shell-v4r2.fx-shell .fx-scan-cta:hover .fx-scan-cta-expand { color: #fff; }
.business-pixel-shell-v4r2.fx-shell .fx-head-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 9px;
}
.business-pixel-shell-v4r2.fx-shell .fx-camera-wrap {
  border: 1px solid var(--fx-border) !important; background: var(--fx-surface-2) !important;
  border-radius: 14px; padding: 10px;
}
.business-pixel-shell-v4r2.fx-shell .fx-camera-hint { font-size: 13px; font-weight: 600; color: #101828 !important; margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-camera-sub { font-size: 11.5px; color: #667085 !important; margin: 2px 0 0; }
.business-pixel-shell-v4r2.fx-shell .fx-camera-msg { margin-top: 10px; text-align: center; font-size: 13px; color: var(--fx-ink-2) !important; }

/* ---------- Notes, badges, listes ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-note {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px; border-radius: 12px; border: 1px solid transparent;
}
.business-pixel-shell-v4r2.fx-shell .fx-note-title { font-size: 14px; font-weight: 700; margin: 0 0 2px; }
.business-pixel-shell-v4r2.fx-shell .fx-note-text { font-size: 13.5px; line-height: 1.55; margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-note-success { background: var(--fx-green-soft) !important; border-color: #abefc6 !important; color: #05603a !important; }
.business-pixel-shell-v4r2.fx-shell .fx-note-error   { background: var(--fx-red-soft) !important; border-color: #fecdca !important; color: #912018 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-note-warning { background: var(--fx-amber-soft) !important; border-color: #fedf89 !important; color: #93370d !important; }

.business-pixel-shell-v4r2.fx-shell .fx-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600;
  border: 1px solid transparent; white-space: nowrap;
}
.business-pixel-shell-v4r2.fx-shell .fx-badge-blue   { background: #eef3ff !important; color: #1f56d3 !important; border-color: #d5e2fd !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-green  { background: #ecfdf3 !important; color: #067647 !important; border-color: #abefc6 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-amber  { background: #fffaeb !important; color: #b54708 !important; border-color: #fedf89 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-red    { background: #fef3f2 !important; color: #b42318 !important; border-color: #fecdca !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-slate  { background: #f2f4f7 !important; color: #475467 !important; border-color: #e4e7ec !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-violet { background: #f4f3ff !important; color: #5925dc !important; border-color: #e4e0fb !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-teal   { background: #f0fdf9 !important; color: #107569 !important; border-color: #99f6e0 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-badge-gold   { background: #fffaeb !important; color: #a15c07 !important; border-color: #fedf89 !important; }

.business-pixel-shell-v4r2.fx-shell .fx-row {
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 12px; padding: 18px;
}
.business-pixel-shell-v4r2.fx-shell .fx-row-title { font-size: 15px; font-weight: 600; color: var(--fx-ink) !important; margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-row-sub { font-size: 13.5px; color: var(--fx-muted) !important; margin: 2px 0 0; }
.business-pixel-shell-v4r2.fx-shell .fx-row-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 14px; margin-top: 10px;
  font-size: 12.5px; color: var(--fx-muted) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-row-meta span { display: inline-flex; align-items: center; gap: 6px; }

.business-pixel-shell-v4r2.fx-shell .fx-list-item {
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 14px 16px; border-radius: 12px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-mini {
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 10px; padding: 12px 14px;
}
.business-pixel-shell-v4r2.fx-shell .fx-mini-label { font-size: 12px; color: var(--fx-muted) !important; margin: 0 0 3px; font-weight: 500; }
.business-pixel-shell-v4r2.fx-shell .fx-mini-value { font-size: 14px; font-weight: 600; color: var(--fx-ink) !important; margin: 0; }

.business-pixel-shell-v4r2.fx-shell .fx-kv {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 14px; color: var(--fx-muted) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-kv strong { color: var(--fx-ink) !important; font-weight: 600; }
.business-pixel-shell-v4r2.fx-shell .fx-kv-total {
  border-top: 1px solid var(--fx-border) !important; padding-top: 14px; font-size: 16px;
}
.business-pixel-shell-v4r2.fx-shell .fx-kv-total strong { color: var(--fx-green) !important; font-weight: 700; }

.business-pixel-shell-v4r2.fx-shell .fx-empty {
  text-align: center; padding: 40px 20px; border-radius: 14px;
  border: 1px dashed var(--fx-border-strong) !important; background: var(--fx-surface-2) !important;
  color: var(--fx-muted) !important; font-size: 14px;
}
.business-pixel-shell-v4r2.fx-shell .fx-empty svg { color: #cbd5e1; margin: 0 auto 12px; display: block; }
.business-pixel-shell-v4r2.fx-shell .fx-empty p { margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-empty-sub { font-size: 13px; color: #98a2b3 !important; margin-top: 6px !important; }
.business-pixel-shell-v4r2.fx-shell .fx-empty-inline {
  text-align: center; padding: 16px; border-radius: 10px;
  background: var(--fx-surface-2) !important; color: var(--fx-muted) !important; font-size: 13.5px;
}
.business-pixel-shell-v4r2.fx-shell .fx-quote {
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 10px; padding: 14px; font-size: 14px; line-height: 1.6; color: var(--fx-ink-2) !important;
}

/* ---------- Tableaux ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-table-wrap { overflow-x: auto; }
.business-pixel-shell-v4r2.fx-shell .fx-table { width: 100%; border-collapse: collapse; text-align: left; }
.business-pixel-shell-v4r2.fx-shell .fx-table th {
  font-size: 12.5px; font-weight: 600; color: var(--fx-muted) !important;
  padding: 12px 10px; border-bottom: 1px solid var(--fx-border) !important; white-space: nowrap;
}
.business-pixel-shell-v4r2.fx-shell .fx-table td {
  font-size: 14px; color: var(--fx-ink) !important; padding: 14px 10px;
  border-bottom: 1px solid #f1f3f7 !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-table tbody tr:hover td { background: var(--fx-surface-2) !important; }

/* ---------- Médias ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-media { position: relative; height: 180px; }
.business-pixel-shell-v4r2.fx-shell .fx-media-sm { height: 128px; }
.business-pixel-shell-v4r2.fx-shell .fx-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.business-pixel-shell-v4r2.fx-shell .fx-media-chip {
  position: absolute; top: 12px; left: 12px; display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border-radius: 999px; font-size: 12px; font-weight: 600;
  background: rgba(255,255,255,.94) !important; color: #101828 !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-media-action {
  position: absolute; bottom: 12px; right: 12px; display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 11px; border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer; border: 0;
  background: rgba(16,24,40,.86) !important; color: #fff !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-media-remove {
  position: absolute; top: 10px; right: 10px; width: 28px; height: 28px; border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  background: rgba(255,255,255,.94) !important; color: #b42318 !important; border: 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-thumb {
  width: 76px; height: 76px; border-radius: 10px; object-fit: cover; flex: none;
  background: var(--fx-surface-2); border: 1px solid var(--fx-border);
}
.business-pixel-shell-v4r2.fx-shell .fx-avatar {
  width: 40px; height: 40px; border-radius: 999px; object-fit: cover;
  border: 1px solid var(--fx-border);
}
.business-pixel-shell-v4r2.fx-shell .fx-photo { position: relative; aspect-ratio: 1/1; border-radius: 10px; overflow: hidden; border: 1px solid var(--fx-border); }
.business-pixel-shell-v4r2.fx-shell .fx-photo img { width: 100%; height: 100%; object-fit: cover; }
.business-pixel-shell-v4r2.fx-shell .fx-photo-remove {
  position: absolute; top: 5px; right: 5px; width: 22px; height: 22px; border-radius: 999px; border: 0;
  background: rgba(16,24,40,.75) !important; color: #fff !important; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.business-pixel-shell-v4r2.fx-shell .fx-dropzone {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
  border-radius: 12px; cursor: pointer; overflow: hidden; padding: 10px; text-align: center;
  background: var(--fx-surface-2) !important; color: var(--fx-muted) !important;
  border: 1px dashed var(--fx-border-strong) !important;
  font-size: 12.5px; font-weight: 500;
}
.business-pixel-shell-v4r2.fx-shell .fx-dropzone:hover {
  border-color: var(--fx-primary) !important; color: var(--fx-primary) !important; background: var(--fx-primary-soft) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-dropzone img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; }

.business-pixel-shell-v4r2.fx-shell .fx-card-split { display: flex; overflow: hidden; height: 148px; }
.business-pixel-shell-v4r2.fx-shell .fx-split-media { position: relative; width: 36%; flex: none; }
.business-pixel-shell-v4r2.fx-shell .fx-split-media img { width: 100%; height: 100%; object-fit: cover; }
.business-pixel-shell-v4r2.fx-shell .fx-split-body {
  flex: 1; padding: 14px 16px; display: flex; flex-direction: column; justify-content: space-between; min-width: 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-discount {
  position: absolute; top: 8px; left: 8px; padding: 3px 8px; border-radius: 6px;
  background: #d92d20 !important; color: #fff !important; font-size: 11px; font-weight: 700;
}

.business-pixel-shell-v4r2.fx-shell .fx-price { font-size: 18px; font-weight: 700; color: var(--fx-primary) !important; margin: 2px 0 0; }
.business-pixel-shell-v4r2.fx-shell .fx-price-hot { font-size: 16px; font-weight: 700; color: #d9480f !important; }
.business-pixel-shell-v4r2.fx-shell .fx-price-old { font-size: 12.5px; color: #98a2b3 !important; text-decoration: line-through; margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-points { font-size: 22px; font-weight: 700; color: var(--fx-primary) !important; }

.business-pixel-shell-v4r2.fx-shell .fx-stars { display: inline-flex; gap: 2px; color: #d0d5dd; }
.business-pixel-shell-v4r2.fx-shell .fx-stars .is-on { color: #f79009; }

.business-pixel-shell-v4r2.fx-shell .fx-highlight {
  background: var(--fx-amber-soft) !important; border: 1px solid #fedf89 !important;
  border-radius: 12px; padding: 14px;
}
.business-pixel-shell-v4r2.fx-shell .fx-highlight .label { color: var(--fx-amber) !important; display: flex; align-items: center; gap: 7px; }

.business-pixel-shell-v4r2.fx-shell .fx-ai-box {
  background: var(--fx-violet-soft) !important; border: 1px solid #e4e0fb !important;
  border-radius: 12px; padding: 16px;
}
.business-pixel-shell-v4r2.fx-shell .fx-ai-box .label { color: var(--fx-violet) !important; }

.business-pixel-shell-v4r2.fx-shell .fx-action-panel {
  background: #101828 !important; color: #fff !important; border-radius: 14px; padding: 20px;
  align-self: flex-start; width: 100%;
}
.business-pixel-shell-v4r2.fx-shell .fx-action-panel .fx-mini-label { color: rgba(255,255,255,.6) !important; }
.business-pixel-shell-v4r2.fx-shell .fx-action-text {
  font-size: 13.5px; line-height: 1.6; color: rgba(255,255,255,.78) !important; margin: 10px 0 16px;
}

.business-pixel-shell-v4r2.fx-shell .fx-choice {
  display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 12px; cursor: pointer;
  text-align: left; background: var(--fx-surface) !important;
  border: 1px solid var(--fx-border-strong) !important; color: var(--fx-ink) !important;
  transition: all .15s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-choice:hover { border-color: #c9daff !important; }
.business-pixel-shell-v4r2.fx-shell .fx-choice.is-active {
  border-color: var(--fx-primary) !important; background: var(--fx-primary-soft) !important; color: var(--fx-primary-dark) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-choice-icon { font-size: 18px; color: var(--fx-primary); }
.business-pixel-shell-v4r2.fx-shell .fx-choice-label { font-size: 13.5px; font-weight: 600; }

.business-pixel-shell-v4r2.fx-shell .fx-switch-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 11px 14px; border-radius: 10px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-switch-label {
  display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; color: var(--fx-ink-2) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-switch-label svg { color: #98a2b3; }

.business-pixel-shell-v4r2.fx-shell .toggle {
  width: 42px; height: 24px; background: #d0d5dd; border-radius: 999px; position: relative;
  transition: background .2s ease;
}
.business-pixel-shell-v4r2.fx-shell .toggle-on { background: var(--fx-primary); }
.business-pixel-shell-v4r2.fx-shell .toggle-knob {
  width: 20px; height: 20px; background: #fff; border-radius: 999px; position: absolute;
  top: 2px; left: 2px; transition: left .2s ease; box-shadow: 0 1px 3px rgba(16,24,40,.2);
}
.business-pixel-shell-v4r2.fx-shell .toggle-on .toggle-knob { left: 20px; }

/* ---------- Publications ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-composer {
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 14px; padding: 16px;
}
.business-pixel-shell-v4r2.fx-shell .fx-composer-input {
  width: 100%; background: transparent !important; border: 0; resize: none;
  font-size: 16px; color: var(--fx-ink) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-composer-input:focus { outline: none; }
.business-pixel-shell-v4r2.fx-shell .fx-composer-input::placeholder { color: #98a2b3 !important; }
.business-pixel-shell-v4r2.fx-shell .fx-composer-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--fx-border) !important;
}
.business-pixel-shell-v4r2.fx-shell .fx-post {
  border-bottom: 1px solid var(--fx-border) !important; padding-bottom: 22px;
}
.business-pixel-shell-v4r2.fx-shell .fx-post:last-child { border-bottom: 0; padding-bottom: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-comment {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 12px 14px; border-radius: 10px;
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
}

/* ---------- Graphique ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-chart {
  display: flex; align-items: flex-end; gap: 10px; height: 240px; padding-bottom: 24px;
}
.business-pixel-shell-v4r2.fx-shell .fx-chart-col {
  position: relative; flex: 1; height: 100%; display: flex; align-items: flex-end;
}
.business-pixel-shell-v4r2.fx-shell .fx-chart-bar {
  position: relative; width: 100%; border-radius: 8px 8px 4px 4px;
  background: linear-gradient(180deg, #4d86f8 0%, #a9c5fd 100%);
  transition: filter .2s ease;
}
.business-pixel-shell-v4r2.fx-shell .fx-chart-col:hover .fx-chart-bar { filter: saturate(1.25); }
.business-pixel-shell-v4r2.fx-shell .fx-chart-tip {
  position: absolute; top: -28px; left: 50%; transform: translateX(-50%);
  padding: 3px 9px; border-radius: 7px; font-size: 12px; font-weight: 600;
  background: #101828 !important; color: #fff !important; opacity: 0; transition: opacity .15s ease;
  white-space: nowrap;
}
.business-pixel-shell-v4r2.fx-shell .fx-chart-col:hover .fx-chart-tip { opacity: 1; }
.business-pixel-shell-v4r2.fx-shell .fx-chart-label {
  position: absolute; bottom: -22px; left: 50%; transform: translateX(-50%);
  font-size: 12px; color: var(--fx-muted) !important;
}

/* ---------- Fenêtres modales ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-modal-backdrop {
  position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(15, 23, 42, .5); backdrop-filter: blur(3px);
}
.business-pixel-shell-v4r2.fx-shell .fx-modal {
  position: relative; width: 100%; border-radius: 18px;
  background: var(--fx-surface) !important; color: var(--fx-ink) !important;
  box-shadow: 0 24px 48px -12px rgba(16, 24, 40, .28);
}
.business-pixel-shell-v4r2.fx-shell .fx-modal-head {
  padding: 20px 24px; border-bottom: 1px solid var(--fx-border) !important;
  background: var(--fx-surface-2) !important; border-radius: 18px 18px 0 0;
}
.business-pixel-shell-v4r2.fx-shell .fx-modal-title { font-size: 17px; font-weight: 700; margin: 0; }
.business-pixel-shell-v4r2.fx-shell .fx-modal-sub { font-size: 13px; color: var(--fx-muted) !important; margin: 3px 0 0; }
.business-pixel-shell-v4r2.fx-shell .fx-modal-close {
  position: absolute; top: 14px; right: 14px; width: 32px; height: 32px; border-radius: 9px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  background: var(--fx-surface) !important; color: var(--fx-ink-2) !important;
  border: 1px solid var(--fx-border-strong) !important; z-index: 1;
}
.business-pixel-shell-v4r2.fx-shell .fx-signature-box {
  background: var(--fx-surface-2) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 12px; padding: 14px;
}

/* ---------- Messagerie ---------- */
.business-pixel-shell-v4r2.fx-shell .fx-chat-shell {
  background: var(--fx-surface) !important; border: 1px solid var(--fx-border) !important;
  border-radius: 0;
}
@media (min-width: 1024px) {
  .business-pixel-shell-v4r2.fx-shell .fx-chat-shell {
    border-radius: var(--fx-radius); padding: 16px; box-shadow: var(--fx-shadow);
  }
}

/* ---------- Toasts ---------- */
.fx-toast { border: 1px solid; box-shadow: 0 12px 32px -12px rgba(16,24,40,.28); }
.fx-toast-success { background: #ecfdf3; border-color: #abefc6; color: #05603a; }
.fx-toast-error   { background: #fef3f2; border-color: #fecdca; color: #912018; }
.fx-toast-warning { background: #fffaeb; border-color: #fedf89; color: #93370d; }
.fx-toast-info    { background: #eef3ff; border-color: #d5e2fd; color: #1f56d3; }
.fx-toast-title { font-size: 14px; font-weight: 700; margin: 0 0 2px; }
.fx-toast-text  { font-size: 13px; margin: 0; opacity: .92; }

/* =========================================================
   COMMERCE V2 — PREMIUM FUNCTIONAL PARITY
   UI-only shell inspired by the validated PRP dashboard.
   No financial/server authority is introduced here.
   ========================================================= */
.business-pixel-shell-v4r2.fx-shell.commerce-v2-shell {
  --commerce-v2-sidebar: 228px;
  min-height: 100dvh;
  background: #f4f7fb !important;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 45;
  width: var(--commerce-v2-sidebar);
  display: flex;
  flex-direction: column;
  padding: 18px 12px 14px;
  background: linear-gradient(180deg, #07172e 0%, #061328 100%);
  border-right: 1px solid rgba(148, 163, 184, .18);
  box-shadow: 18px 0 44px -34px rgba(15, 23, 42, .75);
  color: #e8eef9 !important;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand {
  min-height: 62px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(96, 165, 250, .26);
  border-radius: 15px;
  background: linear-gradient(135deg, rgba(30, 64, 175, .22), rgba(15, 23, 42, .12));
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand-mark {
  width: 38px; height: 38px; flex: 0 0 38px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 12px;
  background: #fff !important; color: #2563eb !important;
  box-shadow: 0 8px 20px -12px rgba(59,130,246,.8);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand-copy { min-width: 0; display: grid; gap: 2px; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand-copy strong {
  color: #fff !important; font-size: 12px; line-height: 1; letter-spacing: -.01em; white-space: nowrap;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand-copy em { color: #60a5fa !important; font-style: normal; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-brand-copy small {
  color: #93a4bf !important; font-size: 7px; font-weight: 800; letter-spacing: .19em;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-business-chip {
  display: flex; align-items: center; gap: 9px;
  margin: 12px 2px 8px; padding: 9px 8px;
  border-radius: 13px; background: rgba(255,255,255,.045);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-business-avatar {
  width: 34px; height: 34px; flex: 0 0 34px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center; overflow: hidden;
  background: rgba(59,130,246,.18) !important; color: #93c5fd !important;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-business-avatar img { width: 100%; height: 100%; object-fit: cover; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-business-chip strong {
  display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: #f8fbff !important; font-size: 11px; font-weight: 800;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-business-chip small {
  display: block; color: #7f93b2 !important; font-size: 8px; font-weight: 700; margin-top: 2px;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav {
  flex: 1 1 auto; min-height: 0; overflow-y: auto; scrollbar-width: none;
  display: flex; flex-direction: column; gap: 3px; padding: 5px 0 10px;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav::-webkit-scrollbar { display: none; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item {
  width: 100%; min-height: 39px; display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border: 0; border-radius: 11px; text-align: left; cursor: pointer;
  background: transparent !important; color: #c7d2e3 !important;
  font-size: 11px; font-weight: 750; line-height: 1.12;
  transition: background .16s ease, color .16s ease, transform .16s ease;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item:hover { background: rgba(255,255,255,.07) !important; color: #fff !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item.is-active {
  background: linear-gradient(135deg, #2563eb, #3b6df4) !important; color: #fff !important;
  box-shadow: 0 12px 24px -18px rgba(59,130,246,.95);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-icon {
  width: 17px; flex: 0 0 17px; display: inline-flex; align-items: center; justify-content: center;
  color: inherit !important;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer {
  flex: 0 0 auto; padding-top: 9px; border-top: 1px solid rgba(148,163,184,.16);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-footer-row { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer button {
  min-height: 37px; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: 1px solid rgba(148,163,184,.16); border-radius: 10px;
  background: rgba(255,255,255,.025) !important; color: #cbd5e1 !important;
  font-size: 10px; font-weight: 750; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer button:hover { background: rgba(255,255,255,.07) !important; color: #fff !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer .commerce-v2-logout {
  width: 100%; margin-top: 6px; justify-content: flex-start; padding-left: 12px;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-content {
  width: min(1420px, calc(100vw - var(--commerce-v2-sidebar))) !important;
  max-width: 1420px !important;
  margin-left: calc(var(--commerce-v2-sidebar) + max(0px, (100vw - var(--commerce-v2-sidebar) - 1420px) / 2)) !important;
  margin-right: 0 !important;
  padding-left: clamp(20px, 2.6vw, 38px) !important;
  padding-right: clamp(20px, 2.6vw, 38px) !important;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-forfy-launcher {
  position: fixed; right: 24px; bottom: 22px; z-index: 48;
  display: inline-flex; align-items: center; gap: 8px;
  min-height: 44px; padding: 0 17px; border: 1px solid rgba(147,197,253,.38); border-radius: 999px;
  background: linear-gradient(135deg, #0f3f91, #2563eb) !important; color: #fff !important;
  box-shadow: 0 16px 34px -18px rgba(30,64,175,.75);
  font-size: 12px; font-weight: 850; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-forfy-launcher:hover { transform: translateY(-1px); filter: brightness(1.05); }

.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub { max-width: 1180px; margin: 0 auto; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-hero {
  display: flex; align-items: flex-end; justify-content: space-between; gap: 20px;
  padding: 26px 28px; border-radius: 20px; border: 1px solid #dfe6f0;
  background: #fff !important; box-shadow: var(--fx-shadow);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-hero h2 {
  margin: 2px 0 5px; font-size: clamp(24px, 3vw, 34px); line-height: 1.08; font-weight: 850; letter-spacing: -.03em;
  color: #071b38 !important;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-hero p:last-child {
  max-width: 760px; margin: 0; color: #64748b !important; font-size: 14px; line-height: 1.55;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card {
  min-height: 92px; display: flex; align-items: center; gap: 13px; padding: 16px;
  border: 1px solid #e2e8f0; border-radius: 16px; background: #fff !important; color: #0f172a !important;
  text-align: left; cursor: pointer; box-shadow: 0 7px 22px -22px rgba(15,23,42,.55);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card:hover { border-color: #bfd2fd; transform: translateY(-1px); }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card-copy { flex: 1; min-width: 0; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card-copy strong { display: block; font-size: 14px; color: #0f172a !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card-copy small { display: block; margin-top: 3px; font-size: 11px; color: #94a3b8 !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-card > svg { color: #94a3b8 !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-boundary-note {
  display: flex; align-items: flex-start; gap: 12px; padding: 16px 18px; border: 1px solid #dbe7fd; border-radius: 15px;
  background: #f5f8ff !important; color: #1e3a5f !important;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-boundary-note > svg { flex: 0 0 auto; margin-top: 2px; color: #2563eb !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-boundary-note strong { font-size: 12px; color: #16345c !important; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-boundary-note p { margin: 3px 0 0; font-size: 12px; line-height: 1.5; color: #5c708e !important; }


/* COMMERCE_UX1_MERCHANT_NAV_LOCK — categorized merchant workspace */
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-label { flex: 1 1 auto; min-width: 0; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-badge {
  min-width: 21px; height: 21px; padding: 0 6px; border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #ef4444 !important; color: #fff !important; font-size: 10px; font-weight: 900;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item--settings {
  margin-top: 8px; border-top: 1px solid rgba(148,163,184,.12);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub {
  max-width: 1180px; margin: 0 auto; padding: 22px; border: 1px solid #e2e8f0; border-radius: 24px;
  background: #fff !important; box-shadow: 0 18px 42px -34px rgba(15,23,42,.5);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today__head,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub__head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; margin-bottom: 18px;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today__head h2,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub__head h2 {
  margin: 2px 0 4px; color: #071b38 !important; font-size: clamp(22px, 2.6vw, 32px); line-height: 1.08; font-weight: 900; letter-spacing: -.035em;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today__head span,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub__head small {
  color: #64748b !important; font-size: 13px; font-weight: 760;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-chip {
  min-height: 42px; padding: 0 15px; border: 1px solid #bfd2fd; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 8px; background: #eff6ff !important; color: #1d4ed8 !important;
  font-size: 12px; font-weight: 900; cursor: pointer;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stats {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 14px;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stat {
  padding: 16px; border: 1px solid #e2e8f0; border-radius: 18px; background: #f8fafc !important;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stat strong { display:block; color:#0f172a !important; font-size: clamp(22px, 3vw, 34px); line-height:1; font-weight:950; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stat span { display:block; margin-top:7px; color:#334155 !important; font-size:12px; font-weight:900; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stat small { display:block; margin-top:3px; color:#64748b !important; font-size:11px; font-weight:760; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card {
  display:flex; justify-content:space-between; align-items:center; gap:18px; padding:18px; margin: 14px 0;
  border-radius: 20px; border: 1px solid #bfdbfe; background: linear-gradient(135deg, #eff6ff, #ffffff) !important;
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card h3 { margin:4px 0 5px; color:#0f172a !important; font-size:18px; font-weight:950; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card span { color:#475569 !important; font-size:13px; font-weight:780; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card__actions { display:flex; flex-wrap:wrap; gap:8px; justify-content:flex-end; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-todo { display:grid; gap:8px; margin-top: 14px; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-todo h3 { margin:0 0 4px; color:#0f172a !important; font-size:14px; font-weight:950; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-todo button,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row {
  width:100%; min-height:58px; display:flex; align-items:center; gap:12px; padding:13px 15px;
  border:1px solid #e2e8f0; border-radius:16px; background:#fff !important; color:#0f172a !important;
  text-align:left; cursor:pointer; box-shadow:0 8px 22px -24px rgba(15,23,42,.55);
}
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-todo button:hover,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row:hover,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row:hover { border-color:#bfd2fd; transform:translateY(-1px); }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-todo button span,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row__main,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row-copy { flex:1 1 auto; min-width:0; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row__main strong,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row-copy strong { display:block; color:#0f172a !important; font-size:14px; font-weight:950; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row__main small,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row-copy small { display:block; margin-top:3px; color:#64748b !important; font-size:12px; font-weight:760; line-height:1.35; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-list,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-list { display:grid; gap:10px; }
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row__status,
.business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row-status {
  flex:0 0 auto; max-width:190px; padding:7px 10px; border-radius:999px; background:#f1f5f9 !important; color:#334155 !important;
  font-size:11px; font-weight:900; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}

@media (max-width: 860px) {
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub { padding: 16px; border-radius: 20px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-today__head,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-hub__head,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card { display: grid; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-stats { grid-template-columns: 1fr; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card__actions,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-forfy-card__actions button { width: 100%; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row { align-items: flex-start; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1-category-row__status,
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-row-status { display: none; }
}


/* COMMERCE_UX1C_CONTEXTUAL_FORFY_BUTTONS_LOCK — contextual Forfy IA CTA by merchant category */
.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta {
  max-width: 1180px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid #bfdbfe;
  background:
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.14), transparent 34%),
    linear-gradient(135deg, #eff6ff 0%, #ffffff 100%) !important;
  box-shadow: 0 18px 42px -34px rgba(15,23,42,.5);
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta h3 {
  margin: 4px 0 5px;
  color: #071b38 !important;
  font-size: clamp(18px, 2vw, 25px);
  line-height: 1.12;
  font-weight: 950;
  letter-spacing: -.03em;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta span {
  display: block;
  max-width: 680px;
  color: #475569 !important;
  font-size: 13px;
  line-height: 1.42;
  font-weight: 780;
}

.business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta .fx-btn {
  flex: 0 0 auto;
  min-height: 46px;
  white-space: nowrap;
}

@media (max-width: 860px) {
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta {
    display: grid;
    padding: 16px;
    border-radius: 20px;
  }

  .business-pixel-shell-v4r2.fx-shell .commerce-v2-ux1c-forfy-category-cta .fx-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-height: 760px) and (min-width: 1024px) {
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar { padding-top: 10px; padding-bottom: 9px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-brand { min-height: 52px; padding: 7px 9px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-business-chip { margin-top: 7px; margin-bottom: 4px; padding-top: 6px; padding-bottom: 6px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item { min-height: 32px; padding-top: 5px; padding-bottom: 5px; font-size: 10px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer button { min-height: 31px; }
}

@media (max-width: 1023px) {
  .business-pixel-shell-v4r2.fx-shell.commerce-v2-shell { --commerce-v2-sidebar: 0px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar {
    position: sticky; top: 0; width: 100%; height: auto; max-height: none; padding: 9px 10px; z-index: 45;
  }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-brand { min-height: 48px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-business-chip { display: none; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-nav {
    flex: none; flex-direction: row; overflow-x: auto; overflow-y: hidden; padding: 7px 0 5px; gap: 6px;
  }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-nav-item {
    flex: 0 0 auto; width: auto; min-height: 34px; padding: 7px 10px; white-space: nowrap;
  }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer { display: flex; gap: 6px; padding-top: 6px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-footer-row { display: flex; gap: 6px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-sidebar-footer .commerce-v2-logout { width: auto; margin: 0; padding: 0 10px; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-content {
    width: 100% !important; max-width: 100% !important; margin: 0 !important; padding-left: 14px !important; padding-right: 14px !important;
  }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-grid { grid-template-columns: 1fr; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-hub-hero { align-items: flex-start; flex-direction: column; }
  .business-pixel-shell-v4r2.fx-shell .commerce-v2-forfy-launcher { right: 12px; bottom: 12px; }
}

@media (prefers-reduced-motion: reduce) {
  .business-pixel-shell-v4r2.fx-shell * { transition: none !important; animation: none !important; }
}
      `}</style>
    </div>
  );
}
